// Auto-generated 600 Q&A for LGL Professional Training: Project Scheduling & Controls
import { Calendar, Layers, HardHat, TrendingUp, ShieldCheck, BarChart3, type LucideIcon } from 'lucide-react';

export interface QuizQuestion {
    id: string;
    qNum: number;
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
    rawAnswer: string;
}

export interface QuizCategory {
    id: string;
    aliases?: string[];
    title: string;
    description: string;
    project: string;
    icon: LucideIcon;
    iconName: string;
    color: string;
    questions: QuizQuestion[];
}

const ICON_MAP: Record<string, LucideIcon> = {
    Calendar,
    Layers,
    HardHat,
    TrendingUp,
    ShieldCheck,
    BarChart3
};

const RAW_CATEGORIES = [
    {
        "id": "module-1",
        "aliases": [
            "cpm",
            "msp",
            "fundamentals"
        ],
        "title": "Module 1: Scheduling Fundamentals & MS Project",
        "description": "Foundations of scheduling, WBS hierarchy, activities, calendars, contracts, CPM logic, float calculations, and MS Project baselining.",
        "project": "LGL Regional Hospital Capital Improvement Project",
        "iconName": "Calendar",
        "color": "#00BFB2",
        "questions": [
            {
                "id": "module-1-q1",
                "qNum": 1,
                "question": "What is a project?",
                "options": [
                    "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took).",
                    "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF.",
                    "A temporary effort with a defined start, a defined end, and a specific goal or deliverable.",
                    "How long we expect an activity to take."
                ],
                "correctAnswer": 2,
                "explanation": "A temporary effort with a defined start, a defined end, and a specific goal or deliverable.",
                "rawAnswer": "A temporary effort with a defined start, a defined end, and a specific goal or deliverable."
            },
            {
                "id": "module-1-q2",
                "qNum": 2,
                "question": "What are the three defining characteristics of a project?",
                "options": [
                    "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example).",
                    "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize).",
                    "What the project is actually building or delivering, including what is in and out of the work.",
                    "Temporary, unique, and progressive (it moves through stages such as planning, building and finishing)."
                ],
                "correctAnswer": 3,
                "explanation": "Temporary, unique, and progressive (it moves through stages such as planning, building and finishing).",
                "rawAnswer": "Temporary, unique, and progressive (it moves through stages such as planning, building and finishing)."
            },
            {
                "id": "module-1-q3",
                "qNum": 3,
                "question": "Give an everyday and a professional example of a project.",
                "options": [
                    "B cannot finish until A starts. It's rare and confusing; understand it exists but use cautiously. MSP: 10SF.",
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems.",
                    "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy).",
                    "You cannot plan work you don't understand. Study the scope first."
                ],
                "correctAnswer": 2,
                "explanation": "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy).",
                "rawAnswer": "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy)."
            },
            {
                "id": "module-1-q4",
                "qNum": 4,
                "question": "How does a project differ from operations?",
                "options": [
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems.",
                    "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists.",
                    "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS."
                ],
                "correctAnswer": 2,
                "explanation": "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists.",
                "rawAnswer": "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists."
            },
            {
                "id": "module-1-q5",
                "qNum": 5,
                "question": "Which of these is a project: building a hospital wing, or scheduling nurses daily?",
                "options": [
                    "A calendar that tells scheduling software when work is allowed to happen.",
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations.",
                    "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize)."
                ],
                "correctAnswer": 2,
                "explanation": "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations.",
                "rawAnswer": "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations."
            },
            {
                "id": "module-1-q6",
                "qNum": 6,
                "question": "What is project management?",
                "options": [
                    "The organized approach to planning, organizing and controlling the work of a project so it achieves its goals within the agreed time, cost and quality.",
                    "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot).",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates."
                ],
                "correctAnswer": 0,
                "explanation": "The organized approach to planning, organizing and controlling the work of a project so it achieves its goals within the agreed time, cost and quality.",
                "rawAnswer": "The organized approach to planning, organizing and controlling the work of a project so it achieves its goals within the agreed time, cost and quality."
            },
            {
                "id": "module-1-q7",
                "qNum": 7,
                "question": "What are the three sides of the project management triangle?",
                "options": [
                    "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved.",
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                    "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team.",
                    "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take)."
                ],
                "correctAnswer": 3,
                "explanation": "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take).",
                "rawAnswer": "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take)."
            },
            {
                "id": "module-1-q8",
                "qNum": 8,
                "question": "Which dimension of the triangle does the project scheduler own?",
                "options": [
                    "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default.",
                    "The Schedule (time) dimension.",
                    "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved.",
                    "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover."
                ],
                "correctAnswer": 1,
                "explanation": "The Schedule (time) dimension.",
                "rawAnswer": "The Schedule (time) dimension."
            },
            {
                "id": "module-1-q9",
                "qNum": 9,
                "question": "Why are the three sides of the triangle said to be connected?",
                "options": [
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems.",
                    "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)",
                    "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS.",
                    "Changing one often affects the others."
                ],
                "correctAnswer": 3,
                "explanation": "Changing one often affects the others.",
                "rawAnswer": "Changing one often affects the others."
            },
            {
                "id": "module-1-q10",
                "qNum": 10,
                "question": "What is the difference between planning and scheduling?",
                "options": [
                    "The completion date the schedule currently calculates based on the latest progress and information.",
                    "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER.",
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\".",
                    "They give unrealistic dates, an incorrect critical path and are a quality failure. Add Predecessors/Successors columns and look for blanks (except the first task and final milestone); filter for extreme Total Slack."
                ],
                "correctAnswer": 1,
                "explanation": "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER.",
                "rawAnswer": "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER."
            },
            {
                "id": "module-1-q11",
                "qNum": 11,
                "question": "Give a construction example of a scheduling question.",
                "options": [
                    "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists.",
                    "When does excavation start? How long does concrete curing take? What must finish before framing begins?",
                    "The following Friday, exactly two calendar weeks later.",
                    "5-Day×8-Hour (standard construction), 6-Day×10-Hour (accelerated), 7-Day/Continuous (24/7 operations, commissioning), Weather calendar (outdoor work), and Project-Specific (shutdowns/permit windows)."
                ],
                "correctAnswer": 1,
                "explanation": "When does excavation start? How long does concrete curing take? What must finish before framing begins?",
                "rawAnswer": "When does excavation start? How long does concrete curing take? What must finish before framing begins?"
            },
            {
                "id": "module-1-q12",
                "qNum": 12,
                "question": "What are the five stages of the project life cycle?",
                "options": [
                    "Initiation, Planning, Execution, Monitor & Control, and Closeout.",
                    "(1) Understand the work, (2) break it into activities, (3) estimate/validate durations, (4) connect activities, (5) calculate, baseline and update, (6) analyze, forecast and report.",
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                    "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others."
                ],
                "correctAnswer": 0,
                "explanation": "Initiation, Planning, Execution, Monitor & Control, and Closeout.",
                "rawAnswer": "Initiation, Planning, Execution, Monitor & Control, and Closeout."
            },
            {
                "id": "module-1-q13",
                "qNum": 13,
                "question": "During which life-cycle stages is the scheduler active?",
                "options": [
                    "From Planning through Closeout, i.e., the entire life cycle after initiation.",
                    "The Schedule (time) dimension.",
                    "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                    "Early Start: soonest it can begin. Early Finish: earliest it can be complete (ES + duration). Late Start / Late Finish: latest it can start/finish without affecting project completion."
                ],
                "correctAnswer": 0,
                "explanation": "From Planning through Closeout, i.e., the entire life cycle after initiation.",
                "rawAnswer": "From Planning through Closeout, i.e., the entire life cycle after initiation."
            },
            {
                "id": "module-1-q14",
                "qNum": 14,
                "question": "What does a scheduler do during Planning, Execution, Monitor & Control and Closeout?",
                "options": [
                    "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot).",
                    "Changing one often affects the others.",
                    "No. It is a WBS category. \"Install Electrical Conduit – Level 1\" is an activity.",
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance."
                ],
                "correctAnswer": 3,
                "explanation": "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance.",
                "rawAnswer": "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance."
            },
            {
                "id": "module-1-q15",
                "qNum": 15,
                "question": "What is the common beginner mistake about when a scheduler works?",
                "options": [
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle.",
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance.",
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list."
                ],
                "correctAnswer": 1,
                "explanation": "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle.",
                "rawAnswer": "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle."
            },
            {
                "id": "module-1-q16",
                "qNum": 16,
                "question": "Is a scheduler a data entry clerk?",
                "options": [
                    "Which days are working days, hours per day, project holidays/non-work periods, and shift patterns.",
                    "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers.",
                    "Identify the variance, find the driving activities, communicate and document.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects."
                ],
                "correctAnswer": 1,
                "explanation": "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers.",
                "rawAnswer": "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers."
            },
            {
                "id": "module-1-q17",
                "qNum": 17,
                "question": "List the six things a scheduler actually does.",
                "options": [
                    "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method.",
                    "The required completion date as defined by the applicable contract.",
                    "(1) Understand the work, (2) break it into activities, (3) estimate/validate durations, (4) connect activities, (5) calculate, baseline and update, (6) analyze, forecast and report.",
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\"."
                ],
                "correctAnswer": 2,
                "explanation": "(1) Understand the work, (2) break it into activities, (3) estimate/validate durations, (4) connect activities, (5) calculate, baseline and update, (6) analyze, forecast and report.",
                "rawAnswer": "(1) Understand the work, (2) break it into activities, (3) estimate/validate durations, (4) connect activities, (5) calculate, baseline and update, (6) analyze, forecast and report."
            },
            {
                "id": "module-1-q18",
                "qNum": 18,
                "question": "Why is knowing how to click buttons in MSP not enough?",
                "options": [
                    "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                    "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float.",
                    "Software operation does not make you a scheduler. You must understand the project, build logical plans and interpret results.",
                    "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid."
                ],
                "correctAnswer": 2,
                "explanation": "Software operation does not make you a scheduler. You must understand the project, build logical plans and interpret results.",
                "rawAnswer": "Software operation does not make you a scheduler. You must understand the project, build logical plans and interpret results."
            },
            {
                "id": "module-1-q19",
                "qNum": 19,
                "question": "What is project scope?",
                "options": [
                    "Initiation, Planning, Execution, Monitor & Control, and Closeout.",
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d.",
                    "What the project is actually building or delivering, including what is in and out of the work.",
                    "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee."
                ],
                "correctAnswer": 2,
                "explanation": "What the project is actually building or delivering, including what is in and out of the work.",
                "rawAnswer": "What the project is actually building or delivering, including what is in and out of the work."
            },
            {
                "id": "module-1-q20",
                "qNum": 20,
                "question": "Where does scope come from?",
                "options": [
                    "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                    "5-Day×8-Hour (standard construction), 6-Day×10-Hour (accelerated), 7-Day/Continuous (24/7 operations, commissioning), Weather calendar (outdoor work), and Project-Specific (shutdowns/permit windows).",
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                    "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined."
                ],
                "correctAnswer": 2,
                "explanation": "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                "rawAnswer": "The scope statement, drawings and specifications, contract documents, and the deliverables list."
            },
            {
                "id": "module-1-q21",
                "qNum": 21,
                "question": "Why must a scheduler understand scope before building the schedule?",
                "options": [
                    "The completion date the schedule currently calculates based on the latest progress and information.",
                    "The organized approach to planning, organizing and controlling the work of a project so it achieves its goals within the agreed time, cost and quality.",
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\".",
                    "You cannot plan work you don't understand. Study the scope first."
                ],
                "correctAnswer": 3,
                "explanation": "You cannot plan work you don't understand. Study the scope first.",
                "rawAnswer": "You cannot plan work you don't understand. Study the scope first."
            },
            {
                "id": "module-1-q22",
                "qNum": 22,
                "question": "What was in scope for the LGL Hospital example?",
                "options": [
                    "A new 4-floor patient care wing, emergency department expansion, site civil and utilities, MEP systems, and interior finishes and medical equipment rough-in.",
                    "You cannot plan work you don't understand. Study the scope first.",
                    "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy).",
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things."
                ],
                "correctAnswer": 0,
                "explanation": "A new 4-floor patient care wing, emergency department expansion, site civil and utilities, MEP systems, and interior finishes and medical equipment rough-in.",
                "rawAnswer": "A new 4-floor patient care wing, emergency department expansion, site civil and utilities, MEP systems, and interior finishes and medical equipment rough-in."
            },
            {
                "id": "module-1-q23",
                "qNum": 23,
                "question": "What is a Work Breakdown Structure (WBS)?",
                "options": [
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance.",
                    "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                    "Schedule breathing room before the completion date is affected (MSP calls it Total Slack). 0 = critical; >0 = flexibility; <0 = behind and must improve to meet the constraint/finish date."
                ],
                "correctAnswer": 1,
                "explanation": "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders.",
                "rawAnswer": "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders."
            },
            {
                "id": "module-1-q24",
                "qNum": 24,
                "question": "What are the WBS levels in the hospital example?",
                "options": [
                    "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders.",
                    "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities).",
                    "Early Start: soonest it can begin. Early Finish: earliest it can be complete (ES + duration). Late Start / Late Finish: latest it can start/finish without affecting project completion.",
                    "FS very common, SS common, FF moderate, SF rare."
                ],
                "correctAnswer": 1,
                "explanation": "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities).",
                "rawAnswer": "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities)."
            },
            {
                "id": "module-1-q25",
                "qNum": 25,
                "question": "Is \"Electrical Work\" an activity?",
                "options": [
                    "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations.",
                    "No. It is a WBS category. \"Install Electrical Conduit – Level 1\" is an activity.",
                    "An agreed price for a defined scope, subject to contract terms and approved changes (e.g., kitchen renovation for an agreed $25,000).",
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things."
                ],
                "correctAnswer": 1,
                "explanation": "No. It is a WBS category. \"Install Electrical Conduit – Level 1\" is an activity.",
                "rawAnswer": "No. It is a WBS category. \"Install Electrical Conduit – Level 1\" is an activity."
            },
            {
                "id": "module-1-q26",
                "qNum": 26,
                "question": "What is the critical distinction between a WBS element and an activity?",
                "options": [
                    "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers.",
                    "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates).",
                    "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee.",
                    "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates."
                ],
                "correctAnswer": 3,
                "explanation": "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates.",
                "rawAnswer": "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates."
            },
            {
                "id": "module-1-q27",
                "qNum": 27,
                "question": "What is an activity?",
                "options": [
                    "A measurable piece of work with a start, a finish and a duration; the basic building block of every schedule.",
                    "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                    "Working days (or hours) based on the project calendar, not calendar days.",
                    "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities."
                ],
                "correctAnswer": 0,
                "explanation": "A measurable piece of work with a start, a finish and a duration; the basic building block of every schedule.",
                "rawAnswer": "A measurable piece of work with a start, a finish and a duration; the basic building block of every schedule."
            },
            {
                "id": "module-1-q28",
                "qNum": 28,
                "question": "What formula is recommended for naming activities?",
                "options": [
                    "The Schedule (time) dimension.",
                    "Identify the variance, find the driving activities, communicate and document.",
                    "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle.",
                    "ACTION VERB + OBJECT + LOCATION."
                ],
                "correctAnswer": 3,
                "explanation": "ACTION VERB + OBJECT + LOCATION.",
                "rawAnswer": "ACTION VERB + OBJECT + LOCATION."
            },
            {
                "id": "module-1-q29",
                "qNum": 29,
                "question": "Give a bad vs. better activity name.",
                "options": [
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\".",
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d.",
                    "Don't use large lags to hide actual work (make it a separate activity). Excessive lead creates unrealistic assumptions, so ensure the overlap is achievable in the field.",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan."
                ],
                "correctAnswer": 0,
                "explanation": "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\".",
                "rawAnswer": "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\"."
            },
            {
                "id": "module-1-q30",
                "qNum": 30,
                "question": "What is an Activity ID?",
                "options": [
                    "The following Friday, exactly two calendar weeks later.",
                    "A unique identifier for each activity, like a filing-system number or phone number.",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "B cannot finish until A starts. It's rare and confusing; understand it exists but use cautiously. MSP: 10SF."
                ],
                "correctAnswer": 1,
                "explanation": "A unique identifier for each activity, like a filing-system number or phone number.",
                "rawAnswer": "A unique identifier for each activity, like a filing-system number or phone number."
            },
            {
                "id": "module-1-q31",
                "qNum": 31,
                "question": "Why do Activity IDs exist?",
                "options": [
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price.",
                    "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them.",
                    "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities).",
                    "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days."
                ],
                "correctAnswer": 1,
                "explanation": "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them.",
                "rawAnswer": "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them."
            },
            {
                "id": "module-1-q32",
                "qNum": 32,
                "question": "What discipline does the ID prefix \"STR-2010\" represent?",
                "options": [
                    "A wedding day: an important event you work toward, not something scheduled as 10 working days.",
                    "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)",
                    "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team.",
                    "An important event with zero duration that marks a point in time, not a period of work."
                ],
                "correctAnswer": 1,
                "explanation": "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)",
                "rawAnswer": "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)"
            },
            {
                "id": "module-1-q33",
                "qNum": 33,
                "question": "What is duration?",
                "options": [
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price.",
                    "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER.",
                    "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities.",
                    "How long we expect an activity to take."
                ],
                "correctAnswer": 3,
                "explanation": "How long we expect an activity to take.",
                "rawAnswer": "How long we expect an activity to take."
            },
            {
                "id": "module-1-q34",
                "qNum": 34,
                "question": "Is duration measured in working days or calendar days?",
                "options": [
                    "It finishes the following Wednesday; 5 working days = 7 calendar days.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                    "Working days (or hours) based on the project calendar, not calendar days.",
                    "(1) Understand the work, (2) break it into activities, (3) estimate/validate durations, (4) connect activities, (5) calculate, baseline and update, (6) analyze, forecast and report."
                ],
                "correctAnswer": 2,
                "explanation": "Working days (or hours) based on the project calendar, not calendar days.",
                "rawAnswer": "Working days (or hours) based on the project calendar, not calendar days."
            },
            {
                "id": "module-1-q35",
                "qNum": 35,
                "question": "If a 5-working-day activity starts on Thursday on a Mon–Fri calendar, when does it finish and how many calendar days is that?",
                "options": [
                    "No. It is a WBS category. \"Install Electrical Conduit – Level 1\" is an activity.",
                    "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example).",
                    "An activity missing a predecessor or successor (or both).",
                    "It finishes the following Wednesday; 5 working days = 7 calendar days."
                ],
                "correctAnswer": 3,
                "explanation": "It finishes the following Wednesday; 5 working days = 7 calendar days.",
                "rawAnswer": "It finishes the following Wednesday; 5 working days = 7 calendar days."
            },
            {
                "id": "module-1-q36",
                "qNum": 36,
                "question": "Name the three types of duration.",
                "options": [
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Temporary, unique, and progressive (it moves through stages such as planning, building and finishing).",
                    "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took).",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan."
                ],
                "correctAnswer": 2,
                "explanation": "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took).",
                "rawAnswer": "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took)."
            },
            {
                "id": "module-1-q37",
                "qNum": 37,
                "question": "What is a project calendar?",
                "options": [
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "A new 4-floor patient care wing, emergency department expansion, site civil and utilities, MEP systems, and interior finishes and medical equipment rough-in.",
                    "A calendar that tells scheduling software when work is allowed to happen.",
                    "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float."
                ],
                "correctAnswer": 2,
                "explanation": "A calendar that tells scheduling software when work is allowed to happen.",
                "rawAnswer": "A calendar that tells scheduling software when work is allowed to happen."
            },
            {
                "id": "module-1-q38",
                "qNum": 38,
                "question": "What does a calendar control?",
                "options": [
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Which days are working days, hours per day, project holidays/non-work periods, and shift patterns.",
                    "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take).",
                    "What the project is actually building or delivering, including what is in and out of the work."
                ],
                "correctAnswer": 1,
                "explanation": "Which days are working days, hours per day, project holidays/non-work periods, and shift patterns.",
                "rawAnswer": "Which days are working days, hours per day, project holidays/non-work periods, and shift patterns."
            },
            {
                "id": "module-1-q39",
                "qNum": 39,
                "question": "Name common calendar types and their typical use.",
                "options": [
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things.",
                    "5-Day×8-Hour (standard construction), 6-Day×10-Hour (accelerated), 7-Day/Continuous (24/7 operations, commissioning), Weather calendar (outdoor work), and Project-Specific (shutdowns/permit windows).",
                    "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor.",
                    "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float."
                ],
                "correctAnswer": 1,
                "explanation": "5-Day×8-Hour (standard construction), 6-Day×10-Hour (accelerated), 7-Day/Continuous (24/7 operations, commissioning), Weather calendar (outdoor work), and Project-Specific (shutdowns/permit windows).",
                "rawAnswer": "5-Day×8-Hour (standard construction), 6-Day×10-Hour (accelerated), 7-Day/Continuous (24/7 operations, commissioning), Weather calendar (outdoor work), and Project-Specific (shutdowns/permit windows)."
            },
            {
                "id": "module-1-q40",
                "qNum": 40,
                "question": "If a 10-working-day activity starts Monday on a 5-day calendar, what day does it finish?",
                "options": [
                    "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default.",
                    "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete.",
                    "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                    "The following Friday, exactly two calendar weeks later."
                ],
                "correctAnswer": 3,
                "explanation": "The following Friday, exactly two calendar weeks later.",
                "rawAnswer": "The following Friday, exactly two calendar weeks later."
            },
            {
                "id": "module-1-q41",
                "qNum": 41,
                "question": "What is a contract?",
                "options": [
                    "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved.",
                    "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates.",
                    "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER.",
                    "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor."
                ],
                "correctAnswer": 0,
                "explanation": "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved.",
                "rawAnswer": "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved."
            },
            {
                "id": "module-1-q42",
                "qNum": 42,
                "question": "Why does the scheduler care about the contract?",
                "options": [
                    "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor.",
                    "Giving a milestone a duration; it then becomes an activity and the logic will be wrong.",
                    "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists.",
                    "FS very common, SS common, FF moderate, SF rare."
                ],
                "correctAnswer": 0,
                "explanation": "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor.",
                "rawAnswer": "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor."
            },
            {
                "id": "module-1-q43",
                "qNum": 43,
                "question": "What are the four contract pricing types?",
                "options": [
                    "Lump Sum/Fixed Price, Time & Materials (T&M), Unit Price, and Cost-Reimbursable/Cost-Plus.",
                    "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path.",
                    "They give unrealistic dates, an incorrect critical path and are a quality failure. Add Predecessors/Successors columns and look for blanks (except the first task and final milestone); filter for extreme Total Slack.",
                    "You cannot plan work you don't understand. Study the scope first."
                ],
                "correctAnswer": 0,
                "explanation": "Lump Sum/Fixed Price, Time & Materials (T&M), Unit Price, and Cost-Reimbursable/Cost-Plus.",
                "rawAnswer": "Lump Sum/Fixed Price, Time & Materials (T&M), Unit Price, and Cost-Reimbursable/Cost-Plus."
            },
            {
                "id": "module-1-q44",
                "qNum": 44,
                "question": "Explain Lump Sum / Fixed Price.",
                "options": [
                    "An agreed price for a defined scope, subject to contract terms and approved changes (e.g., kitchen renovation for an agreed $25,000).",
                    "A unique identifier for each activity, like a filing-system number or phone number.",
                    "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took).",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it."
                ],
                "correctAnswer": 0,
                "explanation": "An agreed price for a defined scope, subject to contract terms and approved changes (e.g., kitchen renovation for an agreed $25,000).",
                "rawAnswer": "An agreed price for a defined scope, subject to contract terms and approved changes (e.g., kitchen renovation for an agreed $25,000)."
            },
            {
                "id": "module-1-q45",
                "qNum": 45,
                "question": "Explain Time & Materials.",
                "options": [
                    "Planning answers WHAT and WHO (what will be built, by whom, with what materials). Scheduling answers WHEN and IN WHAT ORDER.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                    "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team.",
                    "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined."
                ],
                "correctAnswer": 3,
                "explanation": "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined.",
                "rawAnswer": "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined."
            },
            {
                "id": "module-1-q46",
                "qNum": 46,
                "question": "Explain Unit Price contracts.",
                "options": [
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot).",
                    "Original Duration (planned at baseline), Remaining Duration (time still needed), and Actual Duration (how long it actually took).",
                    "A unique identifier for each activity, like a filing-system number or phone number."
                ],
                "correctAnswer": 1,
                "explanation": "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot).",
                "rawAnswer": "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot)."
            },
            {
                "id": "module-1-q47",
                "qNum": 47,
                "question": "Explain Cost-Plus contracts.",
                "options": [
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                    "The organized approach to planning, organizing and controlling the work of a project so it achieves its goals within the agreed time, cost and quality.",
                    "An agreement defining what work is included, who is responsible, how the work is commercially arranged and when important requirements must be achieved.",
                    "Allowable project costs are reimbursed per the contract, plus an agreed fee."
                ],
                "correctAnswer": 3,
                "explanation": "Allowable project costs are reimbursed per the contract, plus an agreed fee.",
                "rawAnswer": "Allowable project costs are reimbursed per the contract, plus an agreed fee."
            },
            {
                "id": "module-1-q48",
                "qNum": 48,
                "question": "How can quantity and productivity estimate duration?",
                "options": [
                    "Format tab → Critical Tasks (bars turn red), and add the Total Slack column (0 = critical).",
                    "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                    "The required completion date as defined by the applicable contract.",
                    "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers."
                ],
                "correctAnswer": 1,
                "explanation": "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                "rawAnswer": "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days."
            },
            {
                "id": "module-1-q49",
                "qNum": 49,
                "question": "What are the delivery methods discussed?",
                "options": [
                    "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                    "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others.",
                    "Design-Bid-Build, Design-Build, and EPC.",
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance."
                ],
                "correctAnswer": 2,
                "explanation": "Design-Bid-Build, Design-Build, and EPC.",
                "rawAnswer": "Design-Bid-Build, Design-Build, and EPC."
            },
            {
                "id": "module-1-q50",
                "qNum": 50,
                "question": "Explain Design-Bid-Build.",
                "options": [
                    "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations.",
                    "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems."
                ],
                "correctAnswer": 2,
                "explanation": "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                "rawAnswer": "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects."
            },
            {
                "id": "module-1-q51",
                "qNum": 51,
                "question": "Explain Design-Build.",
                "options": [
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                    "The required completion date as defined by the applicable contract.",
                    "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method.",
                    "How long we expect an activity to take."
                ],
                "correctAnswer": 2,
                "explanation": "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method.",
                "rawAnswer": "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method."
            },
            {
                "id": "module-1-q52",
                "qNum": 52,
                "question": "What does EPC stand for?",
                "options": [
                    "How long we expect an activity to take.",
                    "From Planning through Closeout, i.e., the entire life cycle after initiation.",
                    "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF.",
                    "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example)."
                ],
                "correctAnswer": 3,
                "explanation": "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example).",
                "rawAnswer": "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example)."
            },
            {
                "id": "module-1-q53",
                "qNum": 53,
                "question": "What does each EPC phase mean?",
                "options": [
                    "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates.",
                    "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them.",
                    "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover.",
                    "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d."
                ],
                "correctAnswer": 2,
                "explanation": "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover.",
                "rawAnswer": "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover."
            },
            {
                "id": "module-1-q54",
                "qNum": 54,
                "question": "Give the EPC schedule dependency chain for switchgear.",
                "options": [
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                    "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid.",
                    "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days.",
                    "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize)."
                ],
                "correctAnswer": 3,
                "explanation": "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize).",
                "rawAnswer": "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize)."
            },
            {
                "id": "module-1-q55",
                "qNum": 55,
                "question": "What is the difference between delivery method and pricing type?",
                "options": [
                    "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities).",
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things.",
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price."
                ],
                "correctAnswer": 1,
                "explanation": "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things.",
                "rawAnswer": "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things."
            },
            {
                "id": "module-1-q56",
                "qNum": 56,
                "question": "Does EPC mean the project is fixed price?",
                "options": [
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price.",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "The following Friday, exactly two calendar weeks later.",
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d."
                ],
                "correctAnswer": 0,
                "explanation": "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price.",
                "rawAnswer": "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price."
            },
            {
                "id": "module-1-q57",
                "qNum": 57,
                "question": "What is the difference between project type and contract pricing type?",
                "options": [
                    "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid.",
                    "A calendar that tells scheduling software when work is allowed to happen.",
                    "Type the Task ID (e.g., \"10\") or explicitly \"10FS\".",
                    "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS."
                ],
                "correctAnswer": 0,
                "explanation": "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid.",
                "rawAnswer": "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid."
            },
            {
                "id": "module-1-q58",
                "qNum": 58,
                "question": "What are the LGL Hospital training example's key facts?",
                "options": [
                    "Hospital/healthcare construction, Design-Build delivery, Lump Sum/Fixed Price, 18-month duration (all training values, not a real contract).",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Project tab → Project Information → Start Date (enter NTP date, not today's default) and Calendar (Standard Mon–Fri 5-Day×8-Hour).",
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars."
                ],
                "correctAnswer": 0,
                "explanation": "Hospital/healthcare construction, Design-Build delivery, Lump Sum/Fixed Price, 18-month duration (all training values, not a real contract).",
                "rawAnswer": "Hospital/healthcare construction, Design-Build delivery, Lump Sum/Fixed Price, 18-month duration (all training values, not a real contract)."
            },
            {
                "id": "module-1-q59",
                "qNum": 59,
                "question": "What is NTP?",
                "options": [
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Giving a milestone a duration; it then becomes an activity and the logic will be wrong.",
                    "Initiation, Planning, Execution, Monitor & Control, and Closeout.",
                    "Notice to Proceed: authorization to begin work in accordance with the applicable contract."
                ],
                "correctAnswer": 3,
                "explanation": "Notice to Proceed: authorization to begin work in accordance with the applicable contract.",
                "rawAnswer": "Notice to Proceed: authorization to begin work in accordance with the applicable contract."
            },
            {
                "id": "module-1-q60",
                "qNum": 60,
                "question": "What is the Contract Completion date?",
                "options": [
                    "Type the Task ID (e.g., \"10\") or explicitly \"10FS\".",
                    "The required completion date as defined by the applicable contract.",
                    "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates.",
                    "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor."
                ],
                "correctAnswer": 1,
                "explanation": "The required completion date as defined by the applicable contract.",
                "rawAnswer": "The required completion date as defined by the applicable contract."
            },
            {
                "id": "module-1-q61",
                "qNum": 61,
                "question": "What is the Current Forecast date?",
                "options": [
                    "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee.",
                    "The completion date the schedule currently calculates based on the latest progress and information.",
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance.",
                    "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d."
                ],
                "correctAnswer": 1,
                "explanation": "The completion date the schedule currently calculates based on the latest progress and information.",
                "rawAnswer": "The completion date the schedule currently calculates based on the latest progress and information."
            },
            {
                "id": "module-1-q62",
                "qNum": 62,
                "question": "What should a scheduler be able to recite about their project (interview tip)?",
                "options": [
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities.",
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems.",
                    "Format tab → Critical Tasks (bars turn red), and add the Total Slack column (0 = critical)."
                ],
                "correctAnswer": 1,
                "explanation": "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities.",
                "rawAnswer": "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities."
            },
            {
                "id": "module-1-q63",
                "qNum": 63,
                "question": "What are contract milestones? Give examples.",
                "options": [
                    "Allowable project costs are reimbursed per the contract, plus an agreed fee.",
                    "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates.",
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things."
                ],
                "correctAnswer": 1,
                "explanation": "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates.",
                "rawAnswer": "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates."
            },
            {
                "id": "module-1-q64",
                "qNum": 64,
                "question": "Substantial Completion is required Nov 30 but forecast is Dec 15. What should the scheduler do?",
                "options": [
                    "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined.",
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                    "An activity missing a predecessor or successor (or both).",
                    "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team."
                ],
                "correctAnswer": 3,
                "explanation": "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team.",
                "rawAnswer": "Identify the 15-day variance, find the driving activities through the critical path, explain what changed and communicate the forecast to the team."
            },
            {
                "id": "module-1-q65",
                "qNum": 65,
                "question": "What are the three steps in responding to a forecast variance?",
                "options": [
                    "Level 1: whole project. Level 2: major phases (Design, Procurement, Site Work). Level 2/3: systems and areas (Structure, MEP, Interior, Commissioning). Level 3/4: measurable pieces of work (activities).",
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                    "Identify the variance, find the driving activities, communicate and document.",
                    "How long we expect an activity to take."
                ],
                "correctAnswer": 2,
                "explanation": "Identify the variance, find the driving activities, communicate and document.",
                "rawAnswer": "Identify the variance, find the driving activities, communicate and document."
            },
            {
                "id": "module-1-q66",
                "qNum": 66,
                "question": "What is the scheduler's boundary regarding legal responsibility?",
                "options": [
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price.",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Report the variance and its schedule impact accurately; do not make legal conclusions about responsibility, damages or entitlement."
                ],
                "correctAnswer": 3,
                "explanation": "Report the variance and its schedule impact accurately; do not make legal conclusions about responsibility, damages or entitlement.",
                "rawAnswer": "Report the variance and its schedule impact accurately; do not make legal conclusions about responsibility, damages or entitlement."
            },
            {
                "id": "module-1-q67",
                "qNum": 67,
                "question": "What is a milestone?",
                "options": [
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                    "Engineering (design electrical system) → Procurement (purchase main switchgear) → Construction (install it) → Commissioning (test and energize).",
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d."
                ],
                "correctAnswer": 2,
                "explanation": "An important event with zero duration that marks a point in time, not a period of work.",
                "rawAnswer": "An important event with zero duration that marks a point in time, not a period of work."
            },
            {
                "id": "module-1-q68",
                "qNum": 68,
                "question": "Give an everyday example of a milestone.",
                "options": [
                    "No. A scheduler is a professional analyst who understands the project, analyzes data and communicates meaningful information to decision-makers.",
                    "Initiation, Planning, Execution, Monitor & Control, and Closeout.",
                    "A wedding day: an important event you work toward, not something scheduled as 10 working days.",
                    "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)"
                ],
                "correctAnswer": 2,
                "explanation": "A wedding day: an important event you work toward, not something scheduled as 10 working days.",
                "rawAnswer": "A wedding day: an important event you work toward, not something scheduled as 10 working days."
            },
            {
                "id": "module-1-q69",
                "qNum": 69,
                "question": "List milestone categories with examples.",
                "options": [
                    "Delivery method describes how design/construction is organized; pricing type describes how the work is paid for. They describe different things.",
                    "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete.",
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                    "A wedding day: an important event you work toward, not something scheduled as 10 working days."
                ],
                "correctAnswer": 2,
                "explanation": "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                "rawAnswer": "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered)."
            },
            {
                "id": "module-1-q70",
                "qNum": 70,
                "question": "What is the common milestone mistake?",
                "options": [
                    "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path.",
                    "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others.",
                    "Giving a milestone a duration; it then becomes an activity and the logic will be wrong.",
                    "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them."
                ],
                "correctAnswer": 2,
                "explanation": "Giving a milestone a duration; it then becomes an activity and the logic will be wrong.",
                "rawAnswer": "Giving a milestone a duration; it then becomes an activity and the logic will be wrong."
            },
            {
                "id": "module-1-q71",
                "qNum": 71,
                "question": "What is schedule logic?",
                "options": [
                    "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete.",
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                    "How long we expect an activity to take.",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan."
                ],
                "correctAnswer": 3,
                "explanation": "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                "rawAnswer": "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan."
            },
            {
                "id": "module-1-q72",
                "qNum": 72,
                "question": "Define predecessor and successor.",
                "options": [
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                    "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee.",
                    "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d."
                ],
                "correctAnswer": 0,
                "explanation": "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                "rawAnswer": "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it."
            },
            {
                "id": "module-1-q73",
                "qNum": 73,
                "question": "Give the concrete sequence logic example.",
                "options": [
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "Project name, type, location, budget, duration, delivery method, pricing type, start and finish dates, major milestones, and their responsibilities.",
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d.",
                    "Excavate foundation → install formwork → install rebar → pour concrete → cure then strip formwork."
                ],
                "correctAnswer": 3,
                "explanation": "Excavate foundation → install formwork → install rebar → pour concrete → cure then strip formwork.",
                "rawAnswer": "Excavate foundation → install formwork → install rebar → pour concrete → cure then strip formwork."
            },
            {
                "id": "module-1-q74",
                "qNum": 74,
                "question": "What is a Finish-to-Start (FS) relationship?",
                "options": [
                    "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default.",
                    "The scope statement, drawings and specifications, contract documents, and the deliverables list.",
                    "Report the variance and its schedule impact accurately; do not make legal conclusions about responsibility, damages or entitlement.",
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\"."
                ],
                "correctAnswer": 0,
                "explanation": "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default.",
                "rawAnswer": "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default."
            },
            {
                "id": "module-1-q75",
                "qNum": 75,
                "question": "How do you write FS with a predecessor in MSP?",
                "options": [
                    "Activity B cannot start until Activity A finishes. It is the most common relationship and the MSP default.",
                    "Type the Task ID (e.g., \"10\") or explicitly \"10FS\".",
                    "Don't use large lags to hide actual work (make it a separate activity). Excessive lead creates unrealistic assumptions, so ensure the overlap is achievable in the field.",
                    "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle."
                ],
                "correctAnswer": 1,
                "explanation": "Type the Task ID (e.g., \"10\") or explicitly \"10FS\".",
                "rawAnswer": "Type the Task ID (e.g., \"10\") or explicitly \"10FS\"."
            },
            {
                "id": "module-1-q76",
                "qNum": 76,
                "question": "Explain Start-to-Start (SS) with an example.",
                "options": [
                    "It finishes the following Wednesday; 5 working days = 7 calendar days.",
                    "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS.",
                    "Building a hospital wing is a project. Scheduling nurses, processing admissions and paying vendors are operations.",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it."
                ],
                "correctAnswer": 1,
                "explanation": "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS.",
                "rawAnswer": "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS."
            },
            {
                "id": "module-1-q77",
                "qNum": 77,
                "question": "Explain Finish-to-Finish (FF) with an example.",
                "options": [
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                    "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF.",
                    "A WBS element is a category or group of work that is not scheduled directly. An activity is an actual piece of work with a duration and dates.",
                    "Thinking the scheduler only works during the \"scheduling\" phase, when they actually work throughout the whole life cycle."
                ],
                "correctAnswer": 1,
                "explanation": "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF.",
                "rawAnswer": "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF."
            },
            {
                "id": "module-1-q78",
                "qNum": 78,
                "question": "Explain Start-to-Finish (SF).",
                "options": [
                    "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates).",
                    "The completion date the schedule currently calculates based on the latest progress and information.",
                    "A new 4-floor patient care wing, emergency department expansion, site civil and utilities, MEP systems, and interior finishes and medical equipment rough-in.",
                    "B cannot finish until A starts. It's rare and confusing; understand it exists but use cautiously. MSP: 10SF."
                ],
                "correctAnswer": 3,
                "explanation": "B cannot finish until A starts. It's rare and confusing; understand it exists but use cautiously. MSP: 10SF.",
                "rawAnswer": "B cannot finish until A starts. It's rare and confusing; understand it exists but use cautiously. MSP: 10SF."
            },
            {
                "id": "module-1-q79",
                "qNum": 79,
                "question": "Rank relationship types by how commonly used.",
                "options": [
                    "FS very common, SS common, FF moderate, SF rare.",
                    "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                    "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined.",
                    "Giving a milestone a duration; it then becomes an activity and the logic will be wrong."
                ],
                "correctAnswer": 0,
                "explanation": "FS very common, SS common, FF moderate, SF rare.",
                "rawAnswer": "FS very common, SS common, FF moderate, SF rare."
            },
            {
                "id": "module-1-q80",
                "qNum": 80,
                "question": "What is lag?",
                "options": [
                    "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d.",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "Report the variance and its schedule impact accurately; do not make legal conclusions about responsibility, damages or entitlement.",
                    "The following Friday, exactly two calendar weeks later."
                ],
                "correctAnswer": 0,
                "explanation": "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d.",
                "rawAnswer": "A deliberate waiting period between two logically connected activities, e.g., Pour Concrete FS+7d → Strip Formwork for curing. MSP: 10FS+7d."
            },
            {
                "id": "module-1-q81",
                "qNum": 81,
                "question": "What is lead?",
                "options": [
                    "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d.",
                    "Design-Bid-Build, Design-Build, and EPC.",
                    "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates).",
                    "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF."
                ],
                "correctAnswer": 0,
                "explanation": "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d.",
                "rawAnswer": "Allowing a successor to begin before its predecessor finishes; essentially negative lag. MSP: 10FS-5d."
            },
            {
                "id": "module-1-q82",
                "qNum": 82,
                "question": "What are the warnings for lag and lead?",
                "options": [
                    "Engineering, Procurement, Construction (with Commissioning as a following phase in the hospital example).",
                    "An agreed price for a defined scope, subject to contract terms and approved changes (e.g., kitchen renovation for an agreed $25,000).",
                    "Don't use large lags to hide actual work (make it a separate activity). Excessive lead creates unrealistic assumptions, so ensure the overlap is achievable in the field.",
                    "No. A project can be EPC + Cost-Plus or Design-Build + Fixed Price."
                ],
                "correctAnswer": 2,
                "explanation": "Don't use large lags to hide actual work (make it a separate activity). Excessive lead creates unrealistic assumptions, so ensure the overlap is achievable in the field.",
                "rawAnswer": "Don't use large lags to hide actual work (make it a separate activity). Excessive lead creates unrealistic assumptions, so ensure the overlap is achievable in the field."
            },
            {
                "id": "module-1-q83",
                "qNum": 83,
                "question": "What is an open end?",
                "options": [
                    "Example: 10,000 ft of conduit at 500 ft/day ≈ 20 working days.",
                    "Planning: builds the baseline schedule. Execution: updates progress. Monitor & Control: analyzes and reports. Closeout: documents final schedule performance.",
                    "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates).",
                    "An activity missing a predecessor or successor (or both)."
                ],
                "correctAnswer": 3,
                "explanation": "An activity missing a predecessor or successor (or both).",
                "rawAnswer": "An activity missing a predecessor or successor (or both)."
            },
            {
                "id": "module-1-q84",
                "qNum": 84,
                "question": "Why are open ends a problem, and how do you find them in MSP?",
                "options": [
                    "The Schedule (time) dimension.",
                    "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take).",
                    "They give unrealistic dates, an incorrect critical path and are a quality failure. Add Predecessors/Successors columns and look for blanks (except the first task and final milestone); filter for extreme Total Slack.",
                    "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders."
                ],
                "correctAnswer": 2,
                "explanation": "They give unrealistic dates, an incorrect critical path and are a quality failure. Add Predecessors/Successors columns and look for blanks (except the first task and final milestone); filter for extreme Total Slack.",
                "rawAnswer": "They give unrealistic dates, an incorrect critical path and are a quality failure. Add Predecessors/Successors columns and look for blanks (except the first task and final milestone); filter for extreme Total Slack."
            },
            {
                "id": "module-1-q85",
                "qNum": 85,
                "question": "What is CPM and how does it work?",
                "options": [
                    "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days.",
                    "It finishes the following Wednesday; 5 working days = 7 calendar days.",
                    "How long we expect an activity to take.",
                    "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates)."
                ],
                "correctAnswer": 3,
                "explanation": "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates).",
                "rawAnswer": "The engine that calculates when activities can happen and which sequence controls the completion date, using a forward pass (earliest dates) and backward pass (latest dates)."
            },
            {
                "id": "module-1-q86",
                "qNum": 86,
                "question": "Define ES, EF, LS and LF.",
                "options": [
                    "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path.",
                    "The order in which work must happen; it tells software how activities depend on each other and turns a list of dates into a real plan.",
                    "Early Start: soonest it can begin. Early Finish: earliest it can be complete (ES + duration). Late Start / Late Finish: latest it can start/finish without affecting project completion.",
                    "Identify the variance, find the driving activities, communicate and document."
                ],
                "correctAnswer": 2,
                "explanation": "Early Start: soonest it can begin. Early Finish: earliest it can be complete (ES + duration). Late Start / Late Finish: latest it can start/finish without affecting project completion.",
                "rawAnswer": "Early Start: soonest it can begin. Early Finish: earliest it can be complete (ES + duration). Late Start / Late Finish: latest it can start/finish without affecting project completion."
            },
            {
                "id": "module-1-q87",
                "qNum": 87,
                "question": "What is the formula for Total Float?",
                "options": [
                    "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path.",
                    "Important dates from the contract that must be represented and monitored, e.g., NTP (Jan 5), Building Dry-In (Jun 15), Permanent Power (Sep 10), Substantial Completion (Nov 30), Final Completion (Dec 31), all training dates.",
                    "The completion date the schedule currently calculates based on the latest progress and information.",
                    "Bad: \"Electrical Work\" / \"Concrete\". Better: \"Install Electrical Conduit – Level 1\" / \"Place Concrete – Building A Foundation\"."
                ],
                "correctAnswer": 0,
                "explanation": "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path.",
                "rawAnswer": "Total Float = LS − ES (or LF − EF). If ES = LS, float is 0 and the activity is on the critical path."
            },
            {
                "id": "module-1-q88",
                "qNum": 88,
                "question": "What is Total Float / Total Slack, and what do values 0, >0 and <0 mean?",
                "options": [
                    "Schedule breathing room before the completion date is affected (MSP calls it Total Slack). 0 = critical; >0 = flexibility; <0 = behind and must improve to meet the constraint/finish date.",
                    "Software operation does not make you a scheduler. You must understand the project, build logical plans and interpret results.",
                    "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take).",
                    "Structural. (CIV = Civil/Site, MEP = Mech/Elec/Plumbing, ELEC = Electrical, INT = Interior, COMM = Commissioning.)"
                ],
                "correctAnswer": 0,
                "explanation": "Schedule breathing room before the completion date is affected (MSP calls it Total Slack). 0 = critical; >0 = flexibility; <0 = behind and must improve to meet the constraint/finish date.",
                "rawAnswer": "Schedule breathing room before the completion date is affected (MSP calls it Total Slack). 0 = critical; >0 = flexibility; <0 = behind and must improve to meet the constraint/finish date."
            },
            {
                "id": "module-1-q89",
                "qNum": 89,
                "question": "What is a common beginner mistake about float?",
                "options": [
                    "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee.",
                    "Project type is what is being built (hospital, data center, etc.); pricing type is how the contractor is paid.",
                    "Design is generally completed first, then work is bid competitively and a contractor is selected to build it. Common for traditional public projects.",
                    "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy)."
                ],
                "correctAnswer": 0,
                "explanation": "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee.",
                "rawAnswer": "Saying \"that activity has 5 days of float so it doesn't matter.\" Float is shared and can be consumed by multiple activities in a chain; it is a snapshot, not a guarantee."
            },
            {
                "id": "module-1-q90",
                "qNum": 90,
                "question": "What is the critical path, and what does critical NOT mean?",
                "options": [
                    "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders.",
                    "Excavate foundation → install formwork → install rebar → pour concrete → cure then strip formwork.",
                    "It may define important dates, payment milestones, schedule requirements and reporting requirements the scheduler must reflect and monitor.",
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk."
                ],
                "correctAnswer": 3,
                "explanation": "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                "rawAnswer": "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk."
            },
            {
                "id": "module-1-q91",
                "qNum": 91,
                "question": "In the two-path example, which path is critical?",
                "options": [
                    "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method.",
                    "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float.",
                    "To identify activities, sort and filter, reference them in reports/RFIs, and link logic between them.",
                    "A project is temporary and produces a unique output. Operations are ongoing and repetitive, continuing as long as the organization exists."
                ],
                "correctAnswer": 1,
                "explanation": "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float.",
                "rawAnswer": "Path B: D(10)→E(10)→F(10) = 30 days beats Path A: A(5)→B(5)→C(10) = 20 days, so Path A has 10 days of float."
            },
            {
                "id": "module-1-q92",
                "qNum": 92,
                "question": "What is a constraint, and which types are mentioned?",
                "options": [
                    "B cannot finish until A finishes; they're tied at the finish. E.g., Install Drywall (Area B) FF with Commission HVAC (Area B). MSP: 10FF.",
                    "A unique identifier for each activity, like a filing-system number or phone number.",
                    "An important event with zero duration that marks a point in time, not a period of work.",
                    "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems."
                ],
                "correctAnswer": 3,
                "explanation": "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems.",
                "rawAnswer": "A date-related restriction that forces an activity to a specific date regardless of logic. Types: ASAP (default), Must Start On/Must Finish On (hard), Finish No Later Than (flexible). Use only when truly required; over-constraining hides real problems."
            },
            {
                "id": "module-1-q93",
                "qNum": 93,
                "question": "How do you set the project start date and confirm the calendar in MSP?",
                "options": [
                    "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover.",
                    "Project tab → Project Information → Start Date (enter NTP date, not today's default) and Calendar (Standard Mon–Fri 5-Day×8-Hour).",
                    "A temporary effort with a defined start, a defined end, and a specific goal or deliverable.",
                    "Payment is quantity completed × agreed unit rate (e.g., install 10,000 ft of conduit at $X per foot)."
                ],
                "correctAnswer": 1,
                "explanation": "Project tab → Project Information → Start Date (enter NTP date, not today's default) and Calendar (Standard Mon–Fri 5-Day×8-Hour).",
                "rawAnswer": "Project tab → Project Information → Start Date (enter NTP date, not today's default) and Calendar (Standard Mon–Fri 5-Day×8-Hour)."
            },
            {
                "id": "module-1-q94",
                "qNum": 94,
                "question": "How do you display the critical path in MSP?",
                "options": [
                    "Format tab → Critical Tasks (bars turn red), and add the Total Slack column (0 = critical).",
                    "Design and construction are integrated under a single entity; they may overlap, potentially saving time. The LGL Hospital training example uses this method.",
                    "Predecessor: the activity that comes before and must happen first. Successor: the activity that follows and depends on it.",
                    "ACTION VERB + OBJECT + LOCATION."
                ],
                "correctAnswer": 0,
                "explanation": "Format tab → Critical Tasks (bars turn red), and add the Total Slack column (0 = critical).",
                "rawAnswer": "Format tab → Critical Tasks (bars turn red), and add the Total Slack column (0 = critical)."
            },
            {
                "id": "module-1-q95",
                "qNum": 95,
                "question": "What items should be checked before baselining?",
                "options": [
                    "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                    "Everyday: planning and hosting a birthday party. Professional: building a new hospital wing (start = design kick-off, end = Certificate of Occupancy).",
                    "A hierarchical breakdown of one big project into smaller, organized pieces, like sorting a messy pile into labeled folders.",
                    "Engineering: design it. Procurement: buy it. Construction: build/install it. Commissioning: test and prepare for turnover."
                ],
                "correctAnswer": 0,
                "explanation": "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars.",
                "rawAnswer": "Critical path reasonableness, extreme float, open ends, intentional constraints, project finish vs. contract, zero-duration milestones connected to logic, and correct calendars."
            },
            {
                "id": "module-1-q96",
                "qNum": 96,
                "question": "What is a baseline and how do you set one in MSP?",
                "options": [
                    "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                    "A wedding day: an important event you work toward, not something scheduled as 10 working days.",
                    "ACTION VERB + OBJECT + LOCATION.",
                    "A temporary effort with a defined start, a defined end, and a specific goal or deliverable."
                ],
                "correctAnswer": 0,
                "explanation": "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost.",
                "rawAnswer": "An approved, locked snapshot of the planned schedule used as the reference for variance. Project tab → Set Baseline → Baseline → Entire Project → OK. It stores baseline start, finish, duration, work and cost."
            },
            {
                "id": "module-1-q97",
                "qNum": 97,
                "question": "What is the Status Date and what are the three progress states?",
                "options": [
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered).",
                    "When does excavation start? How long does concrete curing take? What must finish before framing begins?",
                    "Scope (what work is included), Cost (how much it will cost) and Schedule (how long it will take).",
                    "The \"as-of\" date through which progress is current (set it before entering progress). States: Not Started (leave as planned), In Progress (Actual Start + Remaining Duration), Completed (Actual Start + Actual Finish)."
                ],
                "correctAnswer": 3,
                "explanation": "The \"as-of\" date through which progress is current (set it before entering progress). States: Not Started (leave as planned), In Progress (Actual Start + Remaining Duration), Completed (Actual Start + Actual Finish).",
                "rawAnswer": "The \"as-of\" date through which progress is current (set it before entering progress). States: Not Started (leave as planned), In Progress (Actual Start + Remaining Duration), Completed (Actual Start + Actual Finish)."
            },
            {
                "id": "module-1-q98",
                "qNum": 98,
                "question": "What is Remaining Duration and how should it be obtained?",
                "options": [
                    "Payment based on actual time worked and materials/equipment used at agreed rates (e.g., plumber: 10 hrs × $100/hr + materials). Used when scope isn't fully defined.",
                    "B can start after A starts; they run in parallel. E.g., start excavation → start hauling excavated material. MSP: 10SS.",
                    "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete.",
                    "Giving a milestone a duration; it then becomes an activity and the logic will be wrong."
                ],
                "correctAnswer": 2,
                "explanation": "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete.",
                "rawAnswer": "The working days still needed for an in-progress activity; it drives the forecast finish. Get it from the field team, not from a desk. 50% complete doesn't necessarily mean half the time remains, and entering 0 marks the activity complete."
            },
            {
                "id": "module-1-q99",
                "qNum": 99,
                "question": "What is variance, and how is it calculated in MSP?",
                "options": [
                    "The \"as-of\" date through which progress is current (set it before entering progress). States: Not Started (leave as planned), In Progress (Actual Start + Remaining Duration), Completed (Actual Start + Actual Finish).",
                    "Project tab → Project Information → Start Date (enter NTP date, not today's default) and Calendar (Standard Mon–Fri 5-Day×8-Hour).",
                    "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days.",
                    "How long we expect an activity to take."
                ],
                "correctAnswer": 2,
                "explanation": "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days.",
                "rawAnswer": "The difference between baseline and current. Start Variance = Current Start − Baseline Start; Finish Variance = Current Finish − Baseline Finish; Duration Variance = Current − Baseline Duration. Positive = behind; negative = ahead. Example: Baseline Nov 30 vs. forecast Dec 15 = +15 days."
            },
            {
                "id": "module-1-q100",
                "qNum": 100,
                "question": "What are the five variance-analysis questions, and the schedule reporting golden rule?",
                "options": [
                    "Notice to Proceed: authorization to begin work in accordance with the applicable contract.",
                    "The sequence currently controlling the calculated project completion date (zero total float). It does not mean most expensive, most important, hardest or biggest risk.",
                    "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others.",
                    "Start (NTP), Progress (IFC Design Released, Building Dry-In, Mechanical Completion, Permanent Power), Contract (Substantial/Final Completion) and Interface (Owner Equipment Delivered)."
                ],
                "correctAnswer": 2,
                "explanation": "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others.",
                "rawAnswer": "What changed? When did it change? Why? What does it affect? What should happen next? Golden rule: report facts clearly and accurately and don't minimize bad news; the scheduler reports schedule impact and leaves legal conclusions to others."
            }
        ]
    },
    {
        "id": "module-2",
        "aliases": [
            "p6",
            "primavera"
        ],
        "title": "Module 2: Primavera P6 & Schedule Control",
        "description": "Enterprise Project Structure (EPS), P6 interface, F9 scheduling engine, Data Date dynamics, baselines, retained logic, and activity codes.",
        "project": "LGL Northstar Data Center Capital Project",
        "iconName": "Layers",
        "color": "#3B82F6",
        "questions": [
            {
                "id": "module-2-q1",
                "qNum": 1,
                "question": "What is Primavera P6?",
                "options": [
                    "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d.",
                    "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place.",
                    "EPS organizes PROJECTS across the company; WBS organizes WORK inside ONE project.",
                    "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status."
                ],
                "correctAnswer": 1,
                "explanation": "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place.",
                "rawAnswer": "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place."
            },
            {
                "id": "module-2-q2",
                "qNum": 2,
                "question": "What four capabilities does P6 offer?",
                "options": [
                    "Organize (structured activities and work packages), Connect (link activities with logic), Calculate (press F9 for dates, float and critical path), and Track & Report (update progress, compare to baseline, communicate status).",
                    "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating.",
                    "Region: Gulf Coast → Program: Capital Projects → Project: Northstar Data Center.",
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality."
                ],
                "correctAnswer": 0,
                "explanation": "Organize (structured activities and work packages), Connect (link activities with logic), Calculate (press F9 for dates, float and critical path), and Track & Report (update progress, compare to baseline, communicate status).",
                "rawAnswer": "Organize (structured activities and work packages), Connect (link activities with logic), Calculate (press F9 for dates, float and critical path), and Track & Report (update progress, compare to baseline, communicate status)."
            },
            {
                "id": "module-2-q3",
                "qNum": 3,
                "question": "Why do large projects use P6 instead of a spreadsheet or MSP?",
                "options": [
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\"",
                    "Start to Start: B starts when A starts (piping and insulation may begin together once the area is mobilized).",
                    "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet.",
                    "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide."
                ],
                "correctAnswer": 3,
                "explanation": "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide.",
                "rawAnswer": "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide."
            },
            {
                "id": "module-2-q4",
                "qNum": 4,
                "question": "Which industries use P6, according to the module?",
                "options": [
                    "Data centers, construction & infrastructure, oil & gas / EPC, and power & utilities.",
                    "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete).",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                    "Predecessor: the activity that must happen BEFORE. Successor: the activity that comes AFTER."
                ],
                "correctAnswer": 0,
                "explanation": "Data centers, construction & infrastructure, oil & gas / EPC, and power & utilities.",
                "rawAnswer": "Data centers, construction & infrastructure, oil & gas / EPC, and power & utilities."
            },
            {
                "id": "module-2-q5",
                "qNum": 5,
                "question": "How do MSP and P6 compare in scale?",
                "options": [
                    "Nothing. P6 reschedules it from the Data Date forward using remaining duration (e.g., NST-5000 Steel Structure Erection).",
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands."
                ],
                "correctAnswer": 3,
                "explanation": "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands.",
                "rawAnswer": "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands."
            },
            {
                "id": "module-2-q6",
                "qNum": 6,
                "question": "How do MSP and P6 compare in structure?",
                "options": [
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes.",
                    "When the current and baseline bars drift apart, delay or acceleration is immediately visible.",
                    "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor."
                ],
                "correctAnswer": 1,
                "explanation": "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes.",
                "rawAnswer": "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes."
            },
            {
                "id": "module-2-q7",
                "qNum": 7,
                "question": "Which scheduling principles are identical in both tools?",
                "options": [
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "WBS, logic, critical path, baseline and progress.",
                    "The most-used type; duration is driven by the calendar and logic.",
                    "The bar chart on the right side; each bar is one activity's duration on a timeline."
                ],
                "correctAnswer": 1,
                "explanation": "WBS, logic, critical path, baseline and progress.",
                "rawAnswer": "WBS, logic, critical path, baseline and progress."
            },
            {
                "id": "module-2-q8",
                "qNum": 8,
                "question": "What is the Projects Panel?",
                "options": [
                    "Press F9 → confirm Data Date → Schedule → review results.",
                    "It lists all projects in the database; you open your project from here.",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                    "Data centers, construction & infrastructure, oil & gas / EPC, and power & utilities."
                ],
                "correctAnswer": 1,
                "explanation": "It lists all projects in the database; you open your project from here.",
                "rawAnswer": "It lists all projects in the database; you open your project from here."
            },
            {
                "id": "module-2-q9",
                "qNum": 9,
                "question": "What is the Activities screen?",
                "options": [
                    "The main working area where every task, duration and date lives.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9.",
                    "\"NST 5-Day/8-Hr\": Monday–Friday, 8 hrs/day, standard US holidays.",
                    "Start On or After (can't start before date), Finish On or Before (must finish by date), Must Start On (hard, overrides logic), Must Finish On (hardest, used for contract milestones), As Late As Possible (floats to latest position)."
                ],
                "correctAnswer": 0,
                "explanation": "The main working area where every task, duration and date lives.",
                "rawAnswer": "The main working area where every task, duration and date lives."
            },
            {
                "id": "module-2-q10",
                "qNum": 10,
                "question": "What does the WBS view show?",
                "options": [
                    "Never update out-of-sequence activities silently; always document the decision in the schedule narrative.",
                    "Total Float ≤ 10; Area Code = Building A AND % Complete < 100.",
                    "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work.",
                    "The hierarchical breakdown of work, organizing activities into groups."
                ],
                "correctAnswer": 3,
                "explanation": "The hierarchical breakdown of work, organizing activities into groups.",
                "rawAnswer": "The hierarchical breakdown of work, organizing activities into groups."
            },
            {
                "id": "module-2-q11",
                "qNum": 11,
                "question": "What is the Gantt Chart in P6?",
                "options": [
                    "WBS, logic, critical path, baseline and progress.",
                    "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide.",
                    "The bar chart on the right side; each bar is one activity's duration on a timeline.",
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality."
                ],
                "correctAnswer": 2,
                "explanation": "The bar chart on the right side; each bar is one activity's duration on a timeline.",
                "rawAnswer": "The bar chart on the right side; each bar is one activity's duration on a timeline."
            },
            {
                "id": "module-2-q12",
                "qNum": 12,
                "question": "What is the Bottom Details Panel?",
                "options": [
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality.",
                    "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours.",
                    "Clicking an activity shows its predecessors, successors, resources and codes below.",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic."
                ],
                "correctAnswer": 2,
                "explanation": "Clicking an activity shows its predecessors, successors, resources and codes below.",
                "rawAnswer": "Clicking an activity shows its predecessors, successors, resources and codes below."
            },
            {
                "id": "module-2-q13",
                "qNum": 13,
                "question": "What do the Predecessors/Successors tabs show?",
                "options": [
                    "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "What comes before and after each activity.",
                    "It lists all projects in the database; you open your project from here."
                ],
                "correctAnswer": 2,
                "explanation": "What comes before and after each activity.",
                "rawAnswer": "What comes before and after each activity."
            },
            {
                "id": "module-2-q14",
                "qNum": 14,
                "question": "What does EPS stand for and what does it organize?",
                "options": [
                    "A clear ID, name, type and duration.",
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet.",
                    "Never update out-of-sequence activities silently; always document the decision in the schedule narrative."
                ],
                "correctAnswer": 2,
                "explanation": "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet.",
                "rawAnswer": "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet."
            },
            {
                "id": "module-2-q15",
                "qNum": 15,
                "question": "Explain the EPS filing cabinet analogy.",
                "options": [
                    "Logic.",
                    "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong.",
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating."
                ],
                "correctAnswer": 2,
                "explanation": "Each drawer is a region, each folder is a program, and each document is a project.",
                "rawAnswer": "Each drawer is a region, each folder is a program, and each document is a project."
            },
            {
                "id": "module-2-q16",
                "qNum": 16,
                "question": "Where does Northstar sit in the EPS example?",
                "options": [
                    "Predecessor: the activity that must happen BEFORE. Successor: the activity that comes AFTER.",
                    "Region: Gulf Coast → Program: Capital Projects → Project: Northstar Data Center.",
                    "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly.",
                    "Special non-work days or extra work days not in the standard pattern."
                ],
                "correctAnswer": 1,
                "explanation": "Region: Gulf Coast → Program: Capital Projects → Project: Northstar Data Center.",
                "rawAnswer": "Region: Gulf Coast → Program: Capital Projects → Project: Northstar Data Center."
            },
            {
                "id": "module-2-q17",
                "qNum": 17,
                "question": "What is the difference between EPS and WBS?",
                "options": [
                    "10 or 12 hours per day (versus the standard 8).",
                    "An activity whose duration spans the length of other activities, used for supervision, project management or inspection running continuously.",
                    "Start On or After (can't start before date), Finish On or Before (must finish by date), Must Start On (hard, overrides logic), Must Finish On (hardest, used for contract milestones), As Late As Possible (floats to latest position).",
                    "EPS organizes PROJECTS across the company; WBS organizes WORK inside ONE project."
                ],
                "correctAnswer": 3,
                "explanation": "EPS organizes PROJECTS across the company; WBS organizes WORK inside ONE project.",
                "rawAnswer": "EPS organizes PROJECTS across the company; WBS organizes WORK inside ONE project."
            },
            {
                "id": "module-2-q18",
                "qNum": 18,
                "question": "What is the Project ID for Northstar?",
                "options": [
                    "NST-2025-DC.",
                    "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout.",
                    "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes."
                ],
                "correctAnswer": 0,
                "explanation": "NST-2025-DC.",
                "rawAnswer": "NST-2025-DC."
            },
            {
                "id": "module-2-q19",
                "qNum": 19,
                "question": "What are the Northstar planned start and must-finish-by dates?",
                "options": [
                    "Planned Start 01-Jan-2026; Must Finish By 31-Dec-2027.",
                    "NST-1000 Notice to Proceed (0d), NST-1010 Geotechnical Survey (15d), NST-2010 Transformer Delivery (90d), NST-3000 Site Excavation (20d), NST-4000 Foundation Pour (30d), NST-5000 Steel Structure Erection (45d), NST-9000 Facility Commissioning Complete (0d).",
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "Total Float ≤ 10; Area Code = Building A AND % Complete < 100."
                ],
                "correctAnswer": 0,
                "explanation": "Planned Start 01-Jan-2026; Must Finish By 31-Dec-2027.",
                "rawAnswer": "Planned Start 01-Jan-2026; Must Finish By 31-Dec-2027."
            },
            {
                "id": "module-2-q20",
                "qNum": 20,
                "question": "What is the Northstar project name and responsible manager?",
                "options": [
                    "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls.",
                    "File → New → Project → Complete Wizard → Enter fields → Finish.",
                    "What comes before and after each activity.",
                    "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly."
                ],
                "correctAnswer": 0,
                "explanation": "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls.",
                "rawAnswer": "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls."
            },
            {
                "id": "module-2-q21",
                "qNum": 21,
                "question": "What is the click path to create a new project?",
                "options": [
                    "Select activity → Bottom Panel → Predecessors tab → Add → Choose predecessor → Select relationship type → OK.",
                    "File → New → Project → Complete Wizard → Enter fields → Finish.",
                    "Special non-work days or extra work days not in the standard pattern.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9."
                ],
                "correctAnswer": 1,
                "explanation": "File → New → Project → Complete Wizard → Enter fields → Finish.",
                "rawAnswer": "File → New → Project → Complete Wizard → Enter fields → Finish."
            },
            {
                "id": "module-2-q22",
                "qNum": 22,
                "question": "What is the WBS compared to in the module, and why build it first?",
                "options": [
                    "A clear ID, name, type and duration.",
                    "The longest sequence of activities that determines the project finish date; any delay to a critical activity delays the entire project.",
                    "It skips them automatically in date calculations.",
                    "The project's table of contents. When you add 200+ activities later, each slots into the correct WBS bucket, and filtering, grouping and reporting rely on it being right from day one."
                ],
                "correctAnswer": 3,
                "explanation": "The project's table of contents. When you add 200+ activities later, each slots into the correct WBS bucket, and filtering, grouping and reporting rely on it being right from day one.",
                "rawAnswer": "The project's table of contents. When you add 200+ activities later, each slots into the correct WBS bucket, and filtering, grouping and reporting rely on it being right from day one."
            },
            {
                "id": "module-2-q23",
                "qNum": 23,
                "question": "What is the click path to build the WBS?",
                "options": [
                    "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status.",
                    "Project → WBS → Add → Name each level → Repeat for all branches.",
                    "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls.",
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout."
                ],
                "correctAnswer": 1,
                "explanation": "Project → WBS → Add → Name each level → Repeat for all branches.",
                "rawAnswer": "Project → WBS → Add → Name each level → Repeat for all branches."
            },
            {
                "id": "module-2-q24",
                "qNum": 24,
                "question": "What does a calendar do in P6?",
                "options": [
                    "Logic.",
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\"",
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios.",
                    "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong."
                ],
                "correctAnswer": 3,
                "explanation": "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong.",
                "rawAnswer": "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong."
            },
            {
                "id": "module-2-q25",
                "qNum": 25,
                "question": "What four settings make up a calendar?",
                "options": [
                    "Workweek, Hours per Day, Holidays, and Exceptions.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9.",
                    "10 or 12 hours per day (versus the standard 8).",
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path."
                ],
                "correctAnswer": 0,
                "explanation": "Workweek, Hours per Day, Holidays, and Exceptions.",
                "rawAnswer": "Workweek, Hours per Day, Holidays, and Exceptions."
            },
            {
                "id": "module-2-q26",
                "qNum": 26,
                "question": "What is the Northstar calendar?",
                "options": [
                    "\"NST 5-Day/8-Hr\": Monday–Friday, 8 hrs/day, standard US holidays.",
                    "2-/3-Week Lookahead, Critical Path Report, Milestone Report, Variance Report, and Schedule Narrative.",
                    "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                    "Total Float ≤ 10; Area Code = Building A AND % Complete < 100."
                ],
                "correctAnswer": 0,
                "explanation": "\"NST 5-Day/8-Hr\": Monday–Friday, 8 hrs/day, standard US holidays.",
                "rawAnswer": "\"NST 5-Day/8-Hr\": Monday–Friday, 8 hrs/day, standard US holidays."
            },
            {
                "id": "module-2-q27",
                "qNum": 27,
                "question": "What are Exceptions in a calendar?",
                "options": [
                    "(1) Create project in EPS, (2) build WBS + calendar, (3) add activities + logic, (4) F9 → review → quality check, (5) baseline → set Data Date → update → report.",
                    "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating.",
                    "Special non-work days or extra work days not in the standard pattern.",
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality."
                ],
                "correctAnswer": 2,
                "explanation": "Special non-work days or extra work days not in the standard pattern.",
                "rawAnswer": "Special non-work days or extra work days not in the standard pattern."
            },
            {
                "id": "module-2-q28",
                "qNum": 28,
                "question": "What is the click path to create a calendar?",
                "options": [
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout.",
                    "When the current and baseline bars drift apart, delay or acceleration is immediately visible.",
                    "Admin → Calendars → Add → Name → Set workweek → Set hours → Add holidays → Close.",
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\""
                ],
                "correctAnswer": 2,
                "explanation": "Admin → Calendars → Add → Name → Set workweek → Set hours → Add holidays → Close.",
                "rawAnswer": "Admin → Calendars → Add → Name → Set workweek → Set hours → Add holidays → Close."
            },
            {
                "id": "module-2-q29",
                "qNum": 29,
                "question": "How does P6 treat holidays?",
                "options": [
                    "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes.",
                    "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                    "Never update out-of-sequence activities silently; always document the decision in the schedule narrative.",
                    "It skips them automatically in date calculations."
                ],
                "correctAnswer": 3,
                "explanation": "It skips them automatically in date calculations.",
                "rawAnswer": "It skips them automatically in date calculations."
            },
            {
                "id": "module-2-q30",
                "qNum": 30,
                "question": "What shift hours are mentioned for shift work?",
                "options": [
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                    "10 or 12 hours per day (versus the standard 8).",
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits."
                ],
                "correctAnswer": 2,
                "explanation": "10 or 12 hours per day (versus the standard 8).",
                "rawAnswer": "10 or 12 hours per day (versus the standard 8)."
            },
            {
                "id": "module-2-q31",
                "qNum": 31,
                "question": "What must every activity have before you connect anything?",
                "options": [
                    "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together).",
                    "PV = $5,000, EV = $4,000, AC = $4,500. SV = EV − PV = −$1,000 (behind). CV = EV − AC = −$500 (over budget).",
                    "Project → WBS → Add → Name each level → Repeat for all branches.",
                    "A clear ID, name, type and duration."
                ],
                "correctAnswer": 3,
                "explanation": "A clear ID, name, type and duration.",
                "rawAnswer": "A clear ID, name, type and duration."
            },
            {
                "id": "module-2-q32",
                "qNum": 32,
                "question": "Give sample Northstar activity IDs and durations.",
                "options": [
                    "NST-1000 Notice to Proceed (0d), NST-1010 Geotechnical Survey (15d), NST-2010 Transformer Delivery (90d), NST-3000 Site Excavation (20d), NST-4000 Foundation Pour (30d), NST-5000 Steel Structure Erection (45d), NST-9000 Facility Commissioning Complete (0d).",
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "NST-2025-DC.",
                    "No; the scheduler writes it. A scheduler's job is to communicate the schedule, not only maintain it."
                ],
                "correctAnswer": 0,
                "explanation": "NST-1000 Notice to Proceed (0d), NST-1010 Geotechnical Survey (15d), NST-2010 Transformer Delivery (90d), NST-3000 Site Excavation (20d), NST-4000 Foundation Pour (30d), NST-5000 Steel Structure Erection (45d), NST-9000 Facility Commissioning Complete (0d).",
                "rawAnswer": "NST-1000 Notice to Proceed (0d), NST-1010 Geotechnical Survey (15d), NST-2010 Transformer Delivery (90d), NST-3000 Site Excavation (20d), NST-4000 Foundation Pour (30d), NST-5000 Steel Structure Erection (45d), NST-9000 Facility Commissioning Complete (0d)."
            },
            {
                "id": "module-2-q33",
                "qNum": 33,
                "question": "What is the professional naming rule for activities?",
                "options": [
                    "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                    "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved.",
                    "The main working area where every task, duration and date lives.",
                    "Logic."
                ],
                "correctAnswer": 0,
                "explanation": "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                "rawAnswer": "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\""
            },
            {
                "id": "module-2-q34",
                "qNum": 34,
                "question": "What is a Task Dependent activity?",
                "options": [
                    "The most-used type; duration is driven by the calendar and logic.",
                    "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started."
                ],
                "correctAnswer": 0,
                "explanation": "The most-used type; duration is driven by the calendar and logic.",
                "rawAnswer": "The most-used type; duration is driven by the calendar and logic."
            },
            {
                "id": "module-2-q35",
                "qNum": 35,
                "question": "What is a Resource Dependent activity?",
                "options": [
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026.",
                    "Special non-work days or extra work days not in the standard pattern.",
                    "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\""
                ],
                "correctAnswer": 0,
                "explanation": "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                "rawAnswer": "Duration depends on resource availability, e.g., if the crane isn't available the activity waits."
            },
            {
                "id": "module-2-q36",
                "qNum": 36,
                "question": "What are Start and Finish Milestones?",
                "options": [
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete)."
                ],
                "correctAnswer": 3,
                "explanation": "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete).",
                "rawAnswer": "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete)."
            },
            {
                "id": "module-2-q37",
                "qNum": 37,
                "question": "What is a Level of Effort activity?",
                "options": [
                    "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide.",
                    "An activity whose duration spans the length of other activities, used for supervision, project management or inspection running continuously.",
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                    "File → New → Project → Complete Wizard → Enter fields → Finish."
                ],
                "correctAnswer": 1,
                "explanation": "An activity whose duration spans the length of other activities, used for supervision, project management or inspection running continuously.",
                "rawAnswer": "An activity whose duration spans the length of other activities, used for supervision, project management or inspection running continuously."
            },
            {
                "id": "module-2-q38",
                "qNum": 38,
                "question": "Which activity types should beginners focus on?",
                "options": [
                    "Planned Start 01-Jan-2026; Must Finish By 31-Dec-2027.",
                    "Task Dependent and Milestones, used about 90% of the time on Northstar.",
                    "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed.",
                    "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet."
                ],
                "correctAnswer": 1,
                "explanation": "Task Dependent and Milestones, used about 90% of the time on Northstar.",
                "rawAnswer": "Task Dependent and Milestones, used about 90% of the time on Northstar."
            },
            {
                "id": "module-2-q39",
                "qNum": 39,
                "question": "Define predecessor and successor in P6.",
                "options": [
                    "Predecessor: the activity that must happen BEFORE. Successor: the activity that comes AFTER.",
                    "Reorganizes the view without changing the data, e.g., grouping by WBS → Discipline → Area.",
                    "With hundreds or thousands of activities, spreadsheets or MSP files can't handle the complexity. P6 is the industry standard for capital projects worldwide.",
                    "Clicking an activity shows its predecessors, successors, resources and codes below."
                ],
                "correctAnswer": 0,
                "explanation": "Predecessor: the activity that must happen BEFORE. Successor: the activity that comes AFTER.",
                "rawAnswer": "Predecessor: the activity that must happen BEFORE. Successor: the activity that comes AFTER."
            },
            {
                "id": "module-2-q40",
                "qNum": 40,
                "question": "What does FS mean in P6? Give an example.",
                "options": [
                    "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                    "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                    "Reorganizes the view without changing the data, e.g., grouping by WBS → Discipline → Area.",
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios."
                ],
                "correctAnswer": 0,
                "explanation": "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                "rawAnswer": "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common."
            },
            {
                "id": "module-2-q41",
                "qNum": 41,
                "question": "What does SS mean? Give an example.",
                "options": [
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours.",
                    "Calculates the schedule; P6 works out all dates, float and the critical path. Until you do, dates may be placeholders.",
                    "Start to Start: B starts when A starts (piping and insulation may begin together once the area is mobilized)."
                ],
                "correctAnswer": 3,
                "explanation": "Start to Start: B starts when A starts (piping and insulation may begin together once the area is mobilized).",
                "rawAnswer": "Start to Start: B starts when A starts (piping and insulation may begin together once the area is mobilized)."
            },
            {
                "id": "module-2-q42",
                "qNum": 42,
                "question": "What does FF mean? Give an example.",
                "options": [
                    "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together).",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026.",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                    "Set the Data Date first, then update each activity based on field reports."
                ],
                "correctAnswer": 0,
                "explanation": "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together).",
                "rawAnswer": "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together)."
            },
            {
                "id": "module-2-q43",
                "qNum": 43,
                "question": "What does SF mean and when is it used?",
                "options": [
                    "Two bars per activity: the top is the current schedule, the bottom is the baseline. Drifting apart shows delay or acceleration.",
                    "It skips them automatically in date calculations.",
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios."
                ],
                "correctAnswer": 3,
                "explanation": "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios.",
                "rawAnswer": "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios."
            },
            {
                "id": "module-2-q44",
                "qNum": 44,
                "question": "What is the click path for adding a predecessor?",
                "options": [
                    "\"NST 5-Day/8-Hr\": Monday–Friday, 8 hrs/day, standard US holidays.",
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                    "Select activity → Bottom Panel → Predecessors tab → Add → Choose predecessor → Select relationship type → OK.",
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout."
                ],
                "correctAnswer": 2,
                "explanation": "Select activity → Bottom Panel → Predecessors tab → Add → Choose predecessor → Select relationship type → OK.",
                "rawAnswer": "Select activity → Bottom Panel → Predecessors tab → Add → Choose predecessor → Select relationship type → OK."
            },
            {
                "id": "module-2-q45",
                "qNum": 45,
                "question": "What turns a list of activities into a real schedule?",
                "options": [
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "Logic.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9."
                ],
                "correctAnswer": 2,
                "explanation": "Logic.",
                "rawAnswer": "Logic."
            },
            {
                "id": "module-2-q46",
                "qNum": 46,
                "question": "What sequence is illustrated in the logic chain graphic?",
                "options": [
                    "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated.",
                    "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong.",
                    "Excavate → Foundation → Structure → Equipment.",
                    "Not Started, In Progress, and Complete."
                ],
                "correctAnswer": 2,
                "explanation": "Excavate → Foundation → Structure → Equipment.",
                "rawAnswer": "Excavate → Foundation → Structure → Equipment."
            },
            {
                "id": "module-2-q47",
                "qNum": 47,
                "question": "What does pressing F9 do?",
                "options": [
                    "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "Calculates the schedule; P6 works out all dates, float and the critical path. Until you do, dates may be placeholders.",
                    "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours."
                ],
                "correctAnswer": 2,
                "explanation": "Calculates the schedule; P6 works out all dates, float and the critical path. Until you do, dates may be placeholders.",
                "rawAnswer": "Calculates the schedule; P6 works out all dates, float and the critical path. Until you do, dates may be placeholders."
            },
            {
                "id": "module-2-q48",
                "qNum": 48,
                "question": "List the F9 click-by-click steps.",
                "options": [
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\"",
                    "It skips them automatically in date calculations.",
                    "(1) Press F9 or Tools → Schedule, (2) confirm the Data Date, (3) click Schedule, (4) review the Schedule Log for warnings, (5) check that dates have calculated."
                ],
                "correctAnswer": 3,
                "explanation": "(1) Press F9 or Tools → Schedule, (2) confirm the Data Date, (3) click Schedule, (4) review the Schedule Log for warnings, (5) check that dates have calculated.",
                "rawAnswer": "(1) Press F9 or Tools → Schedule, (2) confirm the Data Date, (3) click Schedule, (4) review the Schedule Log for warnings, (5) check that dates have calculated."
            },
            {
                "id": "module-2-q49",
                "qNum": 49,
                "question": "What does P6 calculate after F9?",
                "options": [
                    "Early Start/Finish, Late Start/Finish, Total Float and the Critical Path.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "Select activity → Bottom Panel → Predecessors tab → Add → Choose predecessor → Select relationship type → OK.",
                    "Set the Data Date first, then update each activity based on field reports."
                ],
                "correctAnswer": 0,
                "explanation": "Early Start/Finish, Late Start/Finish, Total Float and the Critical Path.",
                "rawAnswer": "Early Start/Finish, Late Start/Finish, Total Float and the Critical Path."
            },
            {
                "id": "module-2-q50",
                "qNum": 50,
                "question": "What is the F9 rule?",
                "options": [
                    "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work.",
                    "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\"",
                    "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete).",
                    "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated."
                ],
                "correctAnswer": 3,
                "explanation": "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated.",
                "rawAnswer": "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated."
            },
            {
                "id": "module-2-q51",
                "qNum": 51,
                "question": "What is the Data Date?",
                "options": [
                    "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                    "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating.",
                    "NST-1000 Notice to Proceed (0d), NST-1010 Geotechnical Survey (15d), NST-2010 Transformer Delivery (90d), NST-3000 Site Excavation (20d), NST-4000 Foundation Pour (30d), NST-5000 Steel Structure Erection (45d), NST-9000 Facility Commissioning Complete (0d).",
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9."
                ],
                "correctAnswer": 1,
                "explanation": "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating.",
                "rawAnswer": "The vertical line between past (actuals) and future (forecast); the most important concept in schedule updating."
            },
            {
                "id": "module-2-q52",
                "qNum": 52,
                "question": "What happens left and right of the Data Date?",
                "options": [
                    "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes.",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                    "Task Dependent and Milestones, used about 90% of the time on Northstar.",
                    "When the current and baseline bars drift apart, delay or acceleration is immediately visible."
                ],
                "correctAnswer": 1,
                "explanation": "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                "rawAnswer": "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic."
            },
            {
                "id": "module-2-q53",
                "qNum": 53,
                "question": "What is the Northstar Data Date example?",
                "options": [
                    "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                    "With a Data Date of 01-Jun-2026, any activity that should have started before June 1 must have actual progress entered, otherwise P6 wrongly shows it as future work.",
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "WBS, logic, critical path, baseline and progress."
                ],
                "correctAnswer": 1,
                "explanation": "With a Data Date of 01-Jun-2026, any activity that should have started before June 1 must have actual progress entered, otherwise P6 wrongly shows it as future work.",
                "rawAnswer": "With a Data Date of 01-Jun-2026, any activity that should have started before June 1 must have actual progress entered, otherwise P6 wrongly shows it as future work."
            },
            {
                "id": "module-2-q54",
                "qNum": 54,
                "question": "What is the critical path?",
                "options": [
                    "The longest sequence of activities that determines the project finish date; any delay to a critical activity delays the entire project.",
                    "Start to Start: B starts when A starts (piping and insulation may begin together once the area is mobilized).",
                    "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together).",
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\""
                ],
                "correctAnswer": 0,
                "explanation": "The longest sequence of activities that determines the project finish date; any delay to a critical activity delays the entire project.",
                "rawAnswer": "The longest sequence of activities that determines the project finish date; any delay to a critical activity delays the entire project."
            },
            {
                "id": "module-2-q55",
                "qNum": 55,
                "question": "How do you display and analyze critical activities in P6?",
                "options": [
                    "What comes before and after each activity.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "View → Bars (critical in red, non-critical in blue); add a Total Float column (0 = critical); right-click → Trace Logic to highlight predecessor/successor chains."
                ],
                "correctAnswer": 3,
                "explanation": "View → Bars (critical in red, non-critical in blue); add a Total Float column (0 = critical); right-click → Trace Logic to highlight predecessor/successor chains.",
                "rawAnswer": "View → Bars (critical in red, non-critical in blue); add a Total Float column (0 = critical); right-click → Trace Logic to highlight predecessor/successor chains."
            },
            {
                "id": "module-2-q56",
                "qNum": 56,
                "question": "What is the Northstar critical path example?",
                "options": [
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor.",
                    "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved."
                ],
                "correctAnswer": 0,
                "explanation": "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                "rawAnswer": "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9."
            },
            {
                "id": "module-2-q57",
                "qNum": 57,
                "question": "What is the interview tip for the critical path?",
                "options": [
                    "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\"",
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "Logic.",
                    "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place."
                ],
                "correctAnswer": 0,
                "explanation": "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\"",
                "rawAnswer": "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\""
            },
            {
                "id": "module-2-q58",
                "qNum": 58,
                "question": "What is a baseline?",
                "options": [
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "10 or 12 hours per day (versus the standard 8).",
                    "No; the scheduler writes it. A scheduler's job is to communicate the schedule, not only maintain it."
                ],
                "correctAnswer": 1,
                "explanation": "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                "rawAnswer": "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured."
            },
            {
                "id": "module-2-q59",
                "qNum": 59,
                "question": "What are the steps to create and assign a baseline in P6?",
                "options": [
                    "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls.",
                    "Left: activities that should have started/finished need actual dates entered; P6 won't schedule past work using remaining duration. Right: work not yet started is forecast forward using remaining duration and logic.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9.",
                    "Excavate → Foundation → Structure → Equipment."
                ],
                "correctAnswer": 2,
                "explanation": "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9.",
                "rawAnswer": "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9."
            },
            {
                "id": "module-2-q60",
                "qNum": 60,
                "question": "What do you see on the Gantt after assigning a baseline?",
                "options": [
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios.",
                    "A clear ID, name, type and duration.",
                    "Set the Data Date first, then update each activity based on field reports.",
                    "Two bars per activity: the top is the current schedule, the bottom is the baseline. Drifting apart shows delay or acceleration."
                ],
                "correctAnswer": 3,
                "explanation": "Two bars per activity: the top is the current schedule, the bottom is the baseline. Drifting apart shows delay or acceleration.",
                "rawAnswer": "Two bars per activity: the top is the current schedule, the bottom is the baseline. Drifting apart shows delay or acceleration."
            },
            {
                "id": "module-2-q61",
                "qNum": 61,
                "question": "What is the professional tip about baselines?",
                "options": [
                    "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor.",
                    "It skips them automatically in date calculations.",
                    "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                    "File → New → Project → Complete Wizard → Enter fields → Finish."
                ],
                "correctAnswer": 2,
                "explanation": "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes.",
                "rawAnswer": "Never overwrite the original baseline; create a new revision (Rev 1, Rev 2) when scope changes."
            },
            {
                "id": "module-2-q62",
                "qNum": 62,
                "question": "How does the baseline reveal variance?",
                "options": [
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands.",
                    "With a Data Date of 01-Jun-2026, any activity that should have started before June 1 must have actual progress entered, otherwise P6 wrongly shows it as future work.",
                    "10 or 12 hours per day (versus the standard 8).",
                    "When the current and baseline bars drift apart, delay or acceleration is immediately visible."
                ],
                "correctAnswer": 3,
                "explanation": "When the current and baseline bars drift apart, delay or acceleration is immediately visible.",
                "rawAnswer": "When the current and baseline bars drift apart, delay or acceleration is immediately visible."
            },
            {
                "id": "module-2-q63",
                "qNum": 63,
                "question": "What must you do before updating progress?",
                "options": [
                    "Negative float (project already behind), activities in the past without actuals, and excessive float (>100 days), usually missing logic.",
                    "(1) Press F9 or Tools → Schedule, (2) confirm the Data Date, (3) click Schedule, (4) review the Schedule Log for warnings, (5) check that dates have calculated.",
                    "Set the Data Date first, then update each activity based on field reports.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026."
                ],
                "correctAnswer": 2,
                "explanation": "Set the Data Date first, then update each activity based on field reports.",
                "rawAnswer": "Set the Data Date first, then update each activity based on field reports."
            },
            {
                "id": "module-2-q64",
                "qNum": 64,
                "question": "What are the three states of an activity?",
                "options": [
                    "Not Started, In Progress, and Complete.",
                    "View → Bars (critical in red, non-critical in blue); add a Total Float column (0 = critical); right-click → Trace Logic to highlight predecessor/successor chains.",
                    "The bar chart on the right side; each bar is one activity's duration on a timeline.",
                    "Task Dependent and Milestones, used about 90% of the time on Northstar."
                ],
                "correctAnswer": 0,
                "explanation": "Not Started, In Progress, and Complete.",
                "rawAnswer": "Not Started, In Progress, and Complete."
            },
            {
                "id": "module-2-q65",
                "qNum": 65,
                "question": "What do you enter for a Not Started activity?",
                "options": [
                    "Task Dependent and Milestones, used about 90% of the time on Northstar.",
                    "Nothing. P6 reschedules it from the Data Date forward using remaining duration (e.g., NST-5000 Steel Structure Erection).",
                    "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                    "Planned Start 01-Jan-2026; Must Finish By 31-Dec-2027."
                ],
                "correctAnswer": 1,
                "explanation": "Nothing. P6 reschedules it from the Data Date forward using remaining duration (e.g., NST-5000 Steel Structure Erection).",
                "rawAnswer": "Nothing. P6 reschedules it from the Data Date forward using remaining duration (e.g., NST-5000 Steel Structure Erection)."
            },
            {
                "id": "module-2-q66",
                "qNum": 66,
                "question": "What do you enter for an In Progress activity?",
                "options": [
                    "View → Group and Sort → Add grouping fields (WBS, Discipline code, Area code) → OK → F9.",
                    "Actual Start + Remaining Duration (working days left). Example: NST-3000 Excavation started 05-Jan-2026 with 8 days remaining.",
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "Never update out-of-sequence activities silently; always document the decision in the schedule narrative."
                ],
                "correctAnswer": 1,
                "explanation": "Actual Start + Remaining Duration (working days left). Example: NST-3000 Excavation started 05-Jan-2026 with 8 days remaining.",
                "rawAnswer": "Actual Start + Remaining Duration (working days left). Example: NST-3000 Excavation started 05-Jan-2026 with 8 days remaining."
            },
            {
                "id": "module-2-q67",
                "qNum": 67,
                "question": "What do you enter for a Complete activity?",
                "options": [
                    "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated.",
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands.",
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026."
                ],
                "correctAnswer": 3,
                "explanation": "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026.",
                "rawAnswer": "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026."
            },
            {
                "id": "module-2-q68",
                "qNum": 68,
                "question": "What do you do after updating all activities?",
                "options": [
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout.",
                    "Press F9 → confirm Data Date → Schedule → review results.",
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                    "10 or 12 hours per day (versus the standard 8)."
                ],
                "correctAnswer": 1,
                "explanation": "Press F9 → confirm Data Date → Schedule → review results.",
                "rawAnswer": "Press F9 → confirm Data Date → Schedule → review results."
            },
            {
                "id": "module-2-q69",
                "qNum": 69,
                "question": "What is the situation behind Retained Logic vs. Progress Override?",
                "options": [
                    "Clicking an activity shows its predecessors, successors, resources and codes below.",
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured.",
                    "Logic.",
                    "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work."
                ],
                "correctAnswer": 3,
                "explanation": "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work.",
                "rawAnswer": "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work."
            },
            {
                "id": "module-2-q70",
                "qNum": 70,
                "question": "What is Retained Logic?",
                "options": [
                    "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                    "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status.",
                    "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed.",
                    "Nothing. P6 reschedules it from the Data Date forward using remaining duration (e.g., NST-5000 Steel Structure Erection)."
                ],
                "correctAnswer": 0,
                "explanation": "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started.",
                "rawAnswer": "P6 holds B's remaining work until A finishes; the original logic is respected even though B already started."
            },
            {
                "id": "module-2-q71",
                "qNum": 71,
                "question": "What is Progress Override?",
                "options": [
                    "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status.",
                    "\"The critical path is the longest path through the network. It determines the project end date. Activities on it have zero total float.\"",
                    "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                    "The bar chart on the right side; each bar is one activity's duration on a timeline."
                ],
                "correctAnswer": 0,
                "explanation": "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status.",
                "rawAnswer": "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status."
            },
            {
                "id": "module-2-q72",
                "qNum": 72,
                "question": "What is the default setting and when should it change?",
                "options": [
                    "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved.",
                    "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                    "It lists all projects in the database; you open your project from here.",
                    "Special non-work days or extra work days not in the standard pattern."
                ],
                "correctAnswer": 0,
                "explanation": "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved.",
                "rawAnswer": "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved."
            },
            {
                "id": "module-2-q73",
                "qNum": 73,
                "question": "What is out-of-sequence progress?",
                "options": [
                    "Tells P6 when work is permitted; without a correct calendar every calculated date could be wrong.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "Retained Logic, which most contract schedulers use. Change only when the PM confirms the out-of-sequence work is intentional and approved.",
                    "NST-2025-DC."
                ],
                "correctAnswer": 1,
                "explanation": "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                "rawAnswer": "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix."
            },
            {
                "id": "module-2-q74",
                "qNum": 74,
                "question": "What is the Northstar out-of-sequence example?",
                "options": [
                    "Project Name: LGL Northstar Data Center. Responsible Manager: LGL Project Controls.",
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality.",
                    "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed.",
                    "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor."
                ],
                "correctAnswer": 3,
                "explanation": "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor.",
                "rawAnswer": "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor."
            },
            {
                "id": "module-2-q75",
                "qNum": 75,
                "question": "What must the scheduler investigate for out-of-sequence work?",
                "options": [
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Project → WBS → Add → Name each level → Repeat for all branches.",
                    "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality.",
                    "Project → Maintain Baselines → Add → Name it (e.g., \"NST Baseline Rev 0 - Jan 2026\") → Project → Assign Baselines → select as Project Baseline → F9."
                ],
                "correctAnswer": 2,
                "explanation": "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality.",
                "rawAnswer": "Whether the PM approved it, whether the logic needs revising, any safety/quality risk, whether a schedule revision should go to the client, and which setting (Retained Logic or Progress Override) best reflects reality."
            },
            {
                "id": "module-2-q76",
                "qNum": 76,
                "question": "What is the rule for documenting out-of-sequence decisions?",
                "options": [
                    "Duration depends on resource availability, e.g., if the crane isn't available the activity waits.",
                    "Special non-work days or extra work days not in the standard pattern.",
                    "Never update out-of-sequence activities silently; always document the decision in the schedule narrative.",
                    "Clicking an activity shows its predecessors, successors, resources and codes below."
                ],
                "correctAnswer": 2,
                "explanation": "Never update out-of-sequence activities silently; always document the decision in the schedule narrative.",
                "rawAnswer": "Never update out-of-sequence activities silently; always document the decision in the schedule narrative."
            },
            {
                "id": "module-2-q77",
                "qNum": 77,
                "question": "What is a constraint in P6?",
                "options": [
                    "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly.",
                    "View → Group and Sort → Add grouping fields (WBS, Discipline code, Area code) → OK → F9."
                ],
                "correctAnswer": 2,
                "explanation": "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly.",
                "rawAnswer": "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly."
            },
            {
                "id": "module-2-q78",
                "qNum": 78,
                "question": "Name the common P6 constraints and their effect.",
                "options": [
                    "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "Start On or After (can't start before date), Finish On or Before (must finish by date), Must Start On (hard, overrides logic), Must Finish On (hardest, used for contract milestones), As Late As Possible (floats to latest position).",
                    "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d."
                ],
                "correctAnswer": 2,
                "explanation": "Start On or After (can't start before date), Finish On or Before (must finish by date), Must Start On (hard, overrides logic), Must Finish On (hardest, used for contract milestones), As Late As Possible (floats to latest position).",
                "rawAnswer": "Start On or After (can't start before date), Finish On or Before (must finish by date), Must Start On (hard, overrides logic), Must Finish On (hardest, used for contract milestones), As Late As Possible (floats to latest position)."
            },
            {
                "id": "module-2-q79",
                "qNum": 79,
                "question": "What are the dangers of excessive constraints?",
                "options": [
                    "Where is the project now? What changed this period? What is currently critical? What is delayed? What action is needed? What is the forecast finish?",
                    "Write as a Noun + Verb in past tense or as a deliverable, e.g., \"Foundation Pour Complete,\" not \"Doing foundation.\"",
                    "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated.",
                    "P6 can't show true float, activities that are behind may show positive float, the critical path may appear shorter than it is, and client reviewers flag over-constrained schedules as poor quality."
                ],
                "correctAnswer": 3,
                "explanation": "P6 can't show true float, activities that are behind may show positive float, the critical path may appear shorter than it is, and client reviewers flag over-constrained schedules as poor quality.",
                "rawAnswer": "P6 can't show true float, activities that are behind may show positive float, the critical path may appear shorter than it is, and client reviewers flag over-constrained schedules as poor quality."
            },
            {
                "id": "module-2-q80",
                "qNum": 80,
                "question": "What is the constraint rule?",
                "options": [
                    "Excavate → Underground Utilities → Foundation → Structure → Equipment Installation → Electrical → Testing → Commissioning, each with 0 float after F9.",
                    "Early Start/Finish, Late Start/Finish, Total Float and the Critical Path.",
                    "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed.",
                    "The main working area where every task, duration and date lives."
                ],
                "correctAnswer": 2,
                "explanation": "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed.",
                "rawAnswer": "If you can achieve the same result with logic, use logic. Apply constraints only when a contractual or physical date must be fixed."
            },
            {
                "id": "module-2-q81",
                "qNum": 81,
                "question": "What are activity codes?",
                "options": [
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands.",
                    "The bar chart on the right side; each bar is one activity's duration on a timeline.",
                    "The most-used type; duration is driven by the calendar and logic.",
                    "Labels attached to activities so you can organize, filter and group the schedule; tags on every activity."
                ],
                "correctAnswer": 3,
                "explanation": "Labels attached to activities so you can organize, filter and group the schedule; tags on every activity.",
                "rawAnswer": "Labels attached to activities so you can organize, filter and group the schedule; tags on every activity."
            },
            {
                "id": "module-2-q82",
                "qNum": 82,
                "question": "Name four code types and their uses.",
                "options": [
                    "Not Started, In Progress, and Complete.",
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "SPI = EV ÷ PV = 4,000 ÷ 5,000 = 0.80; CPI = EV ÷ AC = 4,000 ÷ 4,500 = 0.89. Greater than 1.0 is good, less than 1.0 is a problem, 1.0 is exactly on plan.",
                    "What comes before and after each activity."
                ],
                "correctAnswer": 1,
                "explanation": "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                "rawAnswer": "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority)."
            },
            {
                "id": "module-2-q83",
                "qNum": 83,
                "question": "What is the click path for activity codes?",
                "options": [
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                    "Special non-work days or extra work days not in the standard pattern.",
                    "Finish to Finish: B finishes when A finishes (punchlist and final inspection must both finish together)."
                ],
                "correctAnswer": 1,
                "explanation": "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                "rawAnswer": "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab."
            },
            {
                "id": "module-2-q84",
                "qNum": 84,
                "question": "What does Group & Sort do?",
                "options": [
                    "Reorganizes the view without changing the data, e.g., grouping by WBS → Discipline → Area.",
                    "Start to Finish: B cannot finish until A starts. Rarely used, reserved for very specific handover scenarios.",
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026."
                ],
                "correctAnswer": 0,
                "explanation": "Reorganizes the view without changing the data, e.g., grouping by WBS → Discipline → Area.",
                "rawAnswer": "Reorganizes the view without changing the data, e.g., grouping by WBS → Discipline → Area."
            },
            {
                "id": "module-2-q85",
                "qNum": 85,
                "question": "What is the click path for Group & Sort?",
                "options": [
                    "View → Group and Sort → Add grouping fields (WBS, Discipline code, Area code) → OK → F9.",
                    "Each drawer is a region, each folder is a program, and each document is a project.",
                    "Project → WBS → Add → Name each level → Repeat for all branches.",
                    "(1) Press F9 or Tools → Schedule, (2) confirm the Data Date, (3) click Schedule, (4) review the Schedule Log for warnings, (5) check that dates have calculated."
                ],
                "correctAnswer": 0,
                "explanation": "View → Group and Sort → Add grouping fields (WBS, Discipline code, Area code) → OK → F9.",
                "rawAnswer": "View → Group and Sort → Add grouping fields (WBS, Discipline code, Area code) → OK → F9."
            },
            {
                "id": "module-2-q86",
                "qNum": 86,
                "question": "What is a filter?",
                "options": [
                    "Workweek, Hours per Day, Holidays, and Exceptions.",
                    "Software that helps schedulers organize thousands of project activities, connect them, calculate dates, track progress and report status, all in one place.",
                    "View → Bars (critical in red, non-critical in blue); add a Total Float column (0 = critical); right-click → Trace Logic to highlight predecessor/successor chains.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted."
                ],
                "correctAnswer": 3,
                "explanation": "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                "rawAnswer": "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted."
            },
            {
                "id": "module-2-q87",
                "qNum": 87,
                "question": "Give filter logic for \"critical activities\" and \"ABC Electric's next 3 weeks.\"",
                "options": [
                    "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d.",
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\"",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "Clicking an activity shows its predecessors, successors, resources and codes below."
                ],
                "correctAnswer": 0,
                "explanation": "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d.",
                "rawAnswer": "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d."
            },
            {
                "id": "module-2-q88",
                "qNum": 88,
                "question": "Give filter logic for \"float ≤ 10 days\" and \"incomplete activities in Building A.\"",
                "options": [
                    "P6 can't show true float, activities that are behind may show positive float, the critical path may appear shorter than it is, and client reviewers flag over-constrained schedules as poor quality.",
                    "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026.",
                    "Total Float ≤ 10; Area Code = Building A AND % Complete < 100."
                ],
                "correctAnswer": 3,
                "explanation": "Total Float ≤ 10; Area Code = Building A AND % Complete < 100.",
                "rawAnswer": "Total Float ≤ 10; Area Code = Building A AND % Complete < 100."
            },
            {
                "id": "module-2-q89",
                "qNum": 89,
                "question": "What is a layout and what is the click path?",
                "options": [
                    "The schedule says A must finish before B starts, but the field started B early while A is still in progress. The question is what P6 does with B's remaining work.",
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout.",
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands.",
                    "The most-used type; duration is driven by the calendar and logic."
                ],
                "correctAnswer": 1,
                "explanation": "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout.",
                "rawAnswer": "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout."
            },
            {
                "id": "module-2-q90",
                "qNum": 90,
                "question": "Name the five sample layouts.",
                "options": [
                    "Zero-duration markers for key beginning (Notice to Proceed) or completion events (Commissioning Complete).",
                    "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout.",
                    "File → New → Project → Complete Wizard → Enter fields → Finish.",
                    "MSP is best for projects up to a few hundred activities; P6 handles tens of thousands."
                ],
                "correctAnswer": 1,
                "explanation": "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout.",
                "rawAnswer": "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout."
            },
            {
                "id": "module-2-q91",
                "qNum": 91,
                "question": "What is on the beginner schedule quality checklist?",
                "options": [
                    "P6 ignores the original logic for B's remaining work; B continues forward from the Data Date regardless of A's status.",
                    "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout.",
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix."
                ],
                "correctAnswer": 2,
                "explanation": "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                "rawAnswer": "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path."
            },
            {
                "id": "module-2-q92",
                "qNum": 92,
                "question": "What three red flags should you watch for?",
                "options": [
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "Admin → Activity Codes → Add Code Type → Add Code Values → Assign via Bottom Panel → Codes tab.",
                    "Negative float (project already behind), activities in the past without actuals, and excessive float (>100 days), usually missing logic.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted."
                ],
                "correctAnswer": 2,
                "explanation": "Negative float (project already behind), activities in the past without actuals, and excessive float (>100 days), usually missing logic.",
                "rawAnswer": "Negative float (project already behind), activities in the past without actuals, and excessive float (>100 days), usually missing logic."
            },
            {
                "id": "module-2-q93",
                "qNum": 93,
                "question": "List the P6 report types.",
                "options": [
                    "Update Layout, Critical Path Layout, 3-Week Lookahead, Executive Milestone Layout, and Electrical Layout.",
                    "Underground Utilities must finish before Foundation begins, but the crew started pouring foundation on the north side before utilities in that zone were complete, so Foundation shows In Progress with an unfinished predecessor.",
                    "SPI = EV ÷ PV = 4,000 ÷ 5,000 = 0.80; CPI = EV ÷ AC = 4,000 ÷ 4,500 = 0.89. Greater than 1.0 is good, less than 1.0 is a problem, 1.0 is exactly on plan.",
                    "2-/3-Week Lookahead, Critical Path Report, Milestone Report, Variance Report, and Schedule Narrative."
                ],
                "correctAnswer": 3,
                "explanation": "2-/3-Week Lookahead, Critical Path Report, Milestone Report, Variance Report, and Schedule Narrative.",
                "rawAnswer": "2-/3-Week Lookahead, Critical Path Report, Milestone Report, Variance Report, and Schedule Narrative."
            },
            {
                "id": "module-2-q94",
                "qNum": 94,
                "question": "Can P6 auto-generate the schedule narrative?",
                "options": [
                    "Organize (structured activities and work packages), Connect (link activities with logic), Calculate (press F9 for dates, float and critical path), and Track & Report (update progress, compare to baseline, communicate status).",
                    "Critical: Total Float = 0 (or less). ABC Electric: Contractor Code = ABC Electric AND Start ≤ Data Date + 21d.",
                    "No; the scheduler writes it. A scheduler's job is to communicate the schedule, not only maintain it.",
                    "Admin → Calendars → Add → Name → Set workweek → Set hours → Add holidays → Close."
                ],
                "correctAnswer": 2,
                "explanation": "No; the scheduler writes it. A scheduler's job is to communicate the schedule, not only maintain it.",
                "rawAnswer": "No; the scheduler writes it. A scheduler's job is to communicate the schedule, not only maintain it."
            },
            {
                "id": "module-2-q95",
                "qNum": 95,
                "question": "What are the six parts of the schedule narrative?",
                "options": [
                    "MSP is single-file, desktop-based with a familiar Office interface. P6 is an Oracle enterprise database supporting multiple users, advanced resource loading, EPS, baselines and codes.",
                    "Excavate → Foundation → Structure → Equipment.",
                    "Where is the project now? What changed this period? What is currently critical? What is delayed? What action is needed? What is the forecast finish?",
                    "Set the Data Date first, then update each activity based on field reports."
                ],
                "correctAnswer": 2,
                "explanation": "Where is the project now? What changed this period? What is currently critical? What is delayed? What action is needed? What is the forecast finish?",
                "rawAnswer": "Where is the project now? What changed this period? What is currently critical? What is delayed? What action is needed? What is the forecast finish?"
            },
            {
                "id": "module-2-q96",
                "qNum": 96,
                "question": "What separates the professional narrative from the beginner version?",
                "options": [
                    "Area (filter by physical location), Discipline (show an engineer only their scope), Contractor (pull a contractor's lookahead), Priority/Phase (show by phase or priority).",
                    "Clicking an activity shows its predecessors, successors, resources and codes below.",
                    "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\"",
                    "An activity whose duration spans the length of other activities, used for supervision, project management or inspection running continuously."
                ],
                "correctAnswer": 2,
                "explanation": "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\"",
                "rawAnswer": "The professional version references specific activities, dates, numbers, owners, actions and forecast (e.g., 8 calendar days behind baseline driven by Transformer Delivery NST-2010); the beginner one says \"some activities are behind, the project might be late.\""
            },
            {
                "id": "module-2-q97",
                "qNum": 97,
                "question": "What is the Room Renovation EVM example ($10,000 budget)?",
                "options": [
                    "Enterprise Project Structure; it organizes all projects in the company, like a filing cabinet.",
                    "PV = $5,000, EV = $4,000, AC = $4,500. SV = EV − PV = −$1,000 (behind). CV = EV − AC = −$500 (over budget).",
                    "Finish to Start: B cannot start until A finishes (Foundation cannot start until Excavation finishes). Most common.",
                    "Always press F9 after any change to logic, durations or constraints; never trust dates that haven't been recalculated."
                ],
                "correctAnswer": 1,
                "explanation": "PV = $5,000, EV = $4,000, AC = $4,500. SV = EV − PV = −$1,000 (behind). CV = EV − AC = −$500 (over budget).",
                "rawAnswer": "PV = $5,000, EV = $4,000, AC = $4,500. SV = EV − PV = −$1,000 (behind). CV = EV − AC = −$500 (over budget)."
            },
            {
                "id": "module-2-q98",
                "qNum": 98,
                "question": "What are SPI and CPI in the example, and how do you read them?",
                "options": [
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                    "SPI = EV ÷ PV = 4,000 ÷ 5,000 = 0.80; CPI = EV ÷ AC = 4,000 ÷ 4,500 = 0.89. Greater than 1.0 is good, less than 1.0 is a problem, 1.0 is exactly on plan.",
                    "The main working area where every task, duration and date lives.",
                    "A frozen copy of the schedule at a specific point in time, usually when the plan is approved, against which all future progress is measured."
                ],
                "correctAnswer": 1,
                "explanation": "SPI = EV ÷ PV = 4,000 ÷ 5,000 = 0.80; CPI = EV ÷ AC = 4,000 ÷ 4,500 = 0.89. Greater than 1.0 is good, less than 1.0 is a problem, 1.0 is exactly on plan.",
                "rawAnswer": "SPI = EV ÷ PV = 4,000 ÷ 5,000 = 0.80; CPI = EV ÷ AC = 4,000 ÷ 4,500 = 0.89. Greater than 1.0 is good, less than 1.0 is a problem, 1.0 is exactly on plan."
            },
            {
                "id": "module-2-q99",
                "qNum": 99,
                "question": "What are the five steps of the complete P6 workflow?",
                "options": [
                    "(1) Create project in EPS, (2) build WBS + calendar, (3) add activities + logic, (4) F9 → review → quality check, (5) baseline → set Data Date → update → report.",
                    "The field team performed work in a different order than the schedule planned. It requires investigation, not just a data entry fix.",
                    "A question you ask P6; matching activities stay visible and the rest are temporarily hidden, not deleted.",
                    "A saved combination of columns, grouping, filters and Gantt bar settings. View → Layout → Save Layout As → Name → OK; switch via View → Layout → Open Layout."
                ],
                "correctAnswer": 0,
                "explanation": "(1) Create project in EPS, (2) build WBS + calendar, (3) add activities + logic, (4) F9 → review → quality check, (5) baseline → set Data Date → update → report.",
                "rawAnswer": "(1) Create project in EPS, (2) build WBS + calendar, (3) add activities + logic, (4) F9 → review → quality check, (5) baseline → set Data Date → update → report."
            },
            {
                "id": "module-2-q100",
                "qNum": 100,
                "question": "What are the key requirements of the Module 2 practical exercise?",
                "options": [
                    "All activities connected (no open ends), no unreasonable durations (1 or 500 days), not too many constraints, correct calendar, correct Data Date, and a logical critical path.",
                    "Actual Start + Actual Finish, with remaining duration set to zero. Example: NST-1010 started 05-Jan, finished 23-Jan-2026.",
                    "An override that forces P6 to respect a fixed date regardless of logic. Logic should drive dates; use constraints sparingly.",
                    "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours."
                ],
                "correctAnswer": 3,
                "explanation": "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours.",
                "rawAnswer": "Correct EPS node, 7-level WBS, NST 5-Day/8-Hr calendar, 20–25 activities including ≥2 milestones, all logic connected, F9, critical path, Baseline Rev 0, Data Date with progress on 3 activities; plus one out-of-sequence example, codes (Area + Discipline + Contractor), Critical Path and 3-Week Lookahead filters, Group & Sort, 2 layouts, and a one-page narrative. Time: 2–3 hours."
            }
        ]
    },
    {
        "id": "module-3",
        "aliases": [
            "resources",
            "cost"
        ],
        "title": "Module 3: Resource Loading & Cost Control",
        "description": "Labor hours calculations, crew sizing, productivity rates, histograms, overallocation, smoothing vs leveling, cost loading, and S-curves.",
        "project": "LGL Northstar Data Center Capital Project",
        "iconName": "HardHat",
        "color": "#F59E0B",
        "questions": [
            {
                "id": "module-3-q1",
                "qNum": 1,
                "question": "What questions does a time-only schedule leave unanswered?",
                "options": [
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000.",
                    "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d.",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost."
                ],
                "correctAnswer": 3,
                "explanation": "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost.",
                "rawAnswer": "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost."
            },
            {
                "id": "module-3-q2",
                "qNum": 2,
                "question": "What does a time-only schedule tell us about \"Install Cable Tray: Data Hall\"?",
                "options": [
                    "Only the activity and its 10-day duration. No people, no cost, no availability check.",
                    "Work was moved within float; the project finish date did NOT change.",
                    "Using fewer labor hours to produce the same result.",
                    "500 × 0.10 = 50 LH (using the rate table)."
                ],
                "correctAnswer": 0,
                "explanation": "Only the activity and its 10-day duration. No people, no cost, no availability check.",
                "rawAnswer": "Only the activity and its 10-day duration. No people, no cost, no availability check."
            },
            {
                "id": "module-3-q3",
                "qNum": 3,
                "question": "What happens when no resources are loaded?",
                "options": [
                    "It shows which days the resource works, accounting for weekends, holidays and shift patterns.",
                    "There is no cost information.",
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                    "Using fewer labor hours to produce the same result."
                ],
                "correctAnswer": 1,
                "explanation": "There is no cost information.",
                "rawAnswer": "There is no cost information."
            },
            {
                "id": "module-3-q4",
                "qNum": 4,
                "question": "What does resource loading turn the schedule into?",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K.",
                    "A management tool, not just a list of dates.",
                    "Smoothing works within float; leveling may exceed float."
                ],
                "correctAnswer": 2,
                "explanation": "A management tool, not just a list of dates.",
                "rawAnswer": "A management tool, not just a list of dates."
            },
            {
                "id": "module-3-q5",
                "qNum": 5,
                "question": "What is the four-step progression shown for resource loading?",
                "options": [
                    "View → Resource Graph or Resource Usage to see demand vs. availability over time.",
                    "Enterprise → Resources → Add New (the + button).",
                    "Time-Only → Add Resources → Check Availability → Calculate Cost.",
                    "It drives quantity installed and duration validation."
                ],
                "correctAnswer": 2,
                "explanation": "Time-Only → Add Resources → Check Availability → Calculate Cost.",
                "rawAnswer": "Time-Only → Add Resources → Check Availability → Calculate Cost."
            },
            {
                "id": "module-3-q6",
                "qNum": 6,
                "question": "What is a resource?",
                "options": [
                    "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room.",
                    "A management tool, not just a list of dates.",
                    "2.5 days.",
                    "Anything needed to perform the work."
                ],
                "correctAnswer": 3,
                "explanation": "Anything needed to perform the work.",
                "rawAnswer": "Anything needed to perform the work."
            },
            {
                "id": "module-3-q7",
                "qNum": 7,
                "question": "Give the moving-house analogy for resources.",
                "options": [
                    "Labor, Equipment/Nonlabor, and Material.",
                    "6 × 10 × 3 = 180 LH.",
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                    "Enterprise → Resources → Add New (the + button)."
                ],
                "correctAnswer": 2,
                "explanation": "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                "rawAnswer": "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material)."
            },
            {
                "id": "module-3-q8",
                "qNum": 8,
                "question": "Give the construction equivalents of those three.",
                "options": [
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%.",
                    "8 × 10 × 10 = 800 LH.",
                    "Electricians (labor), a crane (equipment), cable (material).",
                    "Material is consumed; once the concrete is poured it is gone."
                ],
                "correctAnswer": 2,
                "explanation": "Electricians (labor), a crane (equipment), cable (material).",
                "rawAnswer": "Electricians (labor), a crane (equipment), cable (material)."
            },
            {
                "id": "module-3-q9",
                "qNum": 9,
                "question": "What are the three resource types?",
                "options": [
                    "Labor, Equipment/Nonlabor, and Material.",
                    "Smoothing works within float; leveling may exceed float.",
                    "View → Resource Usage Profile; red bars = overallocation.",
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material)."
                ],
                "correctAnswer": 0,
                "explanation": "Labor, Equipment/Nonlabor, and Material.",
                "rawAnswer": "Labor, Equipment/Nonlabor, and Material."
            },
            {
                "id": "module-3-q10",
                "qNum": 10,
                "question": "How is labor measured and costed?",
                "options": [
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "$2,000 + $800 = $2,800.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000.",
                    "Labor + Equipment + Material + Expense."
                ],
                "correctAnswer": 0,
                "explanation": "Measured in hours worked; cost = hourly rate × hours worked.",
                "rawAnswer": "Measured in hours worked; cost = hourly rate × hours worked."
            },
            {
                "id": "module-3-q11",
                "qNum": 11,
                "question": "How is equipment measured and costed?",
                "options": [
                    "There is no cost information.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used.",
                    "500 × 0.10 = 50 LH (using the rate table).",
                    "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day)."
                ],
                "correctAnswer": 1,
                "explanation": "Measured in hours or days of use; cost = hourly or daily rate × duration used.",
                "rawAnswer": "Measured in hours or days of use; cost = hourly or daily rate × duration used."
            },
            {
                "id": "module-3-q12",
                "qNum": 12,
                "question": "How is material measured and costed?",
                "options": [
                    "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost.",
                    "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price.",
                    "$17,000.",
                    "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent."
                ],
                "correctAnswer": 1,
                "explanation": "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price.",
                "rawAnswer": "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price."
            },
            {
                "id": "module-3-q13",
                "qNum": 13,
                "question": "What is the labor hours formula?",
                "options": [
                    "Journeyman Electrician $85/hr; Foreman Electrician $95/hr (1 per 5–8 workers); Concrete Finisher $72/hr; Ironworker/Rigger $92/hr.",
                    "Labor Hours × Hourly Rate.",
                    "2 × 8 × 15 = 240 LH.",
                    "Labor Hours = Crew Size × Hours per Day × Duration."
                ],
                "correctAnswer": 3,
                "explanation": "Labor Hours = Crew Size × Hours per Day × Duration.",
                "rawAnswer": "Labor Hours = Crew Size × Hours per Day × Duration."
            },
            {
                "id": "module-3-q14",
                "qNum": 14,
                "question": "How many labor hours do 4 workers produce in one 8-hour day?",
                "options": [
                    "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500.",
                    "4 × 8 = 32 LH.",
                    "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other.",
                    "The approved amount of money to complete the project, allocated down through the WBS to individual activities."
                ],
                "correctAnswer": 1,
                "explanation": "4 × 8 = 32 LH.",
                "rawAnswer": "4 × 8 = 32 LH."
            },
            {
                "id": "module-3-q15",
                "qNum": 15,
                "question": "How many labor hours do 4 workers produce over 5 days at 8 hrs/day?",
                "options": [
                    "4 × 8 × 5 = 160 LH.",
                    "Enterprise → Resources → Add New (the + button).",
                    "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total)."
                ],
                "correctAnswer": 0,
                "explanation": "4 × 8 × 5 = 160 LH.",
                "rawAnswer": "4 × 8 × 5 = 160 LH."
            },
            {
                "id": "module-3-q16",
                "qNum": 16,
                "question": "5 electricians × 10 hours/day × 4 days = ?",
                "options": [
                    "200 LH.",
                    "$68,000 + $11,000 = $79,000.",
                    "$45M total capital budget; Electrical Systems $18M (largest WBS element); 120+ activities, each with its own budget.",
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material)."
                ],
                "correctAnswer": 0,
                "explanation": "200 LH.",
                "rawAnswer": "200 LH."
            },
            {
                "id": "module-3-q17",
                "qNum": 17,
                "question": "3 ironworkers, 8 hours/day, 5 days = ?",
                "options": [
                    "3 × 8 × 5 = 120 LH.",
                    "$17,000.",
                    "Enterprise → Resources → Add New (the + button).",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
                ],
                "correctAnswer": 0,
                "explanation": "3 × 8 × 5 = 120 LH.",
                "rawAnswer": "3 × 8 × 5 = 120 LH."
            },
            {
                "id": "module-3-q18",
                "qNum": 18,
                "question": "6 concrete finishers, 10 hours/day, 3 days = ?",
                "options": [
                    "Higher.",
                    "Time-Only → Add Resources → Check Availability → Calculate Cost.",
                    "Always try smoothing first. Only proceed to leveling if overallocations can't be resolved within existing float.",
                    "6 × 10 × 3 = 180 LH."
                ],
                "correctAnswer": 3,
                "explanation": "6 × 10 × 3 = 180 LH.",
                "rawAnswer": "6 × 10 × 3 = 180 LH."
            },
            {
                "id": "module-3-q19",
                "qNum": 19,
                "question": "2 foremen, 8 hours/day, 15 days = ?",
                "options": [
                    "Anything needed to perform the work.",
                    "2 × 8 × 15 = 240 LH.",
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900."
                ],
                "correctAnswer": 1,
                "explanation": "2 × 8 × 15 = 240 LH.",
                "rawAnswer": "2 × 8 × 15 = 240 LH."
            },
            {
                "id": "module-3-q20",
                "qNum": 20,
                "question": "What are the labor hours for Install Cable Tray at Northstar (8 electricians, 10 hrs/day, 10 days)?",
                "options": [
                    "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500.",
                    "Material is consumed; once the concrete is poured it is gone.",
                    "Shifting work within its available float to remove peaks, without changing the project finish date.",
                    "8 × 10 × 10 = 800 LH."
                ],
                "correctAnswer": 3,
                "explanation": "8 × 10 × 10 = 800 LH.",
                "rawAnswer": "8 × 10 × 10 = 800 LH."
            },
            {
                "id": "module-3-q21",
                "qNum": 21,
                "question": "For 100 labor hours of work, what duration do 2 electricians at 10 hrs/day need?",
                "options": [
                    "Report the impact to the project manager.",
                    "4 × 8 × 5 = 160 LH.",
                    "5 days.",
                    "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost."
                ],
                "correctAnswer": 2,
                "explanation": "5 days.",
                "rawAnswer": "5 days."
            },
            {
                "id": "module-3-q22",
                "qNum": 22,
                "question": "For 100 labor hours, what duration do 4 electricians at 10 hrs/day need?",
                "options": [
                    "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500.",
                    "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost.",
                    "2.5 days.",
                    "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room."
                ],
                "correctAnswer": 2,
                "explanation": "2.5 days.",
                "rawAnswer": "2.5 days."
            },
            {
                "id": "module-3-q23",
                "qNum": 23,
                "question": "For 100 labor hours, what duration do 5 electricians at 10 hrs/day need?",
                "options": [
                    "View → Resource Sheet.",
                    "It shows which days the resource works, accounting for weekends, holidays and shift patterns.",
                    "2 days.",
                    "Labor, Nonlabor (equipment) and Material; each calculates cost differently."
                ],
                "correctAnswer": 2,
                "explanation": "2 days.",
                "rawAnswer": "2 days."
            },
            {
                "id": "module-3-q24",
                "qNum": 24,
                "question": "For 100 labor hours, what duration do 10 electricians at 10 hrs/day need?",
                "options": [
                    "View → Resource Usage Profile; red bars = overallocation.",
                    "4 × 8 × 5 = 160 LH.",
                    "1 day.",
                    "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval."
                ],
                "correctAnswer": 2,
                "explanation": "1 day.",
                "rawAnswer": "1 day."
            },
            {
                "id": "module-3-q25",
                "qNum": 25,
                "question": "What is the general relationship between crew size and duration?",
                "options": [
                    "Smoothing: no change to finish, low risk. Leveling: possibly changes finish, higher risk and needs approval.",
                    "A management tool, not just a list of dates.",
                    "A crowded highway: shift some cars to off-peak hours without changing total travel time.",
                    "More people can compress duration, especially when workers don't get in each other's way."
                ],
                "correctAnswer": 3,
                "explanation": "More people can compress duration, especially when workers don't get in each other's way.",
                "rawAnswer": "More people can compress duration, especially when workers don't get in each other's way."
            },
            {
                "id": "module-3-q26",
                "qNum": 26,
                "question": "What limits adding people to shorten duration?",
                "options": [
                    "Output Rate (Quantity ÷ Labor Hours) and Labor Hours per Unit (Labor Hours ÷ Quantity).",
                    "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "6 concrete finishers × 10 hrs × 3 days = 180 LH."
                ],
                "correctAnswer": 1,
                "explanation": "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room.",
                "rawAnswer": "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room."
            },
            {
                "id": "module-3-q27",
                "qNum": 27,
                "question": "What is the key rule about adding crew?",
                "options": [
                    "Labor Hours = Crew Size × Hours per Day × Duration.",
                    "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other.",
                    "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
                ],
                "correctAnswer": 1,
                "explanation": "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other.",
                "rawAnswer": "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other."
            },
            {
                "id": "module-3-q28",
                "qNum": 28,
                "question": "If the labor hours are known, how do you back into duration?",
                "options": [
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "Labor + Equipment + Material + Expense."
                ],
                "correctAnswer": 1,
                "explanation": "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                "rawAnswer": "Duration = Labor Hours ÷ (Crew Size × Hours per Day)."
            },
            {
                "id": "module-3-q29",
                "qNum": 29,
                "question": "Why must labor-hour calculation be consistent?",
                "options": [
                    "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                    "It's how all labor hours are calculated, every time, on every project.",
                    "Open the activity → Resources tab → Assign → select the resource.",
                    "Using fewer labor hours to produce the same result."
                ],
                "correctAnswer": 1,
                "explanation": "It's how all labor hours are calculated, every time, on every project.",
                "rawAnswer": "It's how all labor hours are calculated, every time, on every project."
            },
            {
                "id": "module-3-q30",
                "qNum": 30,
                "question": "What crew is used to check labor hours for the \"Form & Pour Generator Pad\" activity?",
                "options": [
                    "6 concrete finishers × 10 hrs × 3 days = 180 LH.",
                    "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K.",
                    "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used."
                ],
                "correctAnswer": 0,
                "explanation": "6 concrete finishers × 10 hrs × 3 days = 180 LH.",
                "rawAnswer": "6 concrete finishers × 10 hrs × 3 days = 180 LH."
            },
            {
                "id": "module-3-q31",
                "qNum": 31,
                "question": "In the Crew A/Crew B story, which crew is more productive and why?",
                "options": [
                    "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result.",
                    "4 × 8 = 32 LH.",
                    "Work was moved within float; the project finish date did NOT change.",
                    "Labor, Nonlabor (equipment) and Material; each calculates cost differently."
                ],
                "correctAnswer": 0,
                "explanation": "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result.",
                "rawAnswer": "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result."
            },
            {
                "id": "module-3-q32",
                "qNum": 32,
                "question": "What is the definition of higher productivity?",
                "options": [
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "The 5-day/10-hour Northstar site calendar.",
                    "It's how all labor hours are calculated, every time, on every project.",
                    "Using fewer labor hours to produce the same result."
                ],
                "correctAnswer": 3,
                "explanation": "Using fewer labor hours to produce the same result.",
                "rawAnswer": "Using fewer labor hours to produce the same result."
            },
            {
                "id": "module-3-q33",
                "qNum": 33,
                "question": "What are the two ways to express productivity?",
                "options": [
                    "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room.",
                    "Output Rate (Quantity ÷ Labor Hours) and Labor Hours per Unit (Labor Hours ÷ Quantity).",
                    "$68,000 + $11,000 = $79,000.",
                    "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day)."
                ],
                "correctAnswer": 1,
                "explanation": "Output Rate (Quantity ÷ Labor Hours) and Labor Hours per Unit (Labor Hours ÷ Quantity).",
                "rawAnswer": "Output Rate (Quantity ÷ Labor Hours) and Labor Hours per Unit (Labor Hours ÷ Quantity)."
            },
            {
                "id": "module-3-q34",
                "qNum": 34,
                "question": "For Output Rate, is a higher or lower number more productive?",
                "options": [
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "Higher.",
                    "$17,000.",
                    "A crowded highway: shift some cars to off-peak hours without changing total travel time."
                ],
                "correctAnswer": 1,
                "explanation": "Higher.",
                "rawAnswer": "Higher."
            },
            {
                "id": "module-3-q35",
                "qNum": 35,
                "question": "For Labor Hours per Unit, is a higher or lower number more productive?",
                "options": [
                    "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other.",
                    "Lower (fewer hours per unit).",
                    "5 days.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total)."
                ],
                "correctAnswer": 1,
                "explanation": "Lower (fewer hours per unit).",
                "rawAnswer": "Lower (fewer hours per unit)."
            },
            {
                "id": "module-3-q36",
                "qNum": 36,
                "question": "What are the sample output rates?",
                "options": [
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval.",
                    "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH.",
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%."
                ],
                "correctAnswer": 2,
                "explanation": "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH.",
                "rawAnswer": "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH."
            },
            {
                "id": "module-3-q37",
                "qNum": 37,
                "question": "What are the equivalent labor hours per unit?",
                "options": [
                    "A management tool, not just a list of dates.",
                    "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day).",
                    "Assigning a budgeted cost to each activity so the schedule knows not just when work happens but how much it costs.",
                    "Cable 0.10 LH/LF, Conduit 0.125 LH/LF, Drywall 0.025 LH/SF, Concrete 0.67 LH/CY."
                ],
                "correctAnswer": 3,
                "explanation": "Cable 0.10 LH/LF, Conduit 0.125 LH/LF, Drywall 0.025 LH/SF, Concrete 0.67 LH/CY.",
                "rawAnswer": "Cable 0.10 LH/LF, Conduit 0.125 LH/LF, Drywall 0.025 LH/SF, Concrete 0.67 LH/CY."
            },
            {
                "id": "module-3-q38",
                "qNum": 38,
                "question": "How do you convert an output rate into LH per unit?",
                "options": [
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "Week 3 requires 10 electricians but only 8 are available.",
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total)."
                ],
                "correctAnswer": 2,
                "explanation": "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                "rawAnswer": "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF."
            },
            {
                "id": "module-3-q39",
                "qNum": 39,
                "question": "How many labor hours does 500 LF of cable require at 0.10 LH/LF?",
                "options": [
                    "It's how all labor hours are calculated, every time, on every project.",
                    "3 × 8 × 5 = 120 LH.",
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "500 × 0.10 = 50 LH (using the rate table)."
                ],
                "correctAnswer": 3,
                "explanation": "500 × 0.10 = 50 LH (using the rate table).",
                "rawAnswer": "500 × 0.10 = 50 LH (using the rate table)."
            },
            {
                "id": "module-3-q40",
                "qNum": 40,
                "question": "How would productivity be used in the summary chain?",
                "options": [
                    "5 days.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "It drives quantity installed and duration validation.",
                    "Enterprise → Resources → Add New (the + button)."
                ],
                "correctAnswer": 2,
                "explanation": "It drives quantity installed and duration validation.",
                "rawAnswer": "It drives quantity installed and duration validation."
            },
            {
                "id": "module-3-q41",
                "qNum": 41,
                "question": "Why isn't having a schedule activity enough?",
                "options": [
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                    "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result.",
                    "You must check whether the resources actually exist to perform the work (e.g., we need 10 electricians but only have 6).",
                    "Install Cable Tray (10d) $79,000; Pour Generator Pad (3d) $32,900; Set UPS Modules (5d) $48,500."
                ],
                "correctAnswer": 2,
                "explanation": "You must check whether the resources actually exist to perform the work (e.g., we need 10 electricians but only have 6).",
                "rawAnswer": "You must check whether the resources actually exist to perform the work (e.g., we need 10 electricians but only have 6)."
            },
            {
                "id": "module-3-q42",
                "qNum": 42,
                "question": "Define Maximum Availability.",
                "options": [
                    "The Resource Sheet highlights the resource name in red and the Resource Graph shows the overloaded period above the limit line.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price.",
                    "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost."
                ],
                "correctAnswer": 1,
                "explanation": "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                "rawAnswer": "The most units of a resource available at any time (e.g., company has 8 electricians total)."
            },
            {
                "id": "module-3-q43",
                "qNum": 43,
                "question": "What is a Resource Calendar?",
                "options": [
                    "4 × 8 = 32 LH.",
                    "It shows which days the resource works, accounting for weekends, holidays and shift patterns.",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500."
                ],
                "correctAnswer": 1,
                "explanation": "It shows which days the resource works, accounting for weekends, holidays and shift patterns.",
                "rawAnswer": "It shows which days the resource works, accounting for weekends, holidays and shift patterns."
            },
            {
                "id": "module-3-q44",
                "qNum": 44,
                "question": "What does Units/Time mean?",
                "options": [
                    "Labor, Equipment/Nonlabor, and Material.",
                    "Open the activity → Resources tab → Assign → select the resource.",
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "How many units are assigned per day; e.g., 4 electricians = 400% if 1 unit = 100%."
                ],
                "correctAnswer": 3,
                "explanation": "How many units are assigned per day; e.g., 4 electricians = 400% if 1 unit = 100%.",
                "rawAnswer": "How many units are assigned per day; e.g., 4 electricians = 400% if 1 unit = 100%."
            },
            {
                "id": "module-3-q45",
                "qNum": 45,
                "question": "What is a resource histogram?",
                "options": [
                    "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day).",
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d.",
                    "$26,000; $13,200; $60,000."
                ],
                "correctAnswer": 0,
                "explanation": "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day).",
                "rawAnswer": "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day)."
            },
            {
                "id": "module-3-q46",
                "qNum": 46,
                "question": "In the histogram example, what does each week require?",
                "options": [
                    "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result.",
                    "How many units are assigned per day; e.g., 4 electricians = 400% if 1 unit = 100%.",
                    "Higher.",
                    "Week 1: 4 electricians; Week 2: 6; Week 3: 10; Week 4: 5."
                ],
                "correctAnswer": 3,
                "explanation": "Week 1: 4 electricians; Week 2: 6; Week 3: 10; Week 4: 5.",
                "rawAnswer": "Week 1: 4 electricians; Week 2: 6; Week 3: 10; Week 4: 5."
            },
            {
                "id": "module-3-q47",
                "qNum": 47,
                "question": "What is the problem in that histogram?",
                "options": [
                    "Week 3 requires 10 electricians but only 8 are available.",
                    "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500.",
                    "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost.",
                    "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost."
                ],
                "correctAnswer": 0,
                "explanation": "Week 3 requires 10 electricians but only 8 are available.",
                "rawAnswer": "Week 3 requires 10 electricians but only 8 are available."
            },
            {
                "id": "module-3-q48",
                "qNum": 48,
                "question": "What is overallocation?",
                "options": [
                    "Work was moved within float; the project finish date did NOT change.",
                    "The schedule requires more resources than are actually available during the same time period.",
                    "Smoothing: no change to finish, low risk. Leveling: possibly changes finish, higher risk and needs approval.",
                    "More people can compress duration, especially when workers don't get in each other's way."
                ],
                "correctAnswer": 1,
                "explanation": "The schedule requires more resources than are actually available during the same time period.",
                "rawAnswer": "The schedule requires more resources than are actually available during the same time period."
            },
            {
                "id": "module-3-q49",
                "qNum": 49,
                "question": "What is the overallocation gap in the example?",
                "options": [
                    "Labor, Equipment/Nonlabor, and Material.",
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "Time-Only → Add Resources → Check Availability → Calculate Cost.",
                    "Week 3 requires 10 electricians but only 8 are available."
                ],
                "correctAnswer": 1,
                "explanation": "10 required − 8 available = −2 (overallocated by 2).",
                "rawAnswer": "10 required − 8 available = −2 (overallocated by 2)."
            },
            {
                "id": "module-3-q50",
                "qNum": 50,
                "question": "How does P6 flag overallocation?",
                "options": [
                    "5 days.",
                    "The Resource Usage Profile shows bars exceeding the availability line in red, and the resource name appears in red in the resource dictionary.",
                    "The budget spread across the timeline by when activities are scheduled (the spending profile): Jan $100K, Feb $150K, Mar $250K, Apr $300K, May $200K = $1,000K.",
                    "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH."
                ],
                "correctAnswer": 1,
                "explanation": "The Resource Usage Profile shows bars exceeding the availability line in red, and the resource name appears in red in the resource dictionary.",
                "rawAnswer": "The Resource Usage Profile shows bars exceeding the availability line in red, and the resource name appears in red in the resource dictionary."
            },
            {
                "id": "module-3-q51",
                "qNum": 51,
                "question": "How does Microsoft Project flag overallocation?",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "The Resource Sheet highlights the resource name in red and the Resource Graph shows the overloaded period above the limit line."
                ],
                "correctAnswer": 3,
                "explanation": "The Resource Sheet highlights the resource name in red and the Resource Graph shows the overloaded period above the limit line.",
                "rawAnswer": "The Resource Sheet highlights the resource name in red and the Resource Graph shows the overloaded period above the limit line."
            },
            {
                "id": "module-3-q52",
                "qNum": 52,
                "question": "Why must overallocation be resolved?",
                "options": [
                    "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval.",
                    "4 × 8 × 5 = 160 LH.",
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "6 × 10 × 3 = 180 LH."
                ],
                "correctAnswer": 0,
                "explanation": "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval.",
                "rawAnswer": "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval."
            },
            {
                "id": "module-3-q53",
                "qNum": 53,
                "question": "What is resource smoothing?",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "Blue: demand. Red: overallocation. Horizontal line: availability limit. Stepped curve: cumulative usage to date.",
                    "Shifting work within its available float to remove peaks, without changing the project finish date.",
                    "Adding people beyond a practical limit actually reduces productivity, because too many workers interfere with each other."
                ],
                "correctAnswer": 2,
                "explanation": "Shifting work within its available float to remove peaks, without changing the project finish date.",
                "rawAnswer": "Shifting work within its available float to remove peaks, without changing the project finish date."
            },
            {
                "id": "module-3-q54",
                "qNum": 54,
                "question": "What analogy is used for smoothing?",
                "options": [
                    "View → Resource Sheet.",
                    "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                    "A crowded highway: shift some cars to off-peak hours without changing total travel time.",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400."
                ],
                "correctAnswer": 2,
                "explanation": "A crowded highway: shift some cars to off-peak hours without changing total travel time.",
                "rawAnswer": "A crowded highway: shift some cars to off-peak hours without changing total travel time."
            },
            {
                "id": "module-3-q55",
                "qNum": 55,
                "question": "What did the smoothing example change?",
                "options": [
                    "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "Before: 4, 6, 10 (OVER), 5. After: 6, 7, 8, 6, all within the 8 available.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
                ],
                "correctAnswer": 2,
                "explanation": "Before: 4, 6, 10 (OVER), 5. After: 6, 7, 8, 6, all within the 8 available.",
                "rawAnswer": "Before: 4, 6, 10 (OVER), 5. After: 6, 7, 8, 6, all within the 8 available."
            },
            {
                "id": "module-3-q56",
                "qNum": 56,
                "question": "Did the total demand change in the example, and what did the finish date do?",
                "options": [
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "Week 3 requires 10 electricians but only 8 are available.",
                    "Work was moved within float; the project finish date did NOT change."
                ],
                "correctAnswer": 3,
                "explanation": "Work was moved within float; the project finish date did NOT change.",
                "rawAnswer": "Work was moved within float; the project finish date did NOT change."
            },
            {
                "id": "module-3-q57",
                "qNum": 57,
                "question": "What is resource leveling?",
                "options": [
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent.",
                    "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used."
                ],
                "correctAnswer": 2,
                "explanation": "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                "rawAnswer": "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date."
            },
            {
                "id": "module-3-q58",
                "qNum": 58,
                "question": "Compare smoothing and leveling on float use.",
                "options": [
                    "Report the impact to the project manager.",
                    "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                    "Electricians (labor), a crane (equipment), cable (material).",
                    "Smoothing works within float; leveling may exceed float."
                ],
                "correctAnswer": 3,
                "explanation": "Smoothing works within float; leveling may exceed float.",
                "rawAnswer": "Smoothing works within float; leveling may exceed float."
            },
            {
                "id": "module-3-q59",
                "qNum": 59,
                "question": "Compare them on finish date and project risk.",
                "options": [
                    "Smoothing: no change to finish, low risk. Leveling: possibly changes finish, higher risk and needs approval.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "View → Resource Graph or Resource Usage to see demand vs. availability over time."
                ],
                "correctAnswer": 0,
                "explanation": "Smoothing: no change to finish, low risk. Leveling: possibly changes finish, higher risk and needs approval.",
                "rawAnswer": "Smoothing: no change to finish, low risk. Leveling: possibly changes finish, higher risk and needs approval."
            },
            {
                "id": "module-3-q60",
                "qNum": 60,
                "question": "When do you use each?",
                "options": [
                    "Smoothing for small overallocations with available float; leveling for severe overallocation with no float remaining.",
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "4 × 8 × 5 = 160 LH.",
                    "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent."
                ],
                "correctAnswer": 0,
                "explanation": "Smoothing for small overallocations with available float; leveling for severe overallocation with no float remaining.",
                "rawAnswer": "Smoothing for small overallocations with available float; leveling for severe overallocation with no float remaining."
            },
            {
                "id": "module-3-q61",
                "qNum": 61,
                "question": "What is the rule of order?",
                "options": [
                    "Always try smoothing first. Only proceed to leveling if overallocations can't be resolved within existing float.",
                    "Higher.",
                    "A crowded highway: shift some cars to off-peak hours without changing total travel time.",
                    "$17,000."
                ],
                "correctAnswer": 0,
                "explanation": "Always try smoothing first. Only proceed to leveling if overallocations can't be resolved within existing float.",
                "rawAnswer": "Always try smoothing first. Only proceed to leveling if overallocations can't be resolved within existing float."
            },
            {
                "id": "module-3-q62",
                "qNum": 62,
                "question": "What must you always do when leveling?",
                "options": [
                    "Report the impact to the project manager.",
                    "Labor Hours = Crew Size × Hours per Day × Duration.",
                    "3 × 8 × 5 = 120 LH.",
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF."
                ],
                "correctAnswer": 0,
                "explanation": "Report the impact to the project manager.",
                "rawAnswer": "Report the impact to the project manager."
            },
            {
                "id": "module-3-q63",
                "qNum": 63,
                "question": "Where are resources defined in MSP?",
                "options": [
                    "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                    "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "View → Resource Sheet."
                ],
                "correctAnswer": 3,
                "explanation": "View → Resource Sheet.",
                "rawAnswer": "View → Resource Sheet."
            },
            {
                "id": "module-3-q64",
                "qNum": 64,
                "question": "What resource types does MSP offer?",
                "options": [
                    "Labor, Equipment/Nonlabor, and Material.",
                    "Work (labor), Material (consumable) and Cost (fixed expense). Electricians = Work.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used.",
                    "A crowded highway: shift some cars to off-peak hours without changing total travel time."
                ],
                "correctAnswer": 1,
                "explanation": "Work (labor), Material (consumable) and Cost (fixed expense). Electricians = Work.",
                "rawAnswer": "Work (labor), Material (consumable) and Cost (fixed expense). Electricians = Work."
            },
            {
                "id": "module-3-q65",
                "qNum": 65,
                "question": "What does Max Units mean in MSP, and what is the value for 8 electricians?",
                "options": [
                    "Open the activity → Resources tab → Assign → select the resource.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "Anything needed to perform the work.",
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%."
                ],
                "correctAnswer": 3,
                "explanation": "The maximum number available at one time, expressed in %. 8 electricians = 800%.",
                "rawAnswer": "The maximum number available at one time, expressed in %. 8 electricians = 800%."
            },
            {
                "id": "module-3-q66",
                "qNum": 66,
                "question": "How does MSP compute cost?",
                "options": [
                    "Take the reciprocal: e.g., 10 LF/LH → 1 ÷ 10 = 0.10 LH/LF.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "4 × 8 × 5 = 160 LH.",
                    "3 × 8 × 5 = 120 LH."
                ],
                "correctAnswer": 1,
                "explanation": "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                "rawAnswer": "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign."
            },
            {
                "id": "module-3-q67",
                "qNum": 67,
                "question": "How do you assign a resource to an activity in MSP?",
                "options": [
                    "Select the activity in the Gantt view, double-click → Resources tab → choose the resource → set units.",
                    "2 days.",
                    "Measured in hours or days of use; cost = hourly or daily rate × duration used.",
                    "Work was moved within float; the project finish date did NOT change."
                ],
                "correctAnswer": 0,
                "explanation": "Select the activity in the Gantt view, double-click → Resources tab → choose the resource → set units.",
                "rawAnswer": "Select the activity in the Gantt view, double-click → Resources tab → choose the resource → set units."
            },
            {
                "id": "module-3-q68",
                "qNum": 68,
                "question": "How do you review workload in MSP?",
                "options": [
                    "View → Resource Graph or Resource Usage to see demand vs. availability over time.",
                    "Space, logistics and coordination. For example, you can't fit 50 electricians into a small equipment room.",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "$26,000; $13,200; $60,000."
                ],
                "correctAnswer": 0,
                "explanation": "View → Resource Graph or Resource Usage to see demand vs. availability over time.",
                "rawAnswer": "View → Resource Graph or Resource Usage to see demand vs. availability over time."
            },
            {
                "id": "module-3-q69",
                "qNum": 69,
                "question": "Where do you create a resource in P6?",
                "options": [
                    "The planned labor hours; e.g., Install Cable Tray = 800 Budgeted Labor Units.",
                    "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                    "Enterprise → Resources → Add New (the + button).",
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%."
                ],
                "correctAnswer": 2,
                "explanation": "Enterprise → Resources → Add New (the + button).",
                "rawAnswer": "Enterprise → Resources → Add New (the + button)."
            },
            {
                "id": "module-3-q70",
                "qNum": 70,
                "question": "What is the sample P6 Resource ID and name?",
                "options": [
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "The planned labor hours; e.g., Install Cable Tray = 800 Budgeted Labor Units.",
                    "Labor Hours = Crew Size × Hours per Day × Duration.",
                    "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent."
                ],
                "correctAnswer": 3,
                "explanation": "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent.",
                "rawAnswer": "ID ELEC-001; Name: Electrician, Journeyman. Keep IDs short and consistent."
            },
            {
                "id": "module-3-q71",
                "qNum": 71,
                "question": "What resource types does P6 have?",
                "options": [
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%.",
                    "Only the activity and its 10-day duration. No people, no cost, no availability check.",
                    "Labor, Nonlabor (equipment) and Material; each calculates cost differently.",
                    "Smoothing for small overallocations with available float; leveling for severe overallocation with no float remaining."
                ],
                "correctAnswer": 2,
                "explanation": "Labor, Nonlabor (equipment) and Material; each calculates cost differently.",
                "rawAnswer": "Labor, Nonlabor (equipment) and Material; each calculates cost differently."
            },
            {
                "id": "module-3-q72",
                "qNum": 72,
                "question": "Which calendar does P6 assign in the example?",
                "options": [
                    "Anything needed to perform the work.",
                    "WHO does the work, HOW MANY workers are needed, whether they are AVAILABLE, and HOW MUCH it will cost.",
                    "The 5-day/10-hour Northstar site calendar.",
                    "2.5 days."
                ],
                "correctAnswer": 2,
                "explanation": "The 5-day/10-hour Northstar site calendar.",
                "rawAnswer": "The 5-day/10-hour Northstar site calendar."
            },
            {
                "id": "module-3-q73",
                "qNum": 73,
                "question": "What does Max Units/Time mean in P6?",
                "options": [
                    "10 required − 8 available = −2 (overallocated by 2).",
                    "Cable 0.10 LH/LF, Conduit 0.125 LH/LF, Drywall 0.025 LH/SF, Concrete 0.67 LH/CY.",
                    "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d.",
                    "$26,000; $13,200; $60,000."
                ],
                "correctAnswer": 2,
                "explanation": "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d.",
                "rawAnswer": "The availability ceiling the system checks against; e.g., 8 electricians = 8 units/d."
            },
            {
                "id": "module-3-q74",
                "qNum": 74,
                "question": "What is Price/Unit in P6?",
                "options": [
                    "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost.",
                    "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500.",
                    "Crew A. Both installed 100 ft of cable, but A used 10 labor hours versus 20 for B: half the hours for the same result.",
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400."
                ],
                "correctAnswer": 0,
                "explanation": "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost.",
                "rawAnswer": "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost."
            },
            {
                "id": "module-3-q75",
                "qNum": 75,
                "question": "How do you assign a resource to an activity in P6?",
                "options": [
                    "The 5-day/10-hour Northstar site calendar.",
                    "For funding drawdowns (funds released before invoices are due), subcontractor payments (monthly invoices), and budget control (actual spend versus planned S-curve).",
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "Open the activity → Resources tab → Assign → select the resource."
                ],
                "correctAnswer": 3,
                "explanation": "Open the activity → Resources tab → Assign → select the resource.",
                "rawAnswer": "Open the activity → Resources tab → Assign → select the resource."
            },
            {
                "id": "module-3-q76",
                "qNum": 76,
                "question": "What are Budgeted Units and an example?",
                "options": [
                    "The planned labor hours; e.g., Install Cable Tray = 800 Budgeted Labor Units.",
                    "$68,000 + $11,000 = $79,000.",
                    "5 days.",
                    "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500."
                ],
                "correctAnswer": 0,
                "explanation": "The planned labor hours; e.g., Install Cable Tray = 800 Budgeted Labor Units.",
                "rawAnswer": "The planned labor hours; e.g., Install Cable Tray = 800 Budgeted Labor Units."
            },
            {
                "id": "module-3-q77",
                "qNum": 77,
                "question": "How do you review the P6 profile?",
                "options": [
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                    "An overallocated schedule is unrealistic because it assumes resources that don't exist; it must be resolved before approval.",
                    "$2,000 + $800 = $2,800.",
                    "View → Resource Usage Profile; red bars = overallocation."
                ],
                "correctAnswer": 3,
                "explanation": "View → Resource Usage Profile; red bars = overallocation.",
                "rawAnswer": "View → Resource Usage Profile; red bars = overallocation."
            },
            {
                "id": "module-3-q78",
                "qNum": 78,
                "question": "What do blue bars, red bars, the horizontal line and the stepped curve mean in P6?",
                "options": [
                    "The approved amount of money to complete the project, allocated down through the WBS to individual activities.",
                    "Blue: demand. Red: overallocation. Horizontal line: availability limit. Stepped curve: cumulative usage to date.",
                    "The 5-day/10-hour Northstar site calendar.",
                    "Electricians (labor), a crane (equipment), cable (material)."
                ],
                "correctAnswer": 1,
                "explanation": "Blue: demand. Red: overallocation. Horizontal line: availability limit. Stepped curve: cumulative usage to date.",
                "rawAnswer": "Blue: demand. Red: overallocation. Horizontal line: availability limit. Stepped curve: cumulative usage to date."
            },
            {
                "id": "module-3-q79",
                "qNum": 79,
                "question": "Home renovation example: painter 40 hrs × $50/hr and paint 20 gal × $40/gal. Total?",
                "options": [
                    "People carry boxes (labor), the truck transports everything (equipment), and packing boxes are consumed (material).",
                    "$2,000 + $800 = $2,800.",
                    "6 × 10 × 3 = 180 LH.",
                    "Using fewer labor hours to produce the same result."
                ],
                "correctAnswer": 1,
                "explanation": "$2,000 + $800 = $2,800.",
                "rawAnswer": "$2,000 + $800 = $2,800."
            },
            {
                "id": "module-3-q80",
                "qNum": 80,
                "question": "Construction equivalent: 800 LH × $85 and 500 LF cable tray × $22/LF. Total?",
                "options": [
                    "You must check whether the resources actually exist to perform the work (e.g., we need 10 electricians but only have 6).",
                    "$68,000 + $11,000 = $79,000.",
                    "2 × 8 × 15 = 240 LH.",
                    "3 × 8 × 5 = 120 LH."
                ],
                "correctAnswer": 1,
                "explanation": "$68,000 + $11,000 = $79,000.",
                "rawAnswer": "$68,000 + $11,000 = $79,000."
            },
            {
                "id": "module-3-q81",
                "qNum": 81,
                "question": "What is the labor cost formula?",
                "options": [
                    "Higher.",
                    "Labor Hours × Hourly Rate.",
                    "6 × 10 × 3 = 180 LH.",
                    "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price."
                ],
                "correctAnswer": 1,
                "explanation": "Labor Hours × Hourly Rate.",
                "rawAnswer": "Labor Hours × Hourly Rate."
            },
            {
                "id": "module-3-q82",
                "qNum": 82,
                "question": "What is 200 LH × $85/hr?",
                "options": [
                    "A management tool, not just a list of dates.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "Shifting work within its available float to remove peaks, without changing the project finish date.",
                    "$17,000."
                ],
                "correctAnswer": 3,
                "explanation": "$17,000.",
                "rawAnswer": "$17,000."
            },
            {
                "id": "module-3-q83",
                "qNum": 83,
                "question": "Compute 400 LH × $65/hr; 120 LH × $110/hr; 800 LH × $75/hr.",
                "options": [
                    "$26,000; $13,200; $60,000.",
                    "4 × 8 × 5 = 160 LH.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total)."
                ],
                "correctAnswer": 0,
                "explanation": "$26,000; $13,200; $60,000.",
                "rawAnswer": "$26,000; $13,200; $60,000."
            },
            {
                "id": "module-3-q84",
                "qNum": 84,
                "question": "What are the Northstar labor rates?",
                "options": [
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%.",
                    "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                    "Journeyman Electrician $85/hr; Foreman Electrician $95/hr (1 per 5–8 workers); Concrete Finisher $72/hr; Ironworker/Rigger $92/hr."
                ],
                "correctAnswer": 3,
                "explanation": "Journeyman Electrician $85/hr; Foreman Electrician $95/hr (1 per 5–8 workers); Concrete Finisher $72/hr; Ironworker/Rigger $92/hr.",
                "rawAnswer": "Journeyman Electrician $85/hr; Foreman Electrician $95/hr (1 per 5–8 workers); Concrete Finisher $72/hr; Ironworker/Rigger $92/hr."
            },
            {
                "id": "module-3-q85",
                "qNum": 85,
                "question": "What is the equipment cost formula and example?",
                "options": [
                    "The cost rate, e.g., $85.00/hr for a journeyman electrician, used to calculate budgeted cost.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500.",
                    "500 × 0.10 = 50 LH (using the rate table)."
                ],
                "correctAnswer": 2,
                "explanation": "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500.",
                "rawAnswer": "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500."
            },
            {
                "id": "module-3-q86",
                "qNum": 86,
                "question": "What is the material cost formula and example?",
                "options": [
                    "Enterprise → Resources → Add New (the + button).",
                    "2 × 8 × 15 = 240 LH.",
                    "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500.",
                    "Measured in quantity (CY, LF, EA, Ton); cost = quantity × unit price."
                ],
                "correctAnswer": 2,
                "explanation": "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500.",
                "rawAnswer": "Quantity × Unit Rate. Ready-mix concrete: 100 CY × $165/CY = $16,500."
            },
            {
                "id": "module-3-q87",
                "qNum": 87,
                "question": "Why is material cost based on quantity, not time?",
                "options": [
                    "4 × 8 × 5 = 160 LH.",
                    "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                    "Material is consumed; once the concrete is poured it is gone.",
                    "5 days."
                ],
                "correctAnswer": 2,
                "explanation": "Material is consumed; once the concrete is poured it is gone.",
                "rawAnswer": "Material is consumed; once the concrete is poured it is gone."
            },
            {
                "id": "module-3-q88",
                "qNum": 88,
                "question": "What does equipment cost reflect if it earns no wages?",
                "options": [
                    "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K.",
                    "Using fewer labor hours to produce the same result.",
                    "A rental or ownership cost, charged per hour it's used on site.",
                    "$45M total capital budget; Electrical Systems $18M (largest WBS element); 120+ activities, each with its own budget."
                ],
                "correctAnswer": 2,
                "explanation": "A rental or ownership cost, charged per hour it's used on site.",
                "rawAnswer": "A rental or ownership cost, charged per hour it's used on site."
            },
            {
                "id": "module-3-q89",
                "qNum": 89,
                "question": "What are the components of total Activity Cost?",
                "options": [
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "Assigning a budgeted cost to each activity so the schedule knows not just when work happens but how much it costs.",
                    "Cable 10 LF/LH, Conduit 8 LF/LH, Drywall 40 SF/LH, Concrete 1.5 CY/LH.",
                    "Labor + Equipment + Material + Expense."
                ],
                "correctAnswer": 3,
                "explanation": "Labor + Equipment + Material + Expense.",
                "rawAnswer": "Labor + Equipment + Material + Expense."
            },
            {
                "id": "module-3-q90",
                "qNum": 90,
                "question": "Break down the \"Pour Generator Pad: Data Hall A\" activity cost.",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "It's how all labor hours are calculated, every time, on every project.",
                    "2.5 days.",
                    "Hours Used × Hourly Rate. Mobile crane: 10 hrs × $350/hr = $3,500."
                ],
                "correctAnswer": 0,
                "explanation": "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                "rawAnswer": "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900."
            },
            {
                "id": "module-3-q91",
                "qNum": 91,
                "question": "What are the three cost-loaded activities shown?",
                "options": [
                    "Install Cable Tray (10d) $79,000; Pour Generator Pad (3d) $32,900; Set UPS Modules (5d) $48,500.",
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "$17,000.",
                    "$2,000 + $800 = $2,800."
                ],
                "correctAnswer": 0,
                "explanation": "Install Cable Tray (10d) $79,000; Pour Generator Pad (3d) $32,900; Set UPS Modules (5d) $48,500.",
                "rawAnswer": "Install Cable Tray (10d) $79,000; Pour Generator Pad (3d) $32,900; Set UPS Modules (5d) $48,500."
            },
            {
                "id": "module-3-q92",
                "qNum": 92,
                "question": "What is cost loading?",
                "options": [
                    "Assigning a budgeted cost to each activity so the schedule knows not just when work happens but how much it costs.",
                    "Labor Hours = Crew Size × Hours per Day × Duration.",
                    "Duration = Labor Hours ÷ (Crew Size × Hours per Day).",
                    "Material is consumed; once the concrete is poured it is gone."
                ],
                "correctAnswer": 0,
                "explanation": "Assigning a budgeted cost to each activity so the schedule knows not just when work happens but how much it costs.",
                "rawAnswer": "Assigning a budgeted cost to each activity so the schedule knows not just when work happens but how much it costs."
            },
            {
                "id": "module-3-q93",
                "qNum": 93,
                "question": "What is a budget and how is it allocated?",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "The approved amount of money to complete the project, allocated down through the WBS to individual activities.",
                    "A bar chart showing how many resources are needed over time, one bar per period (usually a week or a day).",
                    "Shifting work within its available float to remove peaks, without changing the project finish date."
                ],
                "correctAnswer": 1,
                "explanation": "The approved amount of money to complete the project, allocated down through the WBS to individual activities.",
                "rawAnswer": "The approved amount of money to complete the project, allocated down through the WBS to individual activities."
            },
            {
                "id": "module-3-q94",
                "qNum": 94,
                "question": "Show the $1M example budget by WBS element.",
                "options": [
                    "Labor 180 LH × $72 = $12,960; Equipment 8 hrs × $280 = $2,240; Material 100 CY × $165 = $16,500; Expense (testing/inspection lump sum) $1,200. Total $32,900.",
                    "Delaying activities beyond their float to resolve overallocation, which may push out the project finish date.",
                    "Labor, Equipment/Nonlabor, and Material.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
                ],
                "correctAnswer": 3,
                "explanation": "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000.",
                "rawAnswer": "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
            },
            {
                "id": "module-3-q95",
                "qNum": 95,
                "question": "What is the scale of the Northstar budget?",
                "options": [
                    "200 LH.",
                    "$45M total capital budget; Electrical Systems $18M (largest WBS element); 120+ activities, each with its own budget.",
                    "Measured in hours worked; cost = hourly rate × hours worked.",
                    "Week 3 requires 10 electricians but only 8 are available."
                ],
                "correctAnswer": 1,
                "explanation": "$45M total capital budget; Electrical Systems $18M (largest WBS element); 120+ activities, each with its own budget.",
                "rawAnswer": "$45M total capital budget; Electrical Systems $18M (largest WBS element); 120+ activities, each with its own budget."
            },
            {
                "id": "module-3-q96",
                "qNum": 96,
                "question": "What is time-phased cost, and what is the $1M monthly spend?",
                "options": [
                    "1 day.",
                    "A management tool, not just a list of dates.",
                    "View → Resource Sheet.",
                    "The budget spread across the timeline by when activities are scheduled (the spending profile): Jan $100K, Feb $150K, Mar $250K, Apr $300K, May $200K = $1,000K."
                ],
                "correctAnswer": 3,
                "explanation": "The budget spread across the timeline by when activities are scheduled (the spending profile): Jan $100K, Feb $150K, Mar $250K, Apr $300K, May $200K = $1,000K.",
                "rawAnswer": "The budget spread across the timeline by when activities are scheduled (the spending profile): Jan $100K, Feb $150K, Mar $250K, Apr $300K, May $200K = $1,000K."
            },
            {
                "id": "module-3-q97",
                "qNum": 97,
                "question": "What is an S-curve and why is it S-shaped?",
                "options": [
                    "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K.",
                    "The 5-day/10-hour Northstar site calendar.",
                    "The most units of a resource available at any time (e.g., company has 8 electricians total).",
                    "More people can compress duration, especially when workers don't get in each other's way."
                ],
                "correctAnswer": 0,
                "explanation": "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K.",
                "rawAnswer": "Cumulative cost over time. Slow start (mobilization/procurement), steep middle (multiple trades working) and flattening tail (commissioning/closeout). The cumulative example: $100K, $250K, $500K, $800K, $1,000K."
            },
            {
                "id": "module-3-q98",
                "qNum": 98,
                "question": "Why do project managers need cash-flow forecasts?",
                "options": [
                    "For funding drawdowns (funds released before invoices are due), subcontractor payments (monthly invoices), and budget control (actual spend versus planned S-curve).",
                    "Enterprise → Resources → Add New (the + button).",
                    "You must check whether the resources actually exist to perform the work (e.g., we need 10 electricians but only have 6).",
                    "Smoothing for small overallocations with available float; leveling for severe overallocation with no float remaining."
                ],
                "correctAnswer": 0,
                "explanation": "For funding drawdowns (funds released before invoices are due), subcontractor payments (monthly invoices), and budget control (actual spend versus planned S-curve).",
                "rawAnswer": "For funding drawdowns (funds released before invoices are due), subcontractor payments (monthly invoices), and budget control (actual spend versus planned S-curve)."
            },
            {
                "id": "module-3-q99",
                "qNum": 99,
                "question": "What are the Phase 1 training project's overallocation risk and its answers?",
                "options": [
                    "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                    "The 5-day/10-hour Northstar site calendar.",
                    "Time-Only → Add Resources → Check Availability → Calculate Cost.",
                    "Electrical $350,000; Civil/Structural $200,000; Mechanical $250,000; Management $100,000; Contingency $100,000; Total $1,000,000."
                ],
                "correctAnswer": 0,
                "explanation": "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400.",
                "rawAnswer": "A1020 Install Conduit and A1030 Install Cable Tray both use 4 electricians; run in parallel they need 8, exactly at the max of 8. Labor costs: Excavate $7,800; Form & Pour $12,960; Conduit $17,000; Cable Tray $17,000; Set Generator $2,944 (32 LH at $92). Materials: concrete $16,500; conduit 400 LF × $8.50 = $3,400."
            },
            {
                "id": "module-3-q100",
                "qNum": 100,
                "question": "What are the Phase 2 expert deliverables and the full chain from activity to S-curve?",
                "options": [
                    "Enter the standard rate (e.g., $85.00/hr) and MSP calculates cost automatically as you assign.",
                    "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                    "The maximum number available at one time, expressed in %. 8 electricians = 800%.",
                    "500 × 0.10 = 50 LH (using the rate table)."
                ],
                "correctAnswer": 1,
                "explanation": "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve.",
                "rawAnswer": "Deliverables: budget reconciliation, WBS cost rollup, time-phased cost report, S-curve, and a one-page resource feasibility memo (executive summary, demand table, resolutions applied, remaining risks). Chain: Activity + duration → crew size → labor hours → productivity → resource demand → activity cost → monthly spend → S-curve. One wrong crew size changes labor hours, cost, cash flow and the S-curve."
            }
        ]
    },
    {
        "id": "module-4",
        "aliases": [
            "baselines",
            "forecasting"
        ],
        "title": "Module 4: Baselines, Progress Updating & Forecasting",
        "description": "The 6-step schedule control cycle, Data Date rules, Remaining Duration, field data gathering, cascading delays, and lookahead reporting.",
        "project": "LGL Northstar Data Center Capital Project",
        "iconName": "TrendingUp",
        "color": "#10B981",
        "questions": [
            {
                "id": "module-4-q1",
                "qNum": 1,
                "question": "What central question does Module 4 answer?",
                "options": [
                    "No; it's the system working correctly as new field information arrives.",
                    "−9 days.",
                    "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?",
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data."
                ],
                "correctAnswer": 2,
                "explanation": "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?",
                "rawAnswer": "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?"
            },
            {
                "id": "module-4-q2",
                "qNum": 2,
                "question": "How does the road trip analogy map to schedule control?",
                "options": [
                    "25 sections; each concept is taught four ways: simple meaning → everyday example → project example → software example.",
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm)."
                ],
                "correctAnswer": 3,
                "explanation": "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm).",
                "rawAnswer": "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm)."
            },
            {
                "id": "module-4-q3",
                "qNum": 3,
                "question": "What are the six steps of schedule control?",
                "options": [
                    "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                    "Plan, Baseline, Status, Analyze, Forecast, Control.",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
                ],
                "correctAnswer": 1,
                "explanation": "Plan, Baseline, Status, Analyze, Forecast, Control.",
                "rawAnswer": "Plan, Baseline, Status, Analyze, Forecast, Control."
            },
            {
                "id": "module-4-q4",
                "qNum": 4,
                "question": "What question does each step answer?",
                "options": [
                    "The new predicted completion date given current information.",
                    "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?",
                    "Retained Logic honors the original logic (successor can't finish until the predecessor finishes): more conservative. Progress Override ignores the broken logic and forecasts from remaining duration only: more optimistic, may understate impact.",
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on."
                ],
                "correctAnswer": 1,
                "explanation": "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?",
                "rawAnswer": "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?"
            },
            {
                "id": "module-4-q5",
                "qNum": 5,
                "question": "What happens if a step in the cycle is missed?",
                "options": [
                    "Forecasts become unreliable.",
                    "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9).",
                    "The number of more working days the field says it will take to finish an in-progress activity; it drives the forecast calculation.",
                    "View → Tables → Variance (Finish Variance column)."
                ],
                "correctAnswer": 0,
                "explanation": "Forecasts become unreliable.",
                "rawAnswer": "Forecasts become unreliable."
            },
            {
                "id": "module-4-q6",
                "qNum": 6,
                "question": "What does \"control\" involve?",
                "options": [
                    "Recovery plans, acceleration and management decisions.",
                    "Not Started, In Progress, Completed.",
                    "The calendar date physical work began in the field; a fact, not an opinion or a wish.",
                    "No action; the software forecasts based on remaining logic."
                ],
                "correctAnswer": 0,
                "explanation": "Recovery plans, acceleration and management decisions.",
                "rawAnswer": "Recovery plans, acceleration and management decisions."
            },
            {
                "id": "module-4-q7",
                "qNum": 7,
                "question": "In the module overview, how many sections does Module 4 have and how are concepts taught?",
                "options": [
                    "It tells the software the activity is 100% done and removes it from critical path analysis.",
                    "−9 days.",
                    "A frozen snapshot of the schedule at approval, like a photo of a newly built house used ten years later to see what changed.",
                    "25 sections; each concept is taught four ways: simple meaning → everyday example → project example → software example."
                ],
                "correctAnswer": 3,
                "explanation": "25 sections; each concept is taught four ways: simple meaning → everyday example → project example → software example.",
                "rawAnswer": "25 sections; each concept is taught four ways: simple meaning → everyday example → project example → software example."
            },
            {
                "id": "module-4-q8",
                "qNum": 8,
                "question": "What is the module's project?",
                "options": [
                    "The LGL Northstar Data Center Capital Project.",
                    "A 10-day activity, 5 days elapsed, field says 50% done, but cable pulling, terminations and testing need 7 more days: total 12 days, 2 days late.",
                    "No actual information; the activity sits entirely in the future.",
                    "Quantified progress such as linear feet installed, cubic yards poured, equipment set."
                ],
                "correctAnswer": 0,
                "explanation": "The LGL Northstar Data Center Capital Project.",
                "rawAnswer": "The LGL Northstar Data Center Capital Project."
            },
            {
                "id": "module-4-q9",
                "qNum": 9,
                "question": "What is the difference between status and analysis?",
                "options": [
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "No; it's the system working correctly as new field information arrives.",
                    "No action; the software forecasts based on remaining logic.",
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance."
                ],
                "correctAnswer": 3,
                "explanation": "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                "rawAnswer": "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance."
            },
            {
                "id": "module-4-q10",
                "qNum": 10,
                "question": "What does the forecast represent?",
                "options": [
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "The new predicted completion date given current information.",
                    "Total = 12 days, a 2-day delay that cascades forward.",
                    "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm)."
                ],
                "correctAnswer": 1,
                "explanation": "The new predicted completion date given current information.",
                "rawAnswer": "The new predicted completion date given current information."
            },
            {
                "id": "module-4-q11",
                "qNum": 11,
                "question": "What is a baseline (photograph analogy)?",
                "options": [
                    "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?",
                    "A frozen snapshot of the schedule at approval, like a photo of a newly built house used ten years later to see what changed.",
                    "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data."
                ],
                "correctAnswer": 1,
                "explanation": "A frozen snapshot of the schedule at approval, like a photo of a newly built house used ten years later to see what changed.",
                "rawAnswer": "A frozen snapshot of the schedule at approval, like a photo of a newly built house used ten years later to see what changed."
            },
            {
                "id": "module-4-q12",
                "qNum": 12,
                "question": "What four things does a baseline record?",
                "options": [
                    "Baseline Start, Baseline Finish, Baseline Duration and Baseline Cost.",
                    "Both Actual Start and Actual Finish; Remaining Duration = 0.",
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage).",
                    "Whether it's caused by real delays or an incorrectly applied constraint."
                ],
                "correctAnswer": 0,
                "explanation": "Baseline Start, Baseline Finish, Baseline Duration and Baseline Cost.",
                "rawAnswer": "Baseline Start, Baseline Finish, Baseline Duration and Baseline Cost."
            },
            {
                "id": "module-4-q13",
                "qNum": 13,
                "question": "Why must you not change the baseline every time something goes wrong?",
                "options": [
                    "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                    "Actual Start known; Actual Finish not yet entered; Remaining Duration entered.",
                    "It destroys it.",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration."
                ],
                "correctAnswer": 0,
                "explanation": "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                "rawAnswer": "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance."
            },
            {
                "id": "module-4-q14",
                "qNum": 14,
                "question": "What is the right approach when electrical rough-in falls behind?",
                "options": [
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                    "The LGL Northstar Data Center Capital Project.",
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion."
                ],
                "correctAnswer": 3,
                "explanation": "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion.",
                "rawAnswer": "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion."
            },
            {
                "id": "module-4-q15",
                "qNum": 15,
                "question": "When may a baseline revision be justified?",
                "options": [
                    "Who they spoke with, the date, and what they were told, for every update.",
                    "Collect field data, enter it faithfully, recalculate, analyze and report honestly to management.",
                    "A formal scope change approved by the owner, through proper change management procedures.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
                ],
                "correctAnswer": 2,
                "explanation": "A formal scope change approved by the owner, through proper change management procedures.",
                "rawAnswer": "A formal scope change approved by the owner, through proper change management procedures."
            },
            {
                "id": "module-4-q16",
                "qNum": 16,
                "question": "What is an update?",
                "options": [
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now).",
                    "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete."
                ],
                "correctAnswer": 3,
                "explanation": "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete.",
                "rawAnswer": "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete."
            },
            {
                "id": "module-4-q17",
                "qNum": 17,
                "question": "What is an update NOT?",
                "options": [
                    "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it.",
                    "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date.",
                    "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
                ],
                "correctAnswer": 2,
                "explanation": "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better.",
                "rawAnswer": "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better."
            },
            {
                "id": "module-4-q18",
                "qNum": 18,
                "question": "What is the GPS analogy for updating?",
                "options": [
                    "Forecasts become unreliable.",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "You tell it where you are now and it recalculates the route ahead; you don't redraw the roads.",
                    "Enter Actual Start and update Remaining Duration from the field."
                ],
                "correctAnswer": 2,
                "explanation": "You tell it where you are now and it recalculates the route ahead; you don't redraw the roads.",
                "rawAnswer": "You tell it where you are now and it recalculates the route ahead; you don't redraw the roads."
            },
            {
                "id": "module-4-q19",
                "qNum": 19,
                "question": "What is the scheduler's job during an update?",
                "options": [
                    "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                    "Collect field data, enter it faithfully, recalculate, analyze and report honestly to management.",
                    "It destroys it.",
                    "Forecasts become unreliable."
                ],
                "correctAnswer": 1,
                "explanation": "Collect field data, enter it faithfully, recalculate, analyze and report honestly to management.",
                "rawAnswer": "Collect field data, enter it faithfully, recalculate, analyze and report honestly to management."
            },
            {
                "id": "module-4-q20",
                "qNum": 20,
                "question": "What does an update do to the baseline comparison if done wrongly (rebuilding)?",
                "options": [
                    "It destroys it.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database.",
                    "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration)."
                ],
                "correctAnswer": 0,
                "explanation": "It destroys it.",
                "rawAnswer": "It destroys it."
            },
            {
                "id": "module-4-q21",
                "qNum": 21,
                "question": "What is the Data Date (Status Date)?",
                "options": [
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "Use Remaining Duration as the primary driver; % Complete is for reporting only. Ask \"How many days until this is done?\" not \"What percent done are you?\"",
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion.",
                    "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it."
                ],
                "correctAnswer": 3,
                "explanation": "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it.",
                "rawAnswer": "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it."
            },
            {
                "id": "module-4-q22",
                "qNum": 22,
                "question": "What should exist to the LEFT of the Data Date?",
                "options": [
                    "Actual information: Actual Start & Finish for completed activities, Actual Start & Remaining Duration for in-progress ones; no planned-only data.",
                    "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better."
                ],
                "correctAnswer": 0,
                "explanation": "Actual information: Actual Start & Finish for completed activities, Actual Start & Remaining Duration for in-progress ones; no planned-only data.",
                "rawAnswer": "Actual information: Actual Start & Finish for completed activities, Actual Start & Remaining Duration for in-progress ones; no planned-only data."
            },
            {
                "id": "module-4-q23",
                "qNum": 23,
                "question": "What exists to the RIGHT of the Data Date?",
                "options": [
                    "Forecast: remaining work calculated by software; float, critical path and variance are measured here.",
                    "The LGL Northstar Data Center Capital Project.",
                    "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent.",
                    "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?"
                ],
                "correctAnswer": 0,
                "explanation": "Forecast: remaining work calculated by software; float, critical path and variance are measured here.",
                "rawAnswer": "Forecast: remaining work calculated by software; float, critical path and variance are measured here."
            },
            {
                "id": "module-4-q24",
                "qNum": 24,
                "question": "Data Date = Oct 15: foundation pour planned Oct 8, finished Oct 10. What do you do?",
                "options": [
                    "Actual information: Actual Start & Finish for completed activities, Actual Start & Remaining Duration for in-progress ones; no planned-only data.",
                    "Enter Actual Finish = Oct 10 (2 days late).",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work."
                ],
                "correctAnswer": 1,
                "explanation": "Enter Actual Finish = Oct 10 (2 days late).",
                "rawAnswer": "Enter Actual Finish = Oct 10 (2 days late)."
            },
            {
                "id": "module-4-q25",
                "qNum": 25,
                "question": "Data Date = Oct 15: electrical rough-in in progress, planned to finish Oct 20. What do you do?",
                "options": [
                    "The calendar date physical work began in the field; a fact, not an opinion or a wish.",
                    "Enter Actual Start and update Remaining Duration from the field.",
                    "Actual Start known; Actual Finish not yet entered; Remaining Duration entered.",
                    "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9)."
                ],
                "correctAnswer": 1,
                "explanation": "Enter Actual Start and update Remaining Duration from the field.",
                "rawAnswer": "Enter Actual Start and update Remaining Duration from the field."
            },
            {
                "id": "module-4-q26",
                "qNum": 26,
                "question": "Data Date = Oct 15: mechanical equipment install planned for Nov 5, not started. What do you do?",
                "options": [
                    "Enter Actual Finish = Oct 10 (2 days late).",
                    "No action; the software forecasts based on remaining logic.",
                    "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged.",
                    "The progressive reduction of float on an activity across successive updates."
                ],
                "correctAnswer": 1,
                "explanation": "No action; the software forecasts based on remaining logic.",
                "rawAnswer": "No action; the software forecasts based on remaining logic."
            },
            {
                "id": "module-4-q27",
                "qNum": 27,
                "question": "When is the Data Date typically set?",
                "options": [
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?",
                    "No action; the software forecasts based on remaining logic.",
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion."
                ],
                "correctAnswer": 0,
                "explanation": "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                "rawAnswer": "The last day of the reporting period, e.g., every Friday or the last working day of the month."
            },
            {
                "id": "module-4-q28",
                "qNum": 28,
                "question": "What are the three activity statuses?",
                "options": [
                    "Enter Actual Start and update Remaining Duration from the field.",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "Not Started, In Progress, Completed."
                ],
                "correctAnswer": 3,
                "explanation": "Not Started, In Progress, Completed.",
                "rawAnswer": "Not Started, In Progress, Completed."
            },
            {
                "id": "module-4-q29",
                "qNum": 29,
                "question": "What data does Not Started require?",
                "options": [
                    "No actual information; the activity sits entirely in the future.",
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage).",
                    "No; it's the system working correctly as new field information arrives.",
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report."
                ],
                "correctAnswer": 0,
                "explanation": "No actual information; the activity sits entirely in the future.",
                "rawAnswer": "No actual information; the activity sits entirely in the future."
            },
            {
                "id": "module-4-q30",
                "qNum": 30,
                "question": "What data does In Progress require?",
                "options": [
                    "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required.",
                    "RD = 6; total 4 + 6 = 10 days, on plan.",
                    "Actual Start known; Actual Finish not yet entered; Remaining Duration entered.",
                    "Just because you want the work done by that date, the contract requires it, someone says \"almost done,\" or the baseline shows that date."
                ],
                "correctAnswer": 2,
                "explanation": "Actual Start known; Actual Finish not yet entered; Remaining Duration entered.",
                "rawAnswer": "Actual Start known; Actual Finish not yet entered; Remaining Duration entered."
            },
            {
                "id": "module-4-q31",
                "qNum": 31,
                "question": "What data does Completed require?",
                "options": [
                    "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete.",
                    "They know what started, what finished and what's left.",
                    "Both Actual Start and Actual Finish; Remaining Duration = 0.",
                    "No; it's the system working correctly as new field information arrives."
                ],
                "correctAnswer": 2,
                "explanation": "Both Actual Start and Actual Finish; Remaining Duration = 0.",
                "rawAnswer": "Both Actual Start and Actual Finish; Remaining Duration = 0."
            },
            {
                "id": "module-4-q32",
                "qNum": 32,
                "question": "Give Northstar examples of each status as of Oct 15.",
                "options": [
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion.",
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                    "Not Started: generator pad construction planned for November. In Progress: electrical rough-in started Oct 6. Completed: foundation pour started Oct 1, finished Oct 10.",
                    "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun."
                ],
                "correctAnswer": 2,
                "explanation": "Not Started: generator pad construction planned for November. In Progress: electrical rough-in started Oct 6. Completed: foundation pour started Oct 1, finished Oct 10.",
                "rawAnswer": "Not Started: generator pad construction planned for November. In Progress: electrical rough-in started Oct 6. Completed: foundation pour started Oct 1, finished Oct 10."
            },
            {
                "id": "module-4-q33",
                "qNum": 33,
                "question": "What is Actual Start?",
                "options": [
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                    "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions.",
                    "The calendar date physical work began in the field; a fact, not an opinion or a wish."
                ],
                "correctAnswer": 3,
                "explanation": "The calendar date physical work began in the field; a fact, not an opinion or a wish.",
                "rawAnswer": "The calendar date physical work began in the field; a fact, not an opinion or a wish."
            },
            {
                "id": "module-4-q34",
                "qNum": 34,
                "question": "Give correct examples of Actual Start.",
                "options": [
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm).",
                    "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date.",
                    "Recovery plans, acceleration and management decisions."
                ],
                "correctAnswer": 2,
                "explanation": "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date.",
                "rawAnswer": "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date."
            },
            {
                "id": "module-4-q35",
                "qNum": 35,
                "question": "Give incorrect ways to enter Actual Start.",
                "options": [
                    "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?",
                    "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide.",
                    "They know what started, what finished and what's left.",
                    "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun."
                ],
                "correctAnswer": 3,
                "explanation": "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun.",
                "rawAnswer": "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun."
            },
            {
                "id": "module-4-q36",
                "qNum": 36,
                "question": "Why are wrong Actual Starts dangerous?",
                "options": [
                    "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent.",
                    "The LGL Northstar Data Center Capital Project.",
                    "Current remaining durations hold, no new delays, and logic remains unchanged.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
                ],
                "correctAnswer": 0,
                "explanation": "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent.",
                "rawAnswer": "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent."
            },
            {
                "id": "module-4-q37",
                "qNum": 37,
                "question": "What is Actual Finish?",
                "options": [
                    "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                    "Plan: what did we intend? Baseline: what plan was approved? Status: what actually happened? Analyze: what changed? Forecast: where are we heading? Control: what should management do?"
                ],
                "correctAnswer": 0,
                "explanation": "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                "rawAnswer": "The date the last unit of work was physically completed and accepted; a fact, not a plan."
            },
            {
                "id": "module-4-q38",
                "qNum": 38,
                "question": "When must you NOT enter an Actual Finish?",
                "options": [
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "Just because you want the work done by that date, the contract requires it, someone says \"almost done,\" or the baseline shows that date.",
                    "Not Started, In Progress, Completed.",
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report."
                ],
                "correctAnswer": 1,
                "explanation": "Just because you want the work done by that date, the contract requires it, someone says \"almost done,\" or the baseline shows that date.",
                "rawAnswer": "Just because you want the work done by that date, the contract requires it, someone says \"almost done,\" or the baseline shows that date."
            },
            {
                "id": "module-4-q39",
                "qNum": 39,
                "question": "What happens if you enter a premature Actual Finish?",
                "options": [
                    "Elapsed days + Remaining Duration.",
                    "It tells the software the activity is 100% done and removes it from critical path analysis.",
                    "Baseline completion Nov 5, forecast Nov 10, variance +5 working days.",
                    "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report)."
                ],
                "correctAnswer": 1,
                "explanation": "It tells the software the activity is 100% done and removes it from critical path analysis.",
                "rawAnswer": "It tells the software the activity is 100% done and removes it from critical path analysis."
            },
            {
                "id": "module-4-q40",
                "qNum": 40,
                "question": "UPS installation was baselined to finish Oct 18. On Oct 15 the field says it's 80% done and needs 3 more days. What do you enter?",
                "options": [
                    "Baseline completion Nov 5, forecast Nov 10, variance +5 working days.",
                    "It tells the software the activity is 100% done and removes it from critical path analysis.",
                    "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                    "Actual Start and Remaining Duration = 3 days; leave Actual Finish blank."
                ],
                "correctAnswer": 3,
                "explanation": "Actual Start and Remaining Duration = 3 days; leave Actual Finish blank.",
                "rawAnswer": "Actual Start and Remaining Duration = 3 days; leave Actual Finish blank."
            },
            {
                "id": "module-4-q41",
                "qNum": 41,
                "question": "When can you enter an Actual Finish?",
                "options": [
                    "The best prediction given current information; it changes as conditions change.",
                    "Only when the superintendent confirms the scope of work is 100% complete.",
                    "Superintendent, Project Manager, Subcontractors, Daily Reports, Progress Meetings, and Quantity Tracking.",
                    "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work."
                ],
                "correctAnswer": 1,
                "explanation": "Only when the superintendent confirms the scope of work is 100% complete.",
                "rawAnswer": "Only when the superintendent confirms the scope of work is 100% complete."
            },
            {
                "id": "module-4-q42",
                "qNum": 42,
                "question": "What is Remaining Duration?",
                "options": [
                    "25 sections; each concept is taught four ways: simple meaning → everyday example → project example → software example.",
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage).",
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "The number of more working days the field says it will take to finish an in-progress activity; it drives the forecast calculation."
                ],
                "correctAnswer": 3,
                "explanation": "The number of more working days the field says it will take to finish an in-progress activity; it drives the forecast calculation.",
                "rawAnswer": "The number of more working days the field says it will take to finish an in-progress activity; it drives the forecast calculation."
            },
            {
                "id": "module-4-q43",
                "qNum": 43,
                "question": "Electrical rough-in: 10 days planned, 4 elapsed, superintendent says 6 more. What is RD and the outcome?",
                "options": [
                    "RD = 6; total 4 + 6 = 10 days, on plan.",
                    "The difference between plan and reality; information, not a bad word.",
                    "It destroys it.",
                    "Use Remaining Duration as the primary driver; % Complete is for reporting only. Ask \"How many days until this is done?\" not \"What percent done are you?\""
                ],
                "correctAnswer": 0,
                "explanation": "RD = 6; total 4 + 6 = 10 days, on plan.",
                "rawAnswer": "RD = 6; total 4 + 6 = 10 days, on plan."
            },
            {
                "id": "module-4-q44",
                "qNum": 44,
                "question": "What if the superintendent says RD = 8 instead?",
                "options": [
                    "Total = 12 days, a 2-day delay that cascades forward.",
                    "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required.",
                    "The new predicted completion date given current information.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database."
                ],
                "correctAnswer": 0,
                "explanation": "Total = 12 days, a 2-day delay that cascades forward.",
                "rawAnswer": "Total = 12 days, a 2-day delay that cascades forward."
            },
            {
                "id": "module-4-q45",
                "qNum": 45,
                "question": "Why is % Complete \"harder to trust\"?",
                "options": [
                    "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work.",
                    "Only when the superintendent confirms the scope of work is 100% complete.",
                    "The activity is taking 2 days longer than planned.",
                    "The calendar date physical work began in the field; a fact, not an opinion or a wish."
                ],
                "correctAnswer": 0,
                "explanation": "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work.",
                "rawAnswer": "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work."
            },
            {
                "id": "module-4-q46",
                "qNum": 46,
                "question": "What is the example of misleading % complete?",
                "options": [
                    "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun.",
                    "Originally: site grading → foundation → structural steel → envelope → MEP rough-in → equipment set → energize → substantial completion. After the Oct 15 update, electrical rough-in slipped 3 days, consumed its 2 days of float and now has negative float; the path runs through electrical rough-in → energize & test → substantial completion.",
                    "A 10-day activity, 5 days elapsed, field says 50% done, but cable pulling, terminations and testing need 7 more days: total 12 days, 2 days late.",
                    "No actual information; the activity sits entirely in the future."
                ],
                "correctAnswer": 2,
                "explanation": "A 10-day activity, 5 days elapsed, field says 50% done, but cable pulling, terminations and testing need 7 more days: total 12 days, 2 days late.",
                "rawAnswer": "A 10-day activity, 5 days elapsed, field says 50% done, but cable pulling, terminations and testing need 7 more days: total 12 days, 2 days late."
            },
            {
                "id": "module-4-q47",
                "qNum": 47,
                "question": "What is the best practice regarding % Complete vs. RD?",
                "options": [
                    "It destroys it.",
                    "The calendar date physical work began in the field; a fact, not an opinion or a wish.",
                    "They know what started, what finished and what's left.",
                    "Use Remaining Duration as the primary driver; % Complete is for reporting only. Ask \"How many days until this is done?\" not \"What percent done are you?\""
                ],
                "correctAnswer": 3,
                "explanation": "Use Remaining Duration as the primary driver; % Complete is for reporting only. Ask \"How many days until this is done?\" not \"What percent done are you?\"",
                "rawAnswer": "Use Remaining Duration as the primary driver; % Complete is for reporting only. Ask \"How many days until this is done?\" not \"What percent done are you?\""
            },
            {
                "id": "module-4-q48",
                "qNum": 48,
                "question": "What does P6 recommend for % complete?",
                "options": [
                    "Baseline Start, Baseline Finish, Baseline Duration and Baseline Cost.",
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion.",
                    "Use Duration % Complete driven by Remaining Duration.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database."
                ],
                "correctAnswer": 2,
                "explanation": "Use Duration % Complete driven by Remaining Duration.",
                "rawAnswer": "Use Duration % Complete driven by Remaining Duration."
            },
            {
                "id": "module-4-q49",
                "qNum": 49,
                "question": "What does MSP recommend for % complete?",
                "options": [
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now).",
                    "Use \"% Work Complete\" cautiously and validate with Remaining Duration.",
                    "They know what started, what finished and what's left."
                ],
                "correctAnswer": 2,
                "explanation": "Use \"% Work Complete\" cautiously and validate with Remaining Duration.",
                "rawAnswer": "Use \"% Work Complete\" cautiously and validate with Remaining Duration."
            },
            {
                "id": "module-4-q50",
                "qNum": 50,
                "question": "What is the formula for total duration of an in-progress activity?",
                "options": [
                    "No; it's the system working correctly as new field information arrives.",
                    "The progressive reduction of float on an activity across successive updates.",
                    "Elapsed days + Remaining Duration.",
                    "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better."
                ],
                "correctAnswer": 2,
                "explanation": "Elapsed days + Remaining Duration.",
                "rawAnswer": "Elapsed days + Remaining Duration."
            },
            {
                "id": "module-4-q51",
                "qNum": 51,
                "question": "Where does update data come from?",
                "options": [
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                    "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                    "Superintendent, Project Manager, Subcontractors, Daily Reports, Progress Meetings, and Quantity Tracking.",
                    "Actual Start and Remaining Duration = 3 days; leave Actual Finish blank."
                ],
                "correctAnswer": 2,
                "explanation": "Superintendent, Project Manager, Subcontractors, Daily Reports, Progress Meetings, and Quantity Tracking.",
                "rawAnswer": "Superintendent, Project Manager, Subcontractors, Daily Reports, Progress Meetings, and Quantity Tracking."
            },
            {
                "id": "module-4-q52",
                "qNum": 52,
                "question": "Why is the superintendent the primary field source?",
                "options": [
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                    "RD = 6; total 4 + 6 = 10 days, on plan.",
                    "The LGL Northstar Data Center Capital Project.",
                    "They know what started, what finished and what's left."
                ],
                "correctAnswer": 3,
                "explanation": "They know what started, what finished and what's left.",
                "rawAnswer": "They know what started, what finished and what's left."
            },
            {
                "id": "module-4-q53",
                "qNum": 53,
                "question": "What is quantity tracking?",
                "options": [
                    "A 10-day activity, 5 days elapsed, field says 50% done, but cable pulling, terminations and testing need 7 more days: total 12 days, 2 days late.",
                    "Quantified progress such as linear feet installed, cubic yards poured, equipment set.",
                    "Deleting activities, rebuilding logic, arbitrarily changing durations or moving dates by hand to make the schedule look better.",
                    "View → Tables → Variance (Finish Variance column)."
                ],
                "correctAnswer": 1,
                "explanation": "Quantified progress such as linear feet installed, cubic yards poured, equipment set.",
                "rawAnswer": "Quantified progress such as linear feet installed, cubic yards poured, equipment set."
            },
            {
                "id": "module-4-q54",
                "qNum": 54,
                "question": "What are the five questions a scheduler must ask for every in-progress activity?",
                "options": [
                    "No action; the software forecasts based on remaining logic.",
                    "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it.",
                    "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                    "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?"
                ],
                "correctAnswer": 3,
                "explanation": "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?",
                "rawAnswer": "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?"
            },
            {
                "id": "module-4-q55",
                "qNum": 55,
                "question": "What problems does question 5 cover?",
                "options": [
                    "Retained Logic honors the original logic (successor can't finish until the predecessor finishes): more conservative. Progress Override ignores the broken logic and forecasts from remaining duration only: more optimistic, may understate impact.",
                    "Pending RFIs, material delays, manpower issues, weather, design changes.",
                    "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report).",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration)."
                ],
                "correctAnswer": 1,
                "explanation": "Pending RFIs, material delays, manpower issues, weather, design changes.",
                "rawAnswer": "Pending RFIs, material delays, manpower issues, weather, design changes."
            },
            {
                "id": "module-4-q56",
                "qNum": 56,
                "question": "What documentation should the scheduler keep?",
                "options": [
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "Who they spoke with, the date, and what they were told, for every update.",
                    "Not Started, In Progress, Completed.",
                    "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete."
                ],
                "correctAnswer": 1,
                "explanation": "Who they spoke with, the date, and what they were told, for every update.",
                "rawAnswer": "Who they spoke with, the date, and what they were told, for every update."
            },
            {
                "id": "module-4-q57",
                "qNum": 57,
                "question": "In the Server Room A example, what are the baselines and field reports?",
                "options": [
                    "A Slab prep (Oct 1–5) done Oct 5; B Electrical rough-in (Oct 6–17) started Oct 6, RD 6d; C Mechanical rough-in (Oct 8–20) started Oct 9, RD 8d; D Equipment set (Oct 21–28) not started; E Energize & test (Oct 29–Nov 5) not started.",
                    "10 days → 6 → 2 → 0 (critical) over four updates.",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "Use Duration % Complete driven by Remaining Duration."
                ],
                "correctAnswer": 0,
                "explanation": "A Slab prep (Oct 1–5) done Oct 5; B Electrical rough-in (Oct 6–17) started Oct 6, RD 6d; C Mechanical rough-in (Oct 8–20) started Oct 9, RD 8d; D Equipment set (Oct 21–28) not started; E Energize & test (Oct 29–Nov 5) not started.",
                "rawAnswer": "A Slab prep (Oct 1–5) done Oct 5; B Electrical rough-in (Oct 6–17) started Oct 6, RD 6d; C Mechanical rough-in (Oct 8–20) started Oct 9, RD 8d; D Equipment set (Oct 21–28) not started; E Energize & test (Oct 29–Nov 5) not started."
            },
            {
                "id": "module-4-q58",
                "qNum": 58,
                "question": "What are the forecast finishes and variances after the update?",
                "options": [
                    "A Oct 5 (0); B Oct 22 (+3); C Oct 24 (+4); D Nov 1 (+4); E Nov 10 (+5).",
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                    "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                    "Telling the software what actually happened in the field since the last reporting period: actual starts/finishes, remaining durations, % complete."
                ],
                "correctAnswer": 0,
                "explanation": "A Oct 5 (0); B Oct 22 (+3); C Oct 24 (+4); D Nov 1 (+4); E Nov 10 (+5).",
                "rawAnswer": "A Oct 5 (0); B Oct 22 (+3); C Oct 24 (+4); D Nov 1 (+4); E Nov 10 (+5)."
            },
            {
                "id": "module-4-q59",
                "qNum": 59,
                "question": "What lesson does the example teach?",
                "options": [
                    "Current remaining durations hold, no new delays, and logic remains unchanged.",
                    "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                    "View → Tables → Variance (Finish Variance column).",
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage)."
                ],
                "correctAnswer": 3,
                "explanation": "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage).",
                "rawAnswer": "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage)."
            },
            {
                "id": "module-4-q60",
                "qNum": 60,
                "question": "What are the two bars on the Gantt?",
                "options": [
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now).",
                    "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required.",
                    "It destroys it.",
                    "No actual information; the activity sits entirely in the future."
                ],
                "correctAnswer": 0,
                "explanation": "The Baseline bar (planned) and the Current bar (where the schedule says you are now).",
                "rawAnswer": "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
            },
            {
                "id": "module-4-q61",
                "qNum": 61,
                "question": "What do aligned, right-extended and left-shortened bars mean?",
                "options": [
                    "No action; the software forecasts based on remaining logic.",
                    "The LGL Northstar Data Center Capital Project.",
                    "Just because you want the work done by that date, the contract requires it, someone says \"almost done,\" or the baseline shows that date.",
                    "Aligned: on schedule. Current ends later: delayed (positive finish variance, bad). Current ends earlier: ahead (negative variance, good)."
                ],
                "correctAnswer": 3,
                "explanation": "Aligned: on schedule. Current ends later: delayed (positive finish variance, bad). Current ends earlier: ahead (negative variance, good).",
                "rawAnswer": "Aligned: on schedule. Current ends later: delayed (positive finish variance, bad). Current ends earlier: ahead (negative variance, good)."
            },
            {
                "id": "module-4-q62",
                "qNum": 62,
                "question": "What is variance?",
                "options": [
                    "Elapsed days + Remaining Duration.",
                    "Originally: site grading → foundation → structural steel → envelope → MEP rough-in → equipment set → energize → substantial completion. After the Oct 15 update, electrical rough-in slipped 3 days, consumed its 2 days of float and now has negative float; the path runs through electrical rough-in → energize & test → substantial completion.",
                    "The difference between plan and reality; information, not a bad word.",
                    "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide."
                ],
                "correctAnswer": 2,
                "explanation": "The difference between plan and reality; information, not a bad word.",
                "rawAnswer": "The difference between plan and reality; information, not a bad word."
            },
            {
                "id": "module-4-q63",
                "qNum": 63,
                "question": "What are the three variance types?",
                "options": [
                    "Enter Actual Start and update Remaining Duration from the field.",
                    "Forecasts become unreliable.",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%)."
                ],
                "correctAnswer": 2,
                "explanation": "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration).",
                "rawAnswer": "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration)."
            },
            {
                "id": "module-4-q64",
                "qNum": 64,
                "question": "What do positive, negative and zero variance mean?",
                "options": [
                    "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                    "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?",
                    "Positive = late; negative = early; zero = on plan. This is universal in the industry.",
                    "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5."
                ],
                "correctAnswer": 2,
                "explanation": "Positive = late; negative = early; zero = on plan. This is universal in the industry.",
                "rawAnswer": "Positive = late; negative = early; zero = on plan. This is universal in the industry."
            },
            {
                "id": "module-4-q65",
                "qNum": 65,
                "question": "Baseline finish Nov 5, forecast Nov 12. What is the finish variance?",
                "options": [
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                    "Use Duration % Complete driven by Remaining Duration.",
                    "+7 days (7 days behind).",
                    "(1) Did it start? (2) When did it start? (3) Is it complete? (4) If not, how many days remain? (5) Any problem that could affect completion?"
                ],
                "correctAnswer": 2,
                "explanation": "+7 days (7 days behind).",
                "rawAnswer": "+7 days (7 days behind)."
            },
            {
                "id": "module-4-q66",
                "qNum": 66,
                "question": "What does a Duration Variance of +2 mean?",
                "options": [
                    "A Slab prep (Oct 1–5) done Oct 5; B Electrical rough-in (Oct 6–17) started Oct 6, RD 6d; C Mechanical rough-in (Oct 8–20) started Oct 9, RD 8d; D Equipment set (Oct 21–28) not started; E Energize & test (Oct 29–Nov 5) not started.",
                    "No actual information; the activity sits entirely in the future.",
                    "Baseline Start, Baseline Finish, Baseline Duration and Baseline Cost.",
                    "The activity is taking 2 days longer than planned."
                ],
                "correctAnswer": 3,
                "explanation": "The activity is taking 2 days longer than planned.",
                "rawAnswer": "The activity is taking 2 days longer than planned."
            },
            {
                "id": "module-4-q67",
                "qNum": 67,
                "question": "What is a forecast (weather analogy)?",
                "options": [
                    "Update the current schedule to show the delay, compare to the baseline, report the variance and forecast the new completion.",
                    "Forecasts become unreliable.",
                    "The best prediction given current information; it changes as conditions change.",
                    "How do we tell the schedule what actually happened, compare it with what we planned, and predict where the project will finish?"
                ],
                "correctAnswer": 2,
                "explanation": "The best prediction given current information; it changes as conditions change.",
                "rawAnswer": "The best prediction given current information; it changes as conditions change."
            },
            {
                "id": "module-4-q68",
                "qNum": 68,
                "question": "What is the Northstar forecast example?",
                "options": [
                    "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged.",
                    "Baseline completion Nov 5, forecast Nov 10, variance +5 working days.",
                    "+7 days (7 days behind).",
                    "Not Started: generator pad construction planned for November. In Progress: electrical rough-in started Oct 6. Completed: foundation pour started Oct 1, finished Oct 10."
                ],
                "correctAnswer": 1,
                "explanation": "Baseline completion Nov 5, forecast Nov 10, variance +5 working days.",
                "rawAnswer": "Baseline completion Nov 5, forecast Nov 10, variance +5 working days."
            },
            {
                "id": "module-4-q69",
                "qNum": 69,
                "question": "What does the forecast assume?",
                "options": [
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "Current remaining durations hold, no new delays, and logic remains unchanged.",
                    "Collect field data, enter it faithfully, recalculate, analyze and report honestly to management.",
                    "The number of more working days the field says it will take to finish an in-progress activity; it drives the forecast calculation."
                ],
                "correctAnswer": 1,
                "explanation": "Current remaining durations hold, no new delays, and logic remains unchanged.",
                "rawAnswer": "Current remaining durations hold, no new delays, and logic remains unchanged."
            },
            {
                "id": "module-4-q70",
                "qNum": 70,
                "question": "Is a changing forecast a failure?",
                "options": [
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                    "No; it's the system working correctly as new field information arrives.",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now)."
                ],
                "correctAnswer": 1,
                "explanation": "No; it's the system working correctly as new field information arrives.",
                "rawAnswer": "No; it's the system working correctly as new field information arrives."
            },
            {
                "id": "module-4-q71",
                "qNum": 71,
                "question": "Can the critical path change after an update?",
                "options": [
                    "Enter Actual Finish = Oct 10 (2 days late).",
                    "The date the last unit of work was physically completed and accepted; a fact, not a plan.",
                    "Baseline completion Nov 5, forecast Nov 10, variance +5 working days.",
                    "Yes. As activities slip, gain float or finish early, it can shift to a different chain."
                ],
                "correctAnswer": 3,
                "explanation": "Yes. As activities slip, gain float or finish early, it can shift to a different chain.",
                "rawAnswer": "Yes. As activities slip, gain float or finish early, it can shift to a different chain."
            },
            {
                "id": "module-4-q72",
                "qNum": 72,
                "question": "How did the critical path shift in the Northstar example?",
                "options": [
                    "Originally: site grading → foundation → structural steel → envelope → MEP rough-in → equipment set → energize → substantial completion. After the Oct 15 update, electrical rough-in slipped 3 days, consumed its 2 days of float and now has negative float; the path runs through electrical rough-in → energize & test → substantial completion.",
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                    "Recovery plans, acceleration and management decisions.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database."
                ],
                "correctAnswer": 0,
                "explanation": "Originally: site grading → foundation → structural steel → envelope → MEP rough-in → equipment set → energize → substantial completion. After the Oct 15 update, electrical rough-in slipped 3 days, consumed its 2 days of float and now has negative float; the path runs through electrical rough-in → energize & test → substantial completion.",
                "rawAnswer": "Originally: site grading → foundation → structural steel → envelope → MEP rough-in → equipment set → energize → substantial completion. After the Oct 15 update, electrical rough-in slipped 3 days, consumed its 2 days of float and now has negative float; the path runs through electrical rough-in → energize & test → substantial completion."
            },
            {
                "id": "module-4-q73",
                "qNum": 73,
                "question": "What should you do after the critical path changes?",
                "options": [
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                    "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9).",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged."
                ],
                "correctAnswer": 3,
                "explanation": "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged.",
                "rawAnswer": "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged."
            },
            {
                "id": "module-4-q74",
                "qNum": 74,
                "question": "What is float erosion?",
                "options": [
                    "The progressive reduction of float on an activity across successive updates.",
                    "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                    "No; it's the system working correctly as new field information arrives.",
                    "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm)."
                ],
                "correctAnswer": 0,
                "explanation": "The progressive reduction of float on an activity across successive updates.",
                "rawAnswer": "The progressive reduction of float on an activity across successive updates."
            },
            {
                "id": "module-4-q75",
                "qNum": 75,
                "question": "Describe the mechanical install float erosion sequence.",
                "options": [
                    "10 days → 6 → 2 → 0 (critical) over four updates.",
                    "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions.",
                    "The activity is taking 2 days longer than planned.",
                    "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5."
                ],
                "correctAnswer": 0,
                "explanation": "10 days → 6 → 2 → 0 (critical) over four updates.",
                "rawAnswer": "10 days → 6 → 2 → 0 (critical) over four updates."
            },
            {
                "id": "module-4-q76",
                "qNum": 76,
                "question": "How should float be tracked?",
                "options": [
                    "Forecasts become unreliable.",
                    "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                    "No action; the software forecasts based on remaining logic.",
                    "Use \"% Work Complete\" cautiously and validate with Remaining Duration."
                ],
                "correctAnswer": 1,
                "explanation": "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                "rawAnswer": "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero."
            },
            {
                "id": "module-4-q77",
                "qNum": 77,
                "question": "What is negative float?",
                "options": [
                    "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                    "The schedule can't meet the imposed date without a change: Required Finish − Calculated Finish.",
                    "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                    "Start Variance (Current Start − Baseline Start), Finish Variance (Current Finish − Baseline Finish), Duration Variance (Current Duration − Baseline Duration)."
                ],
                "correctAnswer": 1,
                "explanation": "The schedule can't meet the imposed date without a change: Required Finish − Calculated Finish.",
                "rawAnswer": "The schedule can't meet the imposed date without a change: Required Finish − Calculated Finish."
            },
            {
                "id": "module-4-q78",
                "qNum": 78,
                "question": "Northstar's contractual energization is Dec 1 but the forecast is Dec 10. What is the float?",
                "options": [
                    "10 days → 6 → 2 → 0 (critical) over four updates.",
                    "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work.",
                    "The Baseline bar (planned) and the Current bar (where the schedule says you are now).",
                    "−9 days."
                ],
                "correctAnswer": 3,
                "explanation": "−9 days.",
                "rawAnswer": "−9 days."
            },
            {
                "id": "module-4-q79",
                "qNum": 79,
                "question": "What actions follow negative float?",
                "options": [
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage).",
                    "Enter Actual Start and update Remaining Duration from the field.",
                    "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions.",
                    "No action; the software forecasts based on remaining logic."
                ],
                "correctAnswer": 2,
                "explanation": "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions.",
                "rawAnswer": "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions."
            },
            {
                "id": "module-4-q80",
                "qNum": 80,
                "question": "What should you check when negative float appears?",
                "options": [
                    "Actual Start known; Actual Finish not yet entered; Remaining Duration entered.",
                    "Superintendent, Project Manager, Subcontractors, Daily Reports, Progress Meetings, and Quantity Tracking.",
                    "A formal scope change approved by the owner, through proper change management procedures.",
                    "Whether it's caused by real delays or an incorrectly applied constraint."
                ],
                "correctAnswer": 3,
                "explanation": "Whether it's caused by real delays or an incorrectly applied constraint.",
                "rawAnswer": "Whether it's caused by real delays or an incorrectly applied constraint."
            },
            {
                "id": "module-4-q81",
                "qNum": 81,
                "question": "What is out-of-sequence progress?",
                "options": [
                    "−9 days.",
                    "Positive = late; negative = early; zero = on plan. This is universal in the industry.",
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                    "Only when the superintendent confirms the scope of work is 100% complete."
                ],
                "correctAnswer": 2,
                "explanation": "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                "rawAnswer": "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on."
            },
            {
                "id": "module-4-q82",
                "qNum": 82,
                "question": "What is the Northstar out-of-sequence example?",
                "options": [
                    "Actual Start and Remaining Duration = 3 days; leave Actual Finish blank.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database.",
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                    "\"Move end of completed parts after status date back to status date\" and \"move start of remaining parts back to status date\" must match the project's scheduling rules."
                ],
                "correctAnswer": 2,
                "explanation": "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                "rawAnswer": "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%)."
            },
            {
                "id": "module-4-q83",
                "qNum": 83,
                "question": "Compare Retained Logic and Progress Override.",
                "options": [
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "An activity records progress before its predecessor is complete, breaking the logic the schedule was built on.",
                    "Retained Logic honors the original logic (successor can't finish until the predecessor finishes): more conservative. Progress Override ignores the broken logic and forecasts from remaining duration only: more optimistic, may understate impact.",
                    "Whether it's caused by real delays or an incorrectly applied constraint."
                ],
                "correctAnswer": 2,
                "explanation": "Retained Logic honors the original logic (successor can't finish until the predecessor finishes): more conservative. Progress Override ignores the broken logic and forecasts from remaining duration only: more optimistic, may understate impact.",
                "rawAnswer": "Retained Logic honors the original logic (successor can't finish until the predecessor finishes): more conservative. Progress Override ignores the broken logic and forecasts from remaining duration only: more optimistic, may understate impact."
            },
            {
                "id": "module-4-q84",
                "qNum": 84,
                "question": "Which is the industry standard?",
                "options": [
                    "No; it's the system working correctly as new field information arrives.",
                    "Retained Logic on most capital projects, but confirm the project's scheduling specification.",
                    "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date.",
                    "Forecast: remaining work calculated by software; float, critical path and variance are measured here."
                ],
                "correctAnswer": 1,
                "explanation": "Retained Logic on most capital projects, but confirm the project's scheduling specification.",
                "rawAnswer": "Retained Logic on most capital projects, but confirm the project's scheduling specification."
            },
            {
                "id": "module-4-q85",
                "qNum": 85,
                "question": "What are steps 1–6 of the P6 update workflow?",
                "options": [
                    "The moment in time that divides the past from the future in the schedule; everything is interpreted relative to it.",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "Quantified progress such as linear feet installed, cubic yards poured, equipment set.",
                    "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration."
                ],
                "correctAnswer": 3,
                "explanation": "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration.",
                "rawAnswer": "(1) Confirm baseline is set (Project → Assign Baselines), (2) set the Data Date (Project → Set Data Date), (3) review activities due this period, (4) enter Actual Start, (5) enter Actual Finish for completed activities only, (6) update Remaining Duration."
            },
            {
                "id": "module-4-q86",
                "qNum": 86,
                "question": "What are steps 7–12?",
                "options": [
                    "Only when the superintendent confirms the scope of work is 100% complete.",
                    "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                    "It hides delay, destroys the performance record, makes the schedule useless as a control tool and prevents root-cause analysis. You'd lose the ability to measure variance.",
                    "Recovery plans, acceleration and management decisions."
                ],
                "correctAnswer": 1,
                "explanation": "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report.",
                "rawAnswer": "(7) Update % complete (Duration % Complete), (8) press F9 (Tools → Schedule), (9) review the Schedule Log for out-of-sequence, open ends and errors, (10) review the critical path (Total Float = 0), (11) review variance columns (BL Finish, Finish Variance), (12) save layout / export report."
            },
            {
                "id": "module-4-q87",
                "qNum": 87,
                "question": "What is the MSP equivalent of Data Date?",
                "options": [
                    "Status Date (Project → Project Information → Status Date).",
                    "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5.",
                    "Pending RFIs, material delays, manpower issues, weather, design changes.",
                    "\"Move end of completed parts after status date back to status date\" and \"move start of remaining parts back to status date\" must match the project's scheduling rules."
                ],
                "correctAnswer": 0,
                "explanation": "Status Date (Project → Project Information → Status Date).",
                "rawAnswer": "Status Date (Project → Project Information → Status Date)."
            },
            {
                "id": "module-4-q88",
                "qNum": 88,
                "question": "What are the MSP equivalents for Set Baseline, Actual Start/Finish and F9?",
                "options": [
                    "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9).",
                    "Forecast: remaining work calculated by software; float, critical path and variance are measured here.",
                    "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report).",
                    "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged."
                ],
                "correctAnswer": 0,
                "explanation": "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9).",
                "rawAnswer": "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9)."
            },
            {
                "id": "module-4-q89",
                "qNum": 89,
                "question": "Where do you view variance in MSP?",
                "options": [
                    "View → Tables → Variance (Finish Variance column).",
                    "They know what started, what finished and what's left.",
                    "Project → Set Baseline → Baseline; Task Information → Tracking tab; Calculate Project (automatic or F9).",
                    "50% done doesn't necessarily mean half the time remains; the remaining half may be harder work."
                ],
                "correctAnswer": 0,
                "explanation": "View → Tables → Variance (Finish Variance column).",
                "rawAnswer": "View → Tables → Variance (Finish Variance column)."
            },
            {
                "id": "module-4-q90",
                "qNum": 90,
                "question": "What MSP settings should be verified?",
                "options": [
                    "The new predicted completion date given current information.",
                    "The last day of the reporting period, e.g., every Friday or the last working day of the month.",
                    "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent.",
                    "\"Move end of completed parts after status date back to status date\" and \"move start of remaining parts back to status date\" must match the project's scheduling rules."
                ],
                "correctAnswer": 3,
                "explanation": "\"Move end of completed parts after status date back to status date\" and \"move start of remaining parts back to status date\" must match the project's scheduling rules.",
                "rawAnswer": "\"Move end of completed parts after status date back to status date\" and \"move start of remaining parts back to status date\" must match the project's scheduling rules."
            },
            {
                "id": "module-4-q91",
                "qNum": 91,
                "question": "What is a lookahead schedule?",
                "options": [
                    "Elapsed days + Remaining Duration.",
                    "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide.",
                    "Total = 12 days, a 2-day delay that cascades forward.",
                    "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent."
                ],
                "correctAnswer": 1,
                "explanation": "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide.",
                "rawAnswer": "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide."
            },
            {
                "id": "module-4-q92",
                "qNum": 92,
                "question": "What are the uses of 2-, 3- and 6-week lookaheads?",
                "options": [
                    "Track trends across updates, not just the current value; an activity losing 2–3 days per period needs attention before hitting zero.",
                    "Status records what actually happened this period (actuals); analysis compares current versus baseline to identify variance.",
                    "Actual information: Actual Start & Finish for completed activities, Actual Start & Remaining Duration for in-progress ones; no planned-only data.",
                    "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report)."
                ],
                "correctAnswer": 3,
                "explanation": "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report).",
                "rawAnswer": "2-week: daily field supervision and sub coordination (e.g., MEP trade coordination for Server Room A). 3-week: weekly progress meetings (OAC meeting). 6-week: management reporting and procurement planning (monthly owner report)."
            },
            {
                "id": "module-4-q93",
                "qNum": 93,
                "question": "Is the lookahead a separate schedule?",
                "options": [
                    "Forecast: remaining work calculated by software; float, critical path and variance are measured here.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database.",
                    "The difference between plan and reality; information, not a bad word.",
                    "A 3-day slip on Activity B cascades through logic and pushes the final milestone 5 days late (compounding slippage)."
                ],
                "correctAnswer": 1,
                "explanation": "No. It's a filtered, simplified view of the master schedule, generated from the same database.",
                "rawAnswer": "No. It's a filtered, simplified view of the master schedule, generated from the same database."
            },
            {
                "id": "module-4-q94",
                "qNum": 94,
                "question": "What six questions must a schedule narrative answer?",
                "options": [
                    "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                    "The best prediction given current information; it changes as conditions change.",
                    "A short-horizon extraction from the master schedule showing work expected to start or continue in the next 2, 3 or 6 weeks; the field team's guide.",
                    "Plan (leave 8am, arrive 6pm) → Baseline (commit to the plan) → Status (at noon you're in Pueblo) → Analyze (traffic made you late) → Forecast (new ETA 7:15pm)."
                ],
                "correctAnswer": 0,
                "explanation": "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                "rawAnswer": "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?"
            },
            {
                "id": "module-4-q95",
                "qNum": 95,
                "question": "What are the six parts of the Northstar narrative template?",
                "options": [
                    "Entering the baseline date because it \"should\" have started; entering a future date; leaving it blank when work has clearly begun.",
                    "What happened this period? What is behind schedule? What is critical now? What changed since last update? What happens next? What action is needed?",
                    "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required.",
                    "Yes. As activities slip, gain float or finish early, it can shift to a different chain."
                ],
                "correctAnswer": 2,
                "explanation": "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required.",
                "rawAnswer": "Period accomplishments, schedule status (forecast vs. baseline), critical path and total float, delays/concerns, 3-week lookahead, and actions required."
            },
            {
                "id": "module-4-q96",
                "qNum": 96,
                "question": "How does professional communication differ from unprofessional?",
                "options": [
                    "A wrong Actual Start cascades through all successor activities downstream. When in doubt, call the superintendent.",
                    "10 days → 6 → 2 → 0 (critical) over four updates.",
                    "Unprofessional: \"The project is delayed,\" \"Electrical is killing us.\" Professional: name the date, variance, cause and fix, e.g., forecast Nov 10, 5 working days later than the Nov 3 baseline, driven by switchgear delay.",
                    "No; it's the system working correctly as new field information arrives."
                ],
                "correctAnswer": 2,
                "explanation": "Unprofessional: \"The project is delayed,\" \"Electrical is killing us.\" Professional: name the date, variance, cause and fix, e.g., forecast Nov 10, 5 working days later than the Nov 3 baseline, driven by switchgear delay.",
                "rawAnswer": "Unprofessional: \"The project is delayed,\" \"Electrical is killing us.\" Professional: name the date, variance, cause and fix, e.g., forecast Nov 10, 5 working days later than the Nov 3 baseline, driven by switchgear delay."
            },
            {
                "id": "module-4-q97",
                "qNum": 97,
                "question": "What is the professional rule of communication?",
                "options": [
                    "Positive = late; negative = early; zero = on plan. This is universal in the industry.",
                    "Crew arrived and began forming the slab on Oct 1; electrician pulled the first conduit run on Oct 6; superintendent confirmed mobilization on that date.",
                    "Crew started cable tray installation before electrical rough-in finished (cable tray 30% while rough-in only 60%).",
                    "Name the date. Name the variance. Name the cause. Name the fix."
                ],
                "correctAnswer": 3,
                "explanation": "Name the date. Name the variance. Name the cause. Name the fix.",
                "rawAnswer": "Name the date. Name the variance. Name the cause. Name the fix."
            },
            {
                "id": "module-4-q98",
                "qNum": 98,
                "question": "List the update quality-check items.",
                "options": [
                    "The difference between plan and reality; information, not a bad word.",
                    "Notify management immediately, and review the critical path after every recalculation; never assume it's unchanged.",
                    "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                    "Status Date (Project → Project Information → Status Date)."
                ],
                "correctAnswer": 2,
                "explanation": "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data.",
                "rawAnswer": "Correct Data Date; Actual Starts and Finishes entered; RDs realistic and field-confirmed; no future actuals; no incomplete activities before the Data Date without explanation; critical path reviewed; float erosion identified; negative float investigated; variance report reviewed; out-of-sequence addressed; narrative matches data."
            },
            {
                "id": "module-4-q99",
                "qNum": 99,
                "question": "If a quality-check item fails, what do you do?",
                "options": [
                    "Positive = late; negative = early; zero = on plan. This is universal in the industry.",
                    "The activity is taking 2 days longer than planned.",
                    "Inform management immediately, develop a recovery plan (add resources, overtime, re-sequence, accelerate), and document the situation and recommended actions.",
                    "Stop and fix it before distributing; a wrong schedule is worse than no schedule."
                ],
                "correctAnswer": 3,
                "explanation": "Stop and fix it before distributing; a wrong schedule is worse than no schedule.",
                "rawAnswer": "Stop and fix it before distributing; a wrong schedule is worse than no schedule."
            },
            {
                "id": "module-4-q100",
                "qNum": 100,
                "question": "What are the multi-period trend items, professional obligations and the Oct 22 scenario tasks?",
                "options": [
                    "Whether it's caused by real delays or an incorrectly applied constraint.",
                    "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5.",
                    "No. It's a filtered, simplified view of the master schedule, generated from the same database.",
                    "Retained Logic on most capital projects, but confirm the project's scheduling specification."
                ],
                "correctAnswer": 1,
                "explanation": "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5.",
                "rawAnswer": "Trend: forecast date movement, critical-path float, count of late activities, float erosion, RD accuracy (e.g., variance grew 0 to +7 days over four periods). Obligations: report reality, be precise, document everything, serve the project team. Oct 22 exercise: enter actuals, set Data Date, recalculate, find the critical path change, create a 3-week lookahead and write the narrative (with one delayed activity +4d, one early −2d, one out-of-sequence); the advanced extension repeats for Oct 29 and Nov 5."
            }
        ]
    },
    {
        "id": "module-5",
        "aliases": [
            "health",
            "delay-analysis",
            "tia"
        ],
        "title": "Module 5: Schedule Health & Delay Analysis",
        "description": "Audit 8 health indicators, open ends, excessive lags/constraints, longest path, near-critical paths, fragnets, and Time Impact Analysis (TIA).",
        "project": "LGL Northstar Data Center Capital Project",
        "iconName": "ShieldCheck",
        "color": "#8B5CF6",
        "questions": [
            {
                "id": "module-5-q1",
                "qNum": 1,
                "question": "Why can a professional-looking schedule still be unreliable?",
                "options": [
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "Like a polished car with a broken engine, missing logic, bad calendars or forced constraints stay invisible until the schedule fails. An analyst must audit the logic, not just the report.",
                    "Built-in waiting time between two activities.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery)."
                ],
                "correctAnswer": 1,
                "explanation": "Like a polished car with a broken engine, missing logic, bad calendars or forced constraints stay invisible until the schedule fails. An analyst must audit the logic, not just the report.",
                "rawAnswer": "Like a polished car with a broken engine, missing logic, bad calendars or forced constraints stay invisible until the schedule fails. An analyst must audit the logic, not just the report."
            },
            {
                "id": "module-5-q2",
                "qNum": 2,
                "question": "What are the eight signs of a healthy schedule?",
                "options": [
                    "Owner-related, contractor-related, and third-party/external, plus concurrent delays.",
                    "Complete logic, reasonable durations, correct calendars, limited constraints, credible critical path, reasonable float, accurate progress, and no unexplained open ends.",
                    "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar.",
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize."
                ],
                "correctAnswer": 1,
                "explanation": "Complete logic, reasonable durations, correct calendars, limited constraints, credible critical path, reasonable float, accurate progress, and no unexplained open ends.",
                "rawAnswer": "Complete logic, reasonable durations, correct calendars, limited constraints, credible critical path, reasonable float, accurate progress, and no unexplained open ends."
            },
            {
                "id": "module-5-q3",
                "qNum": 3,
                "question": "What does \"complete logic\" mean?",
                "options": [
                    "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden.",
                    "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float.",
                    "No single activity spans an uncontrollable timeframe.",
                    "Every activity is connected by meaningful predecessor and successor links."
                ],
                "correctAnswer": 3,
                "explanation": "Every activity is connected by meaningful predecessor and successor links.",
                "rawAnswer": "Every activity is connected by meaningful predecessor and successor links."
            },
            {
                "id": "module-5-q4",
                "qNum": 4,
                "question": "What are \"reasonable durations\"?",
                "options": [
                    "No single activity spans an uncontrollable timeframe.",
                    "Every activity is connected by meaningful predecessor and successor links.",
                    "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15).",
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing."
                ],
                "correctAnswer": 0,
                "explanation": "No single activity spans an uncontrollable timeframe.",
                "rawAnswer": "No single activity spans an uncontrollable timeframe."
            },
            {
                "id": "module-5-q5",
                "qNum": 5,
                "question": "What does \"correct calendars\" require?",
                "options": [
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors.",
                    "Built-in waiting time between two activities.",
                    "Work schedules that reflect actual site and resource availability."
                ],
                "correctAnswer": 3,
                "explanation": "Work schedules that reflect actual site and resource availability.",
                "rawAnswer": "Work schedules that reflect actual site and resource availability."
            },
            {
                "id": "module-5-q6",
                "qNum": 6,
                "question": "What does \"limited constraints\" mean?",
                "options": [
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "Dates are driven by logic, not forced by manual overrides.",
                    "Float acts as a buffer between a delay event and its project impact.",
                    "The Northstar Data Center schedule audit (Section 25)."
                ],
                "correctAnswer": 1,
                "explanation": "Dates are driven by logic, not forced by manual overrides.",
                "rawAnswer": "Dates are driven by logic, not forced by manual overrides."
            },
            {
                "id": "module-5-q7",
                "qNum": 7,
                "question": "What is a credible critical path?",
                "options": [
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done.",
                    "It makes logical construction sense and isn't created by constraints or missing links.",
                    "Review all lags greater than 10 days and all activities with durations greater than 20 days."
                ],
                "correctAnswer": 2,
                "explanation": "It makes logical construction sense and isn't created by constraints or missing links.",
                "rawAnswer": "It makes logical construction sense and isn't created by constraints or missing links."
            },
            {
                "id": "module-5-q8",
                "qNum": 8,
                "question": "What is \"reasonable float\"?",
                "options": [
                    "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion.",
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "Float that reflects real schedule flexibility, not artificial numbers.",
                    "Near-critical: small non-zero float (typically 0–5 days), managed proactively. Fragnet: a self-contained mini-schedule for a delay event, inserted into the contemporaneous schedule and based on actual records. TIA: models the schedule impact by inserting a fragnet into the accepted update, recalculating and comparing dates (a modeled impact, not a legal conclusion)."
                ],
                "correctAnswer": 2,
                "explanation": "Float that reflects real schedule flexibility, not artificial numbers.",
                "rawAnswer": "Float that reflects real schedule flexibility, not artificial numbers."
            },
            {
                "id": "module-5-q9",
                "qNum": 9,
                "question": "What is \"accurate progress\"?",
                "options": [
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize.",
                    "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after).",
                    "Actual starts, finishes and percent complete entered correctly and consistently.",
                    "Gradual disappearance of float across update cycles; the trend matters more than any single value."
                ],
                "correctAnswer": 2,
                "explanation": "Actual starts, finishes and percent complete entered correctly and consistently.",
                "rawAnswer": "Actual starts, finishes and percent complete entered correctly and consistently."
            },
            {
                "id": "module-5-q10",
                "qNum": 10,
                "question": "What cascade follows poor schedule quality?",
                "options": [
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "The calculated completion date is later than the required or target date.",
                    "Actual durations exceeding planned, late starts, predecessor delays, and added scope without schedule updates.",
                    "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims."
                ],
                "correctAnswer": 3,
                "explanation": "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims.",
                "rawAnswer": "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims."
            },
            {
                "id": "module-5-q11",
                "qNum": 11,
                "question": "What happens when the critical path is wrong?",
                "options": [
                    "The IT Infrastructure path with 4 days of float should be on the daily watch list.",
                    "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                    "Management focuses resources on the wrong sequence and real bottlenecks go unnoticed.",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%."
                ],
                "correctAnswer": 2,
                "explanation": "Management focuses resources on the wrong sequence and real bottlenecks go unnoticed.",
                "rawAnswer": "Management focuses resources on the wrong sequence and real bottlenecks go unnoticed."
            },
            {
                "id": "module-5-q12",
                "qNum": 12,
                "question": "Which capstone audits all eight indicators?",
                "options": [
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims.",
                    "Use the \"Longest Path\" filter in the Activity view.",
                    "The Northstar Data Center schedule audit (Section 25)."
                ],
                "correctAnswer": 3,
                "explanation": "The Northstar Data Center schedule audit (Section 25).",
                "rawAnswer": "The Northstar Data Center schedule audit (Section 25)."
            },
            {
                "id": "module-5-q13",
                "qNum": 13,
                "question": "What is an open end?",
                "options": [
                    "An activity missing a predecessor or a successor.",
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float.",
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors."
                ],
                "correctAnswer": 0,
                "explanation": "An activity missing a predecessor or a successor.",
                "rawAnswer": "An activity missing a predecessor or a successor."
            },
            {
                "id": "module-5-q14",
                "qNum": 14,
                "question": "What is a predecessor open end?",
                "options": [
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery).",
                    "Actual starts, finishes and percent complete entered correctly and consistently."
                ],
                "correctAnswer": 1,
                "explanation": "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                "rawAnswer": "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct."
            },
            {
                "id": "module-5-q15",
                "qNum": 15,
                "question": "Give an example of a predecessor open end.",
                "options": [
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "\"Install UPS Systems\" starting on Day 1 even though the data center room isn't built.",
                    "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                    "Is the path logical? Does it make construction sense? Are activities connected correctly (relationship types)? Are constraints creating artificial criticality (test by removing them)?"
                ],
                "correctAnswer": 1,
                "explanation": "\"Install UPS Systems\" starting on Day 1 even though the data center room isn't built.",
                "rawAnswer": "\"Install UPS Systems\" starting on Day 1 even though the data center room isn't built."
            },
            {
                "id": "module-5-q16",
                "qNum": 16,
                "question": "What is a successor open end?",
                "options": [
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated.",
                    "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75.",
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified."
                ],
                "correctAnswer": 1,
                "explanation": "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated.",
                "rawAnswer": "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated."
            },
            {
                "id": "module-5-q17",
                "qNum": 17,
                "question": "Give an example of a successor open end.",
                "options": [
                    "Reviewers often confirm an activity exists and assume logic is correct, but these are different things.",
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "Dates are driven by logic, not forced by manual overrides.",
                    "\"Commission Cooling Tower\" with no link to project completion, so a 30-day delay appears to have zero impact."
                ],
                "correctAnswer": 3,
                "explanation": "\"Commission Cooling Tower\" with no link to project completion, so a 30-day delay appears to have zero impact.",
                "rawAnswer": "\"Commission Cooling Tower\" with no link to project completion, so a 30-day delay appears to have zero impact."
            },
            {
                "id": "module-5-q18",
                "qNum": 18,
                "question": "How do you detect open ends in P6 and MSP?",
                "options": [
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "Built-in waiting time between two activities.",
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "−14 days."
                ],
                "correctAnswer": 0,
                "explanation": "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                "rawAnswer": "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors."
            },
            {
                "id": "module-5-q19",
                "qNum": 19,
                "question": "What test question reveals an open end?",
                "options": [
                    "If Foundation Pour finishes late, does Structural Steel automatically move? Not if there's an open end.",
                    "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount.",
                    "Use the \"Longest Path\" filter in the Activity view.",
                    "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval."
                ],
                "correctAnswer": 0,
                "explanation": "If Foundation Pour finishes late, does Structural Steel automatically move? Not if there's an open end.",
                "rawAnswer": "If Foundation Pour finishes late, does Structural Steel automatically move? Not if there's an open end."
            },
            {
                "id": "module-5-q20",
                "qNum": 20,
                "question": "What is the difference between \"an activity exists\" and \"properly connected\"?",
                "options": [
                    "Reviewers often confirm an activity exists and assume logic is correct, but these are different things.",
                    "Dates are driven by logic, not forced by manual overrides.",
                    "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors."
                ],
                "correctAnswer": 0,
                "explanation": "Reviewers often confirm an activity exists and assume logic is correct, but these are different things.",
                "rawAnswer": "Reviewers often confirm an activity exists and assume logic is correct, but these are different things."
            },
            {
                "id": "module-5-q21",
                "qNum": 21,
                "question": "What is the example of missing logic?",
                "options": [
                    "Float acts as a buffer between a delay event and its project impact.",
                    "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done.",
                    "The schedule models reality; float changes and the critical path may shift.",
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing."
                ],
                "correctAnswer": 1,
                "explanation": "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done.",
                "rawAnswer": "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done."
            },
            {
                "id": "module-5-q22",
                "qNum": 22,
                "question": "What is the consequence and fix for that missing logic?",
                "options": [
                    "Dates are driven by logic, not forced by manual overrides.",
                    "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate.",
                    "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated.",
                    "An activity missing a predecessor or a successor."
                ],
                "correctAnswer": 1,
                "explanation": "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate.",
                "rawAnswer": "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate."
            },
            {
                "id": "module-5-q23",
                "qNum": 23,
                "question": "How do you identify missing logic?",
                "options": [
                    "(1) Check Data Date, (2) logic & open ends, (3) constraints, (4) lags & durations, (5) float, (6) critical/longest path, (7) progress & calendars, (8) document all findings in a written Schedule Health Report. (The capstone text refers to this as the full review workflow.)",
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing.",
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors."
                ],
                "correctAnswer": 1,
                "explanation": "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing.",
                "rawAnswer": "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing."
            },
            {
                "id": "module-5-q24",
                "qNum": 24,
                "question": "What is a dangling activity?",
                "options": [
                    "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done.",
                    "Use the \"Longest Path\" filter in the Activity view.",
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "One connected on only one side (a predecessor or a successor but not both), excluding project start and finish milestones."
                ],
                "correctAnswer": 3,
                "explanation": "One connected on only one side (a predecessor or a successor but not both), excluding project start and finish milestones.",
                "rawAnswer": "One connected on only one side (a predecessor or a successor but not both), excluding project start and finish milestones."
            },
            {
                "id": "module-5-q25",
                "qNum": 25,
                "question": "Why are dangling activities dangerous?",
                "options": [
                    "14 − 10 = 4 days of project impact.",
                    "Like a polished car with a broken engine, missing logic, bad calendars or forced constraints stay invisible until the schedule fails. An analyst must audit the logic, not just the report.",
                    "CPM results become unreliable and float artificially inflated; a dangling finish shows very high float.",
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources)."
                ],
                "correctAnswer": 2,
                "explanation": "CPM results become unreliable and float artificially inflated; a dangling finish shows very high float.",
                "rawAnswer": "CPM results become unreliable and float artificially inflated; a dangling finish shows very high float."
            },
            {
                "id": "module-5-q26",
                "qNum": 26,
                "question": "Northstar dangling example?",
                "options": [
                    "No.",
                    "If they diverge significantly, constraints or logic errors are likely distorting the critical path.",
                    "\"Generator Commissioning\" with no successor to \"Facility Acceptance,\" so its 45-day delay is invisible.",
                    "Owner-related, contractor-related, and third-party/external, plus concurrent delays."
                ],
                "correctAnswer": 2,
                "explanation": "\"Generator Commissioning\" with no successor to \"Facility Acceptance,\" so its 45-day delay is invisible.",
                "rawAnswer": "\"Generator Commissioning\" with no successor to \"Facility Acceptance,\" so its 45-day delay is invisible."
            },
            {
                "id": "module-5-q27",
                "qNum": 27,
                "question": "What is a constraint?",
                "options": [
                    "Activities should generally not exceed 20 working days (4 weeks) without documented justification and a measurement method.",
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                    "A manual override forcing an activity to a specific date, bypassing the network calculation.",
                    "Float acts as a buffer between a delay event and its project impact."
                ],
                "correctAnswer": 2,
                "explanation": "A manual override forcing an activity to a specific date, bypassing the network calculation.",
                "rawAnswer": "A manual override forcing an activity to a specific date, bypassing the network calculation."
            },
            {
                "id": "module-5-q28",
                "qNum": 28,
                "question": "How do constraints hide the critical path?",
                "options": [
                    "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate.",
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize.",
                    "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden.",
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors."
                ],
                "correctAnswer": 2,
                "explanation": "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden.",
                "rawAnswer": "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden."
            },
            {
                "id": "module-5-q29",
                "qNum": 29,
                "question": "When are constraints legitimate?",
                "options": [
                    "No.",
                    "Complete logic, reasonable durations, correct calendars, limited constraints, credible critical path, reasonable float, accurate progress, and no unexplained open ends.",
                    "Never; it should be rejected and returned for correction.",
                    "Regulatory deadlines, permit dates, contractual milestones. The problem is excessive or unexplained ones."
                ],
                "correctAnswer": 3,
                "explanation": "Regulatory deadlines, permit dates, contractual milestones. The problem is excessive or unexplained ones.",
                "rawAnswer": "Regulatory deadlines, permit dates, contractual milestones. The problem is excessive or unexplained ones."
            },
            {
                "id": "module-5-q30",
                "qNum": 30,
                "question": "What is the best practice for constraints?",
                "options": [
                    "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                    "Document every constraint with a reason and flag any not tied to a real contractual or regulatory requirement for removal or review.",
                    "Every activity is connected by meaningful predecessor and successor links.",
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing."
                ],
                "correctAnswer": 1,
                "explanation": "Document every constraint with a reason and flag any not tied to a real contractual or regulatory requirement for removal or review.",
                "rawAnswer": "Document every constraint with a reason and flag any not tied to a real contractual or regulatory requirement for removal or review."
            },
            {
                "id": "module-5-q31",
                "qNum": 31,
                "question": "What is lag?",
                "options": [
                    "MEP at 4 days of float is one small delay from becoming critical with no plan in place.",
                    "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                    "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                    "Built-in waiting time between two activities."
                ],
                "correctAnswer": 3,
                "explanation": "Built-in waiting time between two activities.",
                "rawAnswer": "Built-in waiting time between two activities."
            },
            {
                "id": "module-5-q32",
                "qNum": 32,
                "question": "Give an example of legitimate lag.",
                "options": [
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                    "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after).",
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "Highway = critical path (0 float), main road = near-critical, side street = high-float path."
                ],
                "correctAnswer": 0,
                "explanation": "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                "rawAnswer": "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources)."
            },
            {
                "id": "module-5-q33",
                "qNum": 33,
                "question": "Give an example of excessive lag.",
                "options": [
                    "Complete Design Package FS+60d → Begin Procurement with no explanation; likely hidden review cycles, approvals and submittals.",
                    "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                    "(1) Audit the schedule, (2) correct logic defects, (3) identify critical and near-critical paths, (4) build the fragnet (Design Revision 5d → Owner Review 8d → Revised Procurement 12d), (5) perform a basic TIA, (6) document findings in a Schedule Health Report, (7) one-page management summary.",
                    "Rough-in conduit (15d), wire pull (20d), panel installation (10d), device installation (15d), circuit testing (25d)."
                ],
                "correctAnswer": 0,
                "explanation": "Complete Design Package FS+60d → Begin Procurement with no explanation; likely hidden review cycles, approvals and submittals.",
                "rawAnswer": "Complete Design Package FS+60d → Begin Procurement with no explanation; likely hidden review cycles, approvals and submittals."
            },
            {
                "id": "module-5-q34",
                "qNum": 34,
                "question": "How should excessive lag be fixed?",
                "options": [
                    "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval).",
                    "Float that reflects real schedule flexibility, not artificial numbers.",
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after)."
                ],
                "correctAnswer": 0,
                "explanation": "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval).",
                "rawAnswer": "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval)."
            },
            {
                "id": "module-5-q35",
                "qNum": 35,
                "question": "What is the result of converting lag to activities?",
                "options": [
                    "The schedule models reality; float changes and the critical path may shift.",
                    "\"Commission Cooling Tower\" with no link to project completion, so a 30-day delay appears to have zero impact.",
                    "The calculated completion date is later than the required or target date.",
                    "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact."
                ],
                "correctAnswer": 0,
                "explanation": "The schedule models reality; float changes and the critical path may shift.",
                "rawAnswer": "The schedule models reality; float changes and the critical path may shift."
            },
            {
                "id": "module-5-q36",
                "qNum": 36,
                "question": "Why are 120-day activities uncontrollable?",
                "options": [
                    "The affected activity was on the critical path with zero float, so nothing absorbed it.",
                    "Actual starts, finishes and percent complete entered correctly and consistently.",
                    "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims.",
                    "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float."
                ],
                "correctAnswer": 3,
                "explanation": "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float.",
                "rawAnswer": "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float."
            },
            {
                "id": "module-5-q37",
                "qNum": 37,
                "question": "What example decomposes \"Electrical Work: 120 days\"?",
                "options": [
                    "No.",
                    "Rough-in conduit (15d), wire pull (20d), panel installation (10d), device installation (15d), circuit testing (25d).",
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15)."
                ],
                "correctAnswer": 1,
                "explanation": "Rough-in conduit (15d), wire pull (20d), panel installation (10d), device installation (15d), circuit testing (25d).",
                "rawAnswer": "Rough-in conduit (15d), wire pull (20d), panel installation (10d), device installation (15d), circuit testing (25d)."
            },
            {
                "id": "module-5-q38",
                "qNum": 38,
                "question": "What is the recommended duration threshold?",
                "options": [
                    "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval.",
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                    "Every activity is connected by meaningful predecessor and successor links.",
                    "Activities should generally not exceed 20 working days (4 weeks) without documented justification and a measurement method."
                ],
                "correctAnswer": 3,
                "explanation": "Activities should generally not exceed 20 working days (4 weeks) without documented justification and a measurement method.",
                "rawAnswer": "Activities should generally not exceed 20 working days (4 weeks) without documented justification and a measurement method."
            },
            {
                "id": "module-5-q39",
                "qNum": 39,
                "question": "What lesson does a hidden 3-week panel delay teach?",
                "options": [
                    "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after).",
                    "It stays invisible inside a 120-day activity until it's almost too late to recover.",
                    "It makes logical construction sense and isn't created by constraints or missing links.",
                    "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval)."
                ],
                "correctAnswer": 1,
                "explanation": "It stays invisible inside a 120-day activity until it's almost too late to recover.",
                "rawAnswer": "It stays invisible inside a 120-day activity until it's almost too late to recover."
            },
            {
                "id": "module-5-q40",
                "qNum": 40,
                "question": "How many activities was \"Electrical Systems 150d\" split into in the audit, and what was found?",
                "options": [
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "−14 days.",
                    "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar.",
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation."
                ],
                "correctAnswer": 2,
                "explanation": "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar.",
                "rawAnswer": "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar."
            },
            {
                "id": "module-5-q41",
                "qNum": 41,
                "question": "What is negative float?",
                "options": [
                    "Document every constraint with a reason and flag any not tied to a real contractual or regulatory requirement for removal or review.",
                    "Activities should generally not exceed 20 working days (4 weeks) without documented justification and a measurement method.",
                    "The calculated completion date is later than the required or target date.",
                    "Complete logic, reasonable durations, correct calendars, limited constraints, credible critical path, reasonable float, accurate progress, and no unexplained open ends."
                ],
                "correctAnswer": 2,
                "explanation": "The calculated completion date is later than the required or target date.",
                "rawAnswer": "The calculated completion date is later than the required or target date."
            },
            {
                "id": "module-5-q42",
                "qNum": 42,
                "question": "What is the formula?",
                "options": [
                    "The IT Infrastructure path with 4 days of float should be on the daily watch list.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery).",
                    "Total Float = Required Date − Calculated Date (negative if the calculated date is later).",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
                ],
                "correctAnswer": 2,
                "explanation": "Total Float = Required Date − Calculated Date (negative if the calculated date is later).",
                "rawAnswer": "Total Float = Required Date − Calculated Date (negative if the calculated date is later)."
            },
            {
                "id": "module-5-q43",
                "qNum": 43,
                "question": "Required March 15, calculated March 29. What is the float?",
                "options": [
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact.",
                    "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list.",
                    "−14 days."
                ],
                "correctAnswer": 3,
                "explanation": "−14 days.",
                "rawAnswer": "−14 days."
            },
            {
                "id": "module-5-q44",
                "qNum": 44,
                "question": "Why must negative float be investigated?",
                "options": [
                    "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75.",
                    "Highway = critical path (0 float), main road = near-critical, side street = high-float path.",
                    "The required date may be wrong (a placeholder), the logic may be wrong (missing predecessors/excessive constraints), or it may be genuine and need a recovery plan.",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%."
                ],
                "correctAnswer": 2,
                "explanation": "The required date may be wrong (a placeholder), the logic may be wrong (missing predecessors/excessive constraints), or it may be genuine and need a recovery plan.",
                "rawAnswer": "The required date may be wrong (a placeholder), the logic may be wrong (missing predecessors/excessive constraints), or it may be genuine and need a recovery plan."
            },
            {
                "id": "module-5-q45",
                "qNum": 45,
                "question": "Should a schedule with unexplained negative float be accepted?",
                "options": [
                    "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate.",
                    "Never; it should be rejected and returned for correction.",
                    "Regulatory deadlines, permit dates, contractual milestones. The problem is excessive or unexplained ones.",
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update."
                ],
                "correctAnswer": 1,
                "explanation": "Never; it should be rejected and returned for correction.",
                "rawAnswer": "Never; it should be rejected and returned for correction."
            },
            {
                "id": "module-5-q46",
                "qNum": 46,
                "question": "What is the critical path?",
                "options": [
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount.",
                    "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                    "Owner-related, contractor-related, and third-party/external, plus concurrent delays."
                ],
                "correctAnswer": 1,
                "explanation": "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount.",
                "rawAnswer": "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount."
            },
            {
                "id": "module-5-q47",
                "qNum": 47,
                "question": "What four questions should you ask about any critical path?",
                "options": [
                    "Is the path logical? Does it make construction sense? Are activities connected correctly (relationship types)? Are constraints creating artificial criticality (test by removing them)?",
                    "One connected on only one side (a predecessor or a successor but not both), excluding project start and finish milestones.",
                    "Owner-related, contractor-related, and third-party/external, plus concurrent delays.",
                    "Missing logic < 5% of activities; hard constraints < 5%."
                ],
                "correctAnswer": 0,
                "explanation": "Is the path logical? Does it make construction sense? Are activities connected correctly (relationship types)? Are constraints creating artificial criticality (test by removing them)?",
                "rawAnswer": "Is the path logical? Does it make construction sense? Are activities connected correctly (relationship types)? Are constraints creating artificial criticality (test by removing them)?"
            },
            {
                "id": "module-5-q48",
                "qNum": 48,
                "question": "What is the longest path?",
                "options": [
                    "A manual override forcing an activity to a specific date, bypassing the network calculation.",
                    "Finish-to-Start > 90%; high float (> 44 working days) < 5%.",
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75."
                ],
                "correctAnswer": 3,
                "explanation": "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75.",
                "rawAnswer": "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75."
            },
            {
                "id": "module-5-q49",
                "qNum": 49,
                "question": "Compare critical path and longest path.",
                "options": [
                    "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors.",
                    "Follow a structured workflow: confirm the data date, audit logic/open ends, review constraints with documentation, evaluate lags for hidden work, check durations against the 20-day threshold, analyze float trends, compare critical path to longest path, validate progress against field reports, and prepare a written Schedule Health Report.",
                    "Dates are driven by logic, not forced by manual overrides.",
                    "Finish Date After Fragnet − Finish Date Before Fragnet."
                ],
                "correctAnswer": 0,
                "explanation": "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors.",
                "rawAnswer": "Critical path is float-based (zero total float) and can be distorted by constraints or missing logic. Longest path is duration-based and more resistant to errors."
            },
            {
                "id": "module-5-q50",
                "qNum": 50,
                "question": "How do you display the longest path in P6?",
                "options": [
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                    "(1) Audit the schedule, (2) correct logic defects, (3) identify critical and near-critical paths, (4) build the fragnet (Design Revision 5d → Owner Review 8d → Revised Procurement 12d), (5) perform a basic TIA, (6) document findings in a Schedule Health Report, (7) one-page management summary.",
                    "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar.",
                    "Use the \"Longest Path\" filter in the Activity view."
                ],
                "correctAnswer": 3,
                "explanation": "Use the \"Longest Path\" filter in the Activity view.",
                "rawAnswer": "Use the \"Longest Path\" filter in the Activity view."
            },
            {
                "id": "module-5-q51",
                "qNum": 51,
                "question": "Why compare critical path to longest path?",
                "options": [
                    "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact.",
                    "If they diverge significantly, constraints or logic errors are likely distorting the critical path.",
                    "Gradual disappearance of float across update cycles; the trend matters more than any single value.",
                    "Eight activities; three had zero float and were on the critical path, previously hidden inside the single bar."
                ],
                "correctAnswer": 1,
                "explanation": "If they diverge significantly, constraints or logic errors are likely distorting the critical path.",
                "rawAnswer": "If they diverge significantly, constraints or logic errors are likely distorting the critical path."
            },
            {
                "id": "module-5-q52",
                "qNum": 52,
                "question": "What is a near-critical path?",
                "options": [
                    "MEP at 4 days of float is one small delay from becoming critical with no plan in place.",
                    "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified."
                ],
                "correctAnswer": 1,
                "explanation": "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                "rawAnswer": "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path."
            },
            {
                "id": "module-5-q53",
                "qNum": 53,
                "question": "What are the Northstar float thresholds?",
                "options": [
                    "It makes logical construction sense and isn't created by constraints or missing links.",
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "Model, quantify and document; not assign legal responsibility (that belongs to legal counsel and project management).",
                    "Finish-to-Start > 90%; high float (> 44 working days) < 5%."
                ],
                "correctAnswer": 1,
                "explanation": "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                "rawAnswer": "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update."
            },
            {
                "id": "module-5-q54",
                "qNum": 54,
                "question": "Why does near-critical matter?",
                "options": [
                    "The IT Infrastructure path with 4 days of float should be on the daily watch list.",
                    "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                    "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden.",
                    "Actual starts, finishes and percent complete entered correctly and consistently."
                ],
                "correctAnswer": 1,
                "explanation": "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                "rawAnswer": "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too."
            },
            {
                "id": "module-5-q55",
                "qNum": 55,
                "question": "What is the Northstar near-critical example?",
                "options": [
                    "20 → 14 → 8 → 3 → 0 (about 5 days lost per cycle). Flag for investigation when float drops by more than 25% in a single update cycle.",
                    "(1) Start with the accepted schedule, (2) identify the delay event, (3) build the fragnet, (4) insert at the correct point, (5) recalculate, (6) compare before and after, (7) document the result.",
                    "Use the \"Longest Path\" filter in the Activity view.",
                    "The IT Infrastructure path with 4 days of float should be on the daily watch list."
                ],
                "correctAnswer": 3,
                "explanation": "The IT Infrastructure path with 4 days of float should be on the daily watch list.",
                "rawAnswer": "The IT Infrastructure path with 4 days of float should be on the daily watch list."
            },
            {
                "id": "module-5-q56",
                "qNum": 56,
                "question": "What is float erosion?",
                "options": [
                    "An activity missing a predecessor or a successor.",
                    "The required date may be wrong (a placeholder), the logic may be wrong (missing predecessors/excessive constraints), or it may be genuine and need a recovery plan.",
                    "Gradual disappearance of float across update cycles; the trend matters more than any single value.",
                    "The most recently approved schedule update, in place when the delay occurred."
                ],
                "correctAnswer": 2,
                "explanation": "Gradual disappearance of float across update cycles; the trend matters more than any single value.",
                "rawAnswer": "Gradual disappearance of float across update cycles; the trend matters more than any single value."
            },
            {
                "id": "module-5-q57",
                "qNum": 57,
                "question": "Give the float erosion example and trigger.",
                "options": [
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified.",
                    "The sequence with the greatest total duration from start to finish, e.g., Mechanical & Electrical at 110 days vs. Civil & Structural 90 and IT & Commissioning 75.",
                    "Complete Design Package FS+60d → Begin Procurement with no explanation; likely hidden review cycles, approvals and submittals.",
                    "20 → 14 → 8 → 3 → 0 (about 5 days lost per cycle). Flag for investigation when float drops by more than 25% in a single update cycle."
                ],
                "correctAnswer": 3,
                "explanation": "20 → 14 → 8 → 3 → 0 (about 5 days lost per cycle). Flag for investigation when float drops by more than 25% in a single update cycle.",
                "rawAnswer": "20 → 14 → 8 → 3 → 0 (about 5 days lost per cycle). Flag for investigation when float drops by more than 25% in a single update cycle."
            },
            {
                "id": "module-5-q58",
                "qNum": 58,
                "question": "What causes float erosion?",
                "options": [
                    "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                    "Actual durations exceeding planned, late starts, predecessor delays, and added scope without schedule updates.",
                    "If Foundation Pour finishes late, does Structural Steel automatically move? Not if there's an open end."
                ],
                "correctAnswer": 2,
                "explanation": "Actual durations exceeding planned, late starts, predecessor delays, and added scope without schedule updates.",
                "rawAnswer": "Actual durations exceeding planned, late starts, predecessor delays, and added scope without schedule updates."
            },
            {
                "id": "module-5-q59",
                "qNum": 59,
                "question": "What is the road network analogy?",
                "options": [
                    "Never; it should be rejected and returned for correction.",
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "Highway = critical path (0 float), main road = near-critical, side street = high-float path.",
                    "The Northstar Data Center schedule audit (Section 25)."
                ],
                "correctAnswer": 2,
                "explanation": "Highway = critical path (0 float), main road = near-critical, side street = high-float path.",
                "rawAnswer": "Highway = critical path (0 float), main road = near-critical, side street = high-float path."
            },
            {
                "id": "module-5-q60",
                "qNum": 60,
                "question": "In Northstar's example, what floats do the paths have?",
                "options": [
                    "Every activity is connected by meaningful predecessor and successor links.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                    "Civil & Structure: 0 days (critical); MEP Systems: 4 days (near-critical); IT Infrastructure: 18 days (monitoring).",
                    "(1) Start with the accepted schedule, (2) identify the delay event, (3) build the fragnet, (4) insert at the correct point, (5) recalculate, (6) compare before and after, (7) document the result."
                ],
                "correctAnswer": 2,
                "explanation": "Civil & Structure: 0 days (critical); MEP Systems: 4 days (near-critical); IT Infrastructure: 18 days (monitoring).",
                "rawAnswer": "Civil & Structure: 0 days (critical); MEP Systems: 4 days (near-critical); IT Infrastructure: 18 days (monitoring)."
            },
            {
                "id": "module-5-q61",
                "qNum": 61,
                "question": "What risk arises from managing only the critical path?",
                "options": [
                    "The affected activity was on the critical path with zero float, so nothing absorbed it.",
                    "MEP at 4 days of float is one small delay from becoming critical with no plan in place.",
                    "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list.",
                    "Civil & Structure: 0 days (critical); MEP Systems: 4 days (near-critical); IT Infrastructure: 18 days (monitoring)."
                ],
                "correctAnswer": 1,
                "explanation": "MEP at 4 days of float is one small delay from becoming critical with no plan in place.",
                "rawAnswer": "MEP at 4 days of float is one small delay from becoming critical with no plan in place."
            },
            {
                "id": "module-5-q62",
                "qNum": 62,
                "question": "What is a delay?",
                "options": [
                    "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated.",
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified.",
                    "An activity with no predecessor; it can start any time, giving the CPM engine unlimited early-start flexibility, which is almost never correct.",
                    "An event or condition that affects the planned timing of work."
                ],
                "correctAnswer": 3,
                "explanation": "An event or condition that affects the planned timing of work.",
                "rawAnswer": "An event or condition that affects the planned timing of work."
            },
            {
                "id": "module-5-q63",
                "qNum": 63,
                "question": "What are the three levels of understanding delay?",
                "options": [
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery).",
                    "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion.",
                    "An activity missing a predecessor or a successor.",
                    "Total Float = Required Date − Calculated Date (negative if the calculated date is later)."
                ],
                "correctAnswer": 0,
                "explanation": "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery).",
                "rawAnswer": "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery)."
            },
            {
                "id": "module-5-q64",
                "qNum": 64,
                "question": "Give the three-level example.",
                "options": [
                    "Transformer delivered late (event); it delays Generator Installation start by 14 days (schedule impact); with 10 days of float, project completion moves 4 days (project impact).",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                    "Document every constraint with a reason and flag any not tied to a real contractual or regulatory requirement for removal or review.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery)."
                ],
                "correctAnswer": 0,
                "explanation": "Transformer delivered late (event); it delays Generator Installation start by 14 days (schedule impact); with 10 days of float, project completion moves 4 days (project impact).",
                "rawAnswer": "Transformer delivered late (event); it delays Generator Installation start by 14 days (schedule impact); with 10 days of float, project completion moves 4 days (project impact)."
            },
            {
                "id": "module-5-q65",
                "qNum": 65,
                "question": "In the simple delay example, delivery was due June 1 and arrived June 15. What is the project impact if float is 10 days?",
                "options": [
                    "14 − 10 = 4 days of project impact.",
                    "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                    "Complete Design Package FS+60d → Begin Procurement with no explanation; likely hidden review cycles, approvals and submittals.",
                    "Actual starts, finishes and percent complete entered correctly and consistently."
                ],
                "correctAnswer": 0,
                "explanation": "14 − 10 = 4 days of project impact.",
                "rawAnswer": "14 − 10 = 4 days of project impact."
            },
            {
                "id": "module-5-q66",
                "qNum": 66,
                "question": "What questions determine whether a delay affects the finish?",
                "options": [
                    "Civil & Structure: 0 days (critical); MEP Systems: 4 days (near-critical); IT Infrastructure: 18 days (monitoring).",
                    "Is the activity critical? How much float does it have? Are other paths also delayed (concurrent delays)?",
                    "The Oct 15 update must itself be a healthy, approved schedule; a flawed schedule gives flawed results.",
                    "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15)."
                ],
                "correctAnswer": 1,
                "explanation": "Is the activity critical? How much float does it have? Are other paths also delayed (concurrent delays)?",
                "rawAnswer": "Is the activity critical? How much float does it have? Are other paths also delayed (concurrent delays)?"
            },
            {
                "id": "module-5-q67",
                "qNum": 67,
                "question": "What is the fundamental logic behind delay analysis?",
                "options": [
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                    "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount.",
                    "Float acts as a buffer between a delay event and its project impact."
                ],
                "correctAnswer": 3,
                "explanation": "Float acts as a buffer between a delay event and its project impact.",
                "rawAnswer": "Float acts as a buffer between a delay event and its project impact."
            },
            {
                "id": "module-5-q68",
                "qNum": 68,
                "question": "Should you assume a 14-day delay event equals a 14-day project delay?",
                "options": [
                    "Rough-in conduit (15d), wire pull (20d), panel installation (10d), device installation (15d), circuit testing (25d).",
                    "No.",
                    "Pour Concrete Equipment Pads FS+7d → Install Cooling Equipment (cure time that can't be shortened with resources).",
                    "Actual durations exceeding planned, late starts, predecessor delays, and added scope without schedule updates."
                ],
                "correctAnswer": 1,
                "explanation": "No.",
                "rawAnswer": "No."
            },
            {
                "id": "module-5-q69",
                "qNum": 69,
                "question": "What delay types are classified?",
                "options": [
                    "14 − 10 = 4 days of project impact.",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%.",
                    "Finish-to-Start > 90%; high float (> 44 working days) < 5%.",
                    "Owner-related, contractor-related, and third-party/external, plus concurrent delays."
                ],
                "correctAnswer": 3,
                "explanation": "Owner-related, contractor-related, and third-party/external, plus concurrent delays.",
                "rawAnswer": "Owner-related, contractor-related, and third-party/external, plus concurrent delays."
            },
            {
                "id": "module-5-q70",
                "qNum": 70,
                "question": "Give examples of each delay type.",
                "options": [
                    "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                    "\"Install UPS Systems\" starting on Day 1 even though the data center room isn't built."
                ],
                "correctAnswer": 0,
                "explanation": "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                "rawAnswer": "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure."
            },
            {
                "id": "module-5-q71",
                "qNum": 71,
                "question": "What is the analyst's role with concurrent delays?",
                "options": [
                    "Model, quantify and document; not assign legal responsibility (that belongs to legal counsel and project management).",
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "\"Commission Cooling Tower\" with no link to project completion, so a 30-day delay appears to have zero impact.",
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing."
                ],
                "correctAnswer": 0,
                "explanation": "Model, quantify and document; not assign legal responsibility (that belongs to legal counsel and project management).",
                "rawAnswer": "Model, quantify and document; not assign legal responsibility (that belongs to legal counsel and project management)."
            },
            {
                "id": "module-5-q72",
                "qNum": 72,
                "question": "What is a fragnet?",
                "options": [
                    "A manual override forcing an activity to a specific date, bypassing the network calculation.",
                    "\"Generator Commissioning\" with no successor to \"Facility Acceptance,\" so its 45-day delay is invisible.",
                    "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact.",
                    "Finish Date After Fragnet − Finish Date Before Fragnet."
                ],
                "correctAnswer": 2,
                "explanation": "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact.",
                "rawAnswer": "A fragment network: a small, self-contained network of activities representing a specific delay event, change or impact."
            },
            {
                "id": "module-5-q73",
                "qNum": 73,
                "question": "Give the fragnet example.",
                "options": [
                    "The IT Infrastructure path with 4 days of float should be on the daily watch list.",
                    "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery)."
                ],
                "correctAnswer": 2,
                "explanation": "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                "rawAnswer": "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred."
            },
            {
                "id": "module-5-q74",
                "qNum": 74,
                "question": "What are the three fragnet principles?",
                "options": [
                    "It stays invisible inside a 120-day activity until it's almost too late to recover.",
                    "Finish Date After Fragnet − Finish Date Before Fragnet.",
                    "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion.",
                    "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after)."
                ],
                "correctAnswer": 3,
                "explanation": "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after).",
                "rawAnswer": "It models real work (documented from actual records), inserts at the correct point, and is isolated and measurable (impact = completion date difference before/after)."
            },
            {
                "id": "module-5-q75",
                "qNum": 75,
                "question": "What is Time Impact Analysis (TIA)?",
                "options": [
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "The industry-standard method for measuring the potential schedule impact of a delay or change by inserting a fragnet into the contemporaneous schedule.",
                    "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval)."
                ],
                "correctAnswer": 2,
                "explanation": "The industry-standard method for measuring the potential schedule impact of a delay or change by inserting a fragnet into the contemporaneous schedule.",
                "rawAnswer": "The industry-standard method for measuring the potential schedule impact of a delay or change by inserting a fragnet into the contemporaneous schedule."
            },
            {
                "id": "module-5-q76",
                "qNum": 76,
                "question": "What are the seven TIA steps?",
                "options": [
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                    "The Oct 15 update must itself be a healthy, approved schedule; a flawed schedule gives flawed results.",
                    "(1) Start with the accepted schedule, (2) identify the delay event, (3) build the fragnet, (4) insert at the correct point, (5) recalculate, (6) compare before and after, (7) document the result."
                ],
                "correctAnswer": 3,
                "explanation": "(1) Start with the accepted schedule, (2) identify the delay event, (3) build the fragnet, (4) insert at the correct point, (5) recalculate, (6) compare before and after, (7) document the result.",
                "rawAnswer": "(1) Start with the accepted schedule, (2) identify the delay event, (3) build the fragnet, (4) insert at the correct point, (5) recalculate, (6) compare before and after, (7) document the result."
            },
            {
                "id": "module-5-q77",
                "qNum": 77,
                "question": "What is the contemporaneous schedule?",
                "options": [
                    "It makes logical construction sense and isn't created by constraints or missing links.",
                    "An activity missing a predecessor or a successor.",
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize."
                ],
                "correctAnswer": 2,
                "explanation": "The most recently approved schedule update, in place when the delay occurred.",
                "rawAnswer": "The most recently approved schedule update, in place when the delay occurred."
            },
            {
                "id": "module-5-q78",
                "qNum": 78,
                "question": "What is a TIA result?",
                "options": [
                    "A modeled potential impact that must consider float, concurrent activities and recovery work before stating a final position.",
                    "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims.",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%.",
                    "The affected activity was on the critical path with zero float, so nothing absorbed it."
                ],
                "correctAnswer": 0,
                "explanation": "A modeled potential impact that must consider float, concurrent activities and recovery work before stating a final position.",
                "rawAnswer": "A modeled potential impact that must consider float, concurrent activities and recovery work before stating a final position."
            },
            {
                "id": "module-5-q79",
                "qNum": 79,
                "question": "What is TIA NOT?",
                "options": [
                    "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                    "The calculated completion date is later than the required or target date.",
                    "−14 days.",
                    "Total Float = Required Date − Calculated Date (negative if the calculated date is later)."
                ],
                "correctAnswer": 0,
                "explanation": "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation.",
                "rawAnswer": "Not a legal determination of fault, not a claim (it's schedule evidence), not valid on a flawed baseline, and not complete without contemporaneous documentation."
            },
            {
                "id": "module-5-q80",
                "qNum": 80,
                "question": "Describe the Northstar TIA result.",
                "options": [
                    "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15).",
                    "Identify the lag, ask what work is hidden, and replace it with real, linked activities (e.g., Review → Owner Approval → Resubmittal → Final Approval).",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%.",
                    "Reviewers often confirm an activity exists and assume logic is correct, but these are different things."
                ],
                "correctAnswer": 0,
                "explanation": "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15).",
                "rawAnswer": "A generator room design revision fragnet, Design Revision (5d) → Owner Approval (10d) → Rework (8d), inserted on the critical path: project finish moved from Dec 10 to Dec 18, +8 calendar days (Data Date Oct 15)."
            },
            {
                "id": "module-5-q81",
                "qNum": 81,
                "question": "Why did the whole fragnet flow through to the finish?",
                "options": [
                    "P6: filter Total Float for very high values or use the Schedule Log. MSP: use Task Inspector to find tasks with no predecessors or successors.",
                    "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list.",
                    "Wrong dates → wrong float → wrong critical path → bad forecast (progress curves, cost forecasts, histograms) → bad decisions on acceleration, recovery and claims.",
                    "The affected activity was on the critical path with zero float, so nothing absorbed it."
                ],
                "correctAnswer": 3,
                "explanation": "The affected activity was on the critical path with zero float, so nothing absorbed it.",
                "rawAnswer": "The affected activity was on the critical path with zero float, so nothing absorbed it."
            },
            {
                "id": "module-5-q82",
                "qNum": 82,
                "question": "What condition does the TIA depend on?",
                "options": [
                    "Walk the work sequence, check the predecessor list, check the successor list, and test by delaying an activity: if nothing that should follow moves, a link is missing.",
                    "The Oct 15 update must itself be a healthy, approved schedule; a flawed schedule gives flawed results.",
                    "The longest sequence of activities that determines the earliest possible completion; any critical delay delays the finish by the same amount.",
                    "Level 1: the delay event (what happened). Level 2: the schedule impact (effect on the network, needs CPM). Level 3: the project impact (effect on completion after float, concurrency and recovery)."
                ],
                "correctAnswer": 1,
                "explanation": "The Oct 15 update must itself be a healthy, approved schedule; a flawed schedule gives flawed results.",
                "rawAnswer": "The Oct 15 update must itself be a healthy, approved schedule; a flawed schedule gives flawed results."
            },
            {
                "id": "module-5-q83",
                "qNum": 83,
                "question": "List the steps of the quality review workflow shown.",
                "options": [
                    "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval.",
                    "(1) Check Data Date, (2) logic & open ends, (3) constraints, (4) lags & durations, (5) float, (6) critical/longest path, (7) progress & calendars, (8) document all findings in a written Schedule Health Report. (The capstone text refers to this as the full review workflow.)",
                    "An activity missing a predecessor or a successor.",
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified."
                ],
                "correctAnswer": 1,
                "explanation": "(1) Check Data Date, (2) logic & open ends, (3) constraints, (4) lags & durations, (5) float, (6) critical/longest path, (7) progress & calendars, (8) document all findings in a written Schedule Health Report. (The capstone text refers to this as the full review workflow.)",
                "rawAnswer": "(1) Check Data Date, (2) logic & open ends, (3) constraints, (4) lags & durations, (5) float, (6) critical/longest path, (7) progress & calendars, (8) document all findings in a written Schedule Health Report. (The capstone text refers to this as the full review workflow.)"
            },
            {
                "id": "module-5-q84",
                "qNum": 84,
                "question": "What thresholds apply to lags and durations in the review?",
                "options": [
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified.",
                    "Review all lags greater than 10 days and all activities with durations greater than 20 days.",
                    "Float acts as a buffer between a delay event and its project impact.",
                    "Never; it should be rejected and returned for correction."
                ],
                "correctAnswer": 1,
                "explanation": "Review all lags greater than 10 days and all activities with durations greater than 20 days.",
                "rawAnswer": "Review all lags greater than 10 days and all activities with durations greater than 20 days."
            },
            {
                "id": "module-5-q85",
                "qNum": 85,
                "question": "What is the purpose of DCMA-style metrics?",
                "options": [
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize.",
                    "Use the \"Longest Path\" filter in the Activity view.",
                    "14 − 10 = 4 days of project impact.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
                ],
                "correctAnswer": 0,
                "explanation": "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize.",
                "rawAnswer": "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize."
            },
            {
                "id": "module-5-q86",
                "qNum": 86,
                "question": "What are the DCMA targets for missing logic and hard constraints?",
                "options": [
                    "Is the activity critical? How much float does it have? Are other paths also delayed (concurrent delays)?",
                    "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                    "Missing logic < 5% of activities; hard constraints < 5%."
                ],
                "correctAnswer": 3,
                "explanation": "Missing logic < 5% of activities; hard constraints < 5%.",
                "rawAnswer": "Missing logic < 5% of activities; hard constraints < 5%."
            },
            {
                "id": "module-5-q87",
                "qNum": 87,
                "question": "What are the targets for leads and lags?",
                "options": [
                    "Leads (negative lag) 0%; lags < 5% of relationships, each justified.",
                    "The schedule shows a start weeks too early and crews show up to an unready site. Fix: add FS Electrical Rough-in → Install Server Room Flooring, recalculate and validate.",
                    "Design Revision → Owner Review → Rework, inserted into the schedule where the change occurred.",
                    "No."
                ],
                "correctAnswer": 0,
                "explanation": "Leads (negative lag) 0%; lags < 5% of relationships, each justified.",
                "rawAnswer": "Leads (negative lag) 0%; lags < 5% of relationships, each justified."
            },
            {
                "id": "module-5-q88",
                "qNum": 88,
                "question": "What are the targets for relationship types and high float?",
                "options": [
                    "The Northstar Data Center schedule audit (Section 25).",
                    "An activity constrained to \"Start No Earlier Than April 1\" shows zero float even if predecessors finish by March 10, so the critical path runs through it while the real path stays hidden.",
                    "Float that reflects real schedule flexibility, not artificial numbers.",
                    "Finish-to-Start > 90%; high float (> 44 working days) < 5%."
                ],
                "correctAnswer": 3,
                "explanation": "Finish-to-Start > 90%; high float (> 44 working days) < 5%.",
                "rawAnswer": "Finish-to-Start > 90%; high float (> 44 working days) < 5%."
            },
            {
                "id": "module-5-q89",
                "qNum": 89,
                "question": "What are the targets for negative float and duration?",
                "options": [
                    "An activity with no successor; its delay doesn't ripple forward and float is artificially inflated.",
                    "One connected on only one side (a predecessor or a successor but not both), excluding project start and finish milestones.",
                    "Negative float 0%; activities with remaining duration > 44 working days < 5%.",
                    "CPM results become unreliable and float artificially inflated; a dangling finish shows very high float."
                ],
                "correctAnswer": 2,
                "explanation": "Negative float 0%; activities with remaining duration > 44 working days < 5%.",
                "rawAnswer": "Negative float 0%; activities with remaining duration > 44 working days < 5%."
            },
            {
                "id": "module-5-q90",
                "qNum": 90,
                "question": "What are the Total Float and Free Float formulas?",
                "options": [
                    "Never; it should be rejected and returned for correction.",
                    "Review all lags greater than 10 days and all activities with durations greater than 20 days.",
                    "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
                ],
                "correctAnswer": 2,
                "explanation": "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity.",
                "rawAnswer": "Total Float = Late Finish − Early Finish (or Required − Calculated Date). Free Float = Early Start of successor − Early Finish of activity."
            },
            {
                "id": "module-5-q91",
                "qNum": 91,
                "question": "What is the TIA impact formula?",
                "options": [
                    "Install Cooling Infrastructure (no predecessor) = open end; Electrical Systems 150d = long duration; Design → Procurement FS+60d = excessive lag; Commissioning SNET April 1 = hard constraint; UPS Installation −12 float = negative float; IT Infrastructure Complete with no successor = open end; Structural Steel 60% with actual finish entered = bad progress.",
                    "The affected activity was on the critical path with zero float, so nothing absorbed it.",
                    "Finish Date After Fragnet − Finish Date Before Fragnet.",
                    "Near-critical: small non-zero float (typically 0–5 days), managed proactively. Fragnet: a self-contained mini-schedule for a delay event, inserted into the contemporaneous schedule and based on actual records. TIA: models the schedule impact by inserting a fragnet into the accepted update, recalculating and comparing dates (a modeled impact, not a legal conclusion)."
                ],
                "correctAnswer": 2,
                "explanation": "Finish Date After Fragnet − Finish Date Before Fragnet.",
                "rawAnswer": "Finish Date After Fragnet − Finish Date Before Fragnet."
            },
            {
                "id": "module-5-q92",
                "qNum": 92,
                "question": "List the flawed-schedule audit findings and classifications.",
                "options": [
                    "No measurable progress (what is 43% of \"Electrical Work\"?), delays hide inside, and float is misleading because sub-tasks may have zero float.",
                    "Float that reflects real schedule flexibility, not artificial numbers.",
                    "Install Cooling Infrastructure (no predecessor) = open end; Electrical Systems 150d = long duration; Design → Procurement FS+60d = excessive lag; Commissioning SNET April 1 = hard constraint; UPS Installation −12 float = negative float; IT Infrastructure Complete with no successor = open end; Structural Steel 60% with actual finish entered = bad progress.",
                    "\"Install Server Room Flooring\" has no link to \"Complete Electrical Rough-in,\" though flooring can't start until under-floor electrical is done."
                ],
                "correctAnswer": 2,
                "explanation": "Install Cooling Infrastructure (no predecessor) = open end; Electrical Systems 150d = long duration; Design → Procurement FS+60d = excessive lag; Commissioning SNET April 1 = hard constraint; UPS Installation −12 float = negative float; IT Infrastructure Complete with no successor = open end; Structural Steel 60% with actual finish entered = bad progress.",
                "rawAnswer": "Install Cooling Infrastructure (no predecessor) = open end; Electrical Systems 150d = long duration; Design → Procurement FS+60d = excessive lag; Commissioning SNET April 1 = hard constraint; UPS Installation −12 float = negative float; IT Infrastructure Complete with no successor = open end; Structural Steel 60% with actual finish entered = bad progress."
            },
            {
                "id": "module-5-q93",
                "qNum": 93,
                "question": "What corrections and impacts were found?",
                "options": [
                    "Float that reflects real schedule flexibility, not artificial numbers.",
                    "Dates are driven by logic, not forced by manual overrides.",
                    "Never; it should be rejected and returned for correction.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
                ],
                "correctAnswer": 3,
                "explanation": "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                "rawAnswer": "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
            },
            {
                "id": "module-5-q94",
                "qNum": 94,
                "question": "What are the professional habits of the expert analyst?",
                "options": [
                    "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor.",
                    "A modeled potential impact that must consider float, concurrent activities and recovery work before stating a final position.",
                    "Reviewers often confirm an activity exists and assume logic is correct, but these are different things."
                ],
                "correctAnswer": 0,
                "explanation": "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion.",
                "rawAnswer": "Start with the Data Date; trust logic over appearance; investigate, don't accept; watch trends not snapshots; compare critical to longest path; manage near-critical paths; convert lag to activities; use the contemporaneous schedule for TIA; document everything; state the analysis, not the conclusion."
            },
            {
                "id": "module-5-q95",
                "qNum": 95,
                "question": "Interview: \"How do you check schedule quality?\" (professional answer)",
                "options": [
                    "Reviewers often confirm an activity exists and assume logic is correct, but these are different things.",
                    "The calculated completion date is later than the required or target date.",
                    "The industry-standard method for measuring the potential schedule impact of a delay or change by inserting a fragnet into the contemporaneous schedule.",
                    "Follow a structured workflow: confirm the data date, audit logic/open ends, review constraints with documentation, evaluate lags for hidden work, check durations against the 20-day threshold, analyze float trends, compare critical path to longest path, validate progress against field reports, and prepare a written Schedule Health Report."
                ],
                "correctAnswer": 3,
                "explanation": "Follow a structured workflow: confirm the data date, audit logic/open ends, review constraints with documentation, evaluate lags for hidden work, check durations against the 20-day threshold, analyze float trends, compare critical path to longest path, validate progress against field reports, and prepare a written Schedule Health Report.",
                "rawAnswer": "Follow a structured workflow: confirm the data date, audit logic/open ends, review constraints with documentation, evaluate lags for hidden work, check durations against the 20-day threshold, analyze float trends, compare critical path to longest path, validate progress against field reports, and prepare a written Schedule Health Report."
            },
            {
                "id": "module-5-q96",
                "qNum": 96,
                "question": "Interview: \"What is an open end?\" (professional answer)",
                "options": [
                    "The industry-standard method for measuring the potential schedule impact of a delay or change by inserting a fragnet into the contemporaneous schedule.",
                    "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval.",
                    "Finish Date After Fragnet − Finish Date Before Fragnet.",
                    "Open end fixed changed early start by 22 days; 150d split into 8 activities; 60d lag replaced by three activities totaling 45 working days (15-day reduction); SNET removed moved the start to March 18 (14 days earlier); UPS float returned from −12 to +4 after fixing a missing predecessor."
                ],
                "correctAnswer": 1,
                "explanation": "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval.",
                "rawAnswer": "A logic deficiency where an activity lacks a predecessor (unconstrained early start) or a successor (no delay propagation forward). It corrupts CPM with inflated float and must be documented and corrected before approval."
            },
            {
                "id": "module-5-q97",
                "qNum": 97,
                "question": "Interview: \"What causes negative float?\"",
                "options": [
                    "MEP at 4 days of float is one small delay from becoming critical with no plan in place.",
                    "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "If they diverge significantly, constraints or logic errors are likely distorting the critical path."
                ],
                "correctAnswer": 1,
                "explanation": "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action.",
                "rawAnswer": "Required Date − Calculated Date < 0. Causes: real slippage, an incorrectly entered required date, missing predecessors that push dates out, or excessive constraints. Each needs investigation before corrective action."
            },
            {
                "id": "module-5-q98",
                "qNum": 98,
                "question": "Interview: \"What is near-critical, what is a fragnet, and how do you explain TIA?\"",
                "options": [
                    "Owner: late design approvals, owner-directed changes, late site access. Contractor: resource shortages, poor productivity, failed inspections, late submittals. Third-party: extreme weather, utility company delays, permitting slowdowns, force majeure.",
                    "Near-critical: small non-zero float (typically 0–5 days), managed proactively. Fragnet: a self-contained mini-schedule for a delay event, inserted into the contemporaneous schedule and based on actual records. TIA: models the schedule impact by inserting a fragnet into the accepted update, recalculating and comparing dates (a modeled impact, not a legal conclusion).",
                    "A path with 5 days of float becomes critical the moment a 5-day delay occurs, with no warning, so reports must show near-critical paths too.",
                    "Is the activity critical? How much float does it have? Are other paths also delayed (concurrent delays)?"
                ],
                "correctAnswer": 1,
                "explanation": "Near-critical: small non-zero float (typically 0–5 days), managed proactively. Fragnet: a self-contained mini-schedule for a delay event, inserted into the contemporaneous schedule and based on actual records. TIA: models the schedule impact by inserting a fragnet into the accepted update, recalculating and comparing dates (a modeled impact, not a legal conclusion).",
                "rawAnswer": "Near-critical: small non-zero float (typically 0–5 days), managed proactively. Fragnet: a self-contained mini-schedule for a delay event, inserted into the contemporaneous schedule and based on actual records. TIA: models the schedule impact by inserting a fragnet into the accepted update, recalculating and comparing dates (a modeled impact, not a legal conclusion)."
            },
            {
                "id": "module-5-q99",
                "qNum": 99,
                "question": "What are the seven capstone deliverables?",
                "options": [
                    "0 days: Critical, monitor daily. 1–5: Near-critical, weekly action. 6–14: Watch, biweekly. 15–30: Monitor, monthly. 30+: Low concern, routine update.",
                    "(1) Audit the schedule, (2) correct logic defects, (3) identify critical and near-critical paths, (4) build the fragnet (Design Revision 5d → Owner Review 8d → Revised Procurement 12d), (5) perform a basic TIA, (6) document findings in a Schedule Health Report, (7) one-page management summary.",
                    "Never; it should be rejected and returned for correction.",
                    "Like a polished car with a broken engine, missing logic, bad calendars or forced constraints stay invisible until the schedule fails. An analyst must audit the logic, not just the report."
                ],
                "correctAnswer": 1,
                "explanation": "(1) Audit the schedule, (2) correct logic defects, (3) identify critical and near-critical paths, (4) build the fragnet (Design Revision 5d → Owner Review 8d → Revised Procurement 12d), (5) perform a basic TIA, (6) document findings in a Schedule Health Report, (7) one-page management summary.",
                "rawAnswer": "(1) Audit the schedule, (2) correct logic defects, (3) identify critical and near-critical paths, (4) build the fragnet (Design Revision 5d → Owner Review 8d → Revised Procurement 12d), (5) perform a basic TIA, (6) document findings in a Schedule Health Report, (7) one-page management summary."
            },
            {
                "id": "module-5-q100",
                "qNum": 100,
                "question": "What are the capstone evaluation criteria and what does the management summary contain?",
                "options": [
                    "They're diagnostic tools, like a dashboard, that point to areas needing deeper investigation, not a checklist to memorize.",
                    "The most recently approved schedule update, in place when the delay occurred.",
                    "A path with small but non-zero float, low enough that a minor delay could push it onto the critical path.",
                    "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list."
                ],
                "correctAnswer": 3,
                "explanation": "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list.",
                "rawAnswer": "Completeness, accuracy, professionalism and critical thinking (investigate, don't accept negative float or unexplained constraints). The summary should be jargon-free and state schedule health, critical path status, the delay event and modeled impact, recommended actions and a near-critical watch list."
            }
        ]
    },
    {
        "id": "module-6",
        "aliases": [
            "evm",
            "project-controls",
            "executive-reporting"
        ],
        "title": "Module 6: Project Controls & Performance Reporting",
        "description": "Earned Value Management (PV, EV, AC, SV, CV, SPI, CPI, ETC, EAC, VAC), root-cause analysis, schedule recovery, and executive dashboards.",
        "project": "LGL Northstar Data Center Capital Project",
        "iconName": "BarChart3",
        "color": "#EC4899",
        "questions": [
            {
                "id": "module-6-q1",
                "qNum": 1,
                "question": "What seven questions must every project controls professional answer for management?",
                "options": [
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "PV = $5,000; EV = $4,000; AC = $4,500.",
                    "Credibility is everything; never adjust metrics to please management.",
                    "What did we plan? What have we accomplished? What have we spent? Are we ahead or behind? Are we over or under budget? Where will we finish? What should management do?"
                ],
                "correctAnswer": 3,
                "explanation": "What did we plan? What have we accomplished? What have we spent? Are we ahead or behind? Are we over or under budget? Where will we finish? What should management do?",
                "rawAnswer": "What did we plan? What have we accomplished? What have we spent? Are we ahead or behind? Are we over or under budget? Where will we finish? What should management do?"
            },
            {
                "id": "module-6-q2",
                "qNum": 2,
                "question": "What is Project Controls?",
                "options": [
                    "PV = $5,000; EV = $4,000; AC = $4,500.",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?",
                    "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
                ],
                "correctAnswer": 2,
                "explanation": "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost.",
                "rawAnswer": "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost."
            },
            {
                "id": "module-6-q3",
                "qNum": 3,
                "question": "What is added on top of the four areas?",
                "options": [
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "No; they're illustrative. Follow the organization's approved KPI framework.",
                    "Progress (how much planned work is done) and Forecasting (where the project is heading: final cost and finish date).",
                    "Planned 62% by Month 12; actual 55%; forecast finish Feb 2026, two months late."
                ],
                "correctAnswer": 2,
                "explanation": "Progress (how much planned work is done) and Forecasting (where the project is heading: final cost and finish date).",
                "rawAnswer": "Progress (how much planned work is done) and Forecasting (where the project is heading: final cost and finish date)."
            },
            {
                "id": "module-6-q4",
                "qNum": 4,
                "question": "What is the result of project controls?",
                "options": [
                    "Actual performance with the plan for remaining work.",
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "Credibility is everything; never adjust metrics to please management."
                ],
                "correctAnswer": 2,
                "explanation": "An integrated, honest and actionable picture management can trust and act upon.",
                "rawAnswer": "An integrated, honest and actionable picture management can trust and act upon."
            },
            {
                "id": "module-6-q5",
                "qNum": 5,
                "question": "What are Northstar's scope facts?",
                "options": [
                    "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost.",
                    "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action.",
                    "A 40,000 sq ft Tier III data center in Houston, TX, including civil, structural, MEP, IT infrastructure, security and commissioning.",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick."
                ],
                "correctAnswer": 2,
                "explanation": "A 40,000 sq ft Tier III data center in Houston, TX, including civil, structural, MEP, IT infrastructure, security and commissioning.",
                "rawAnswer": "A 40,000 sq ft Tier III data center in Houston, TX, including civil, structural, MEP, IT infrastructure, security and commissioning."
            },
            {
                "id": "module-6-q6",
                "qNum": 6,
                "question": "What are Northstar's schedule facts?",
                "options": [
                    "BAC $48M, PV $29.76M, EV $26.4M, AC $27.5M: SV −$3.36M, CV −$1.1M, SPI 0.887, CPI 0.96, EAC $50M, VAC −$2M; root cause is switchgear 42 working days late from manufacturer delays with MEP on the critical path; forecast completion slips +43 calendar days.",
                    "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "Baseline 24-month duration; start Jan 2024; planned completion Dec 2025."
                ],
                "correctAnswer": 3,
                "explanation": "Baseline 24-month duration; start Jan 2024; planned completion Dec 2025.",
                "rawAnswer": "Baseline 24-month duration; start Jan 2024; planned completion Dec 2025."
            },
            {
                "id": "module-6-q7",
                "qNum": 7,
                "question": "What is Northstar's approved budget?",
                "options": [
                    "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                    "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
                ],
                "correctAnswer": 1,
                "explanation": "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                "rawAnswer": "$48,000,000 (BAC), including labor, materials, equipment and indirect costs."
            },
            {
                "id": "module-6-q8",
                "qNum": 8,
                "question": "Who performs the work at Northstar?",
                "options": [
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible.",
                    "SV = EV − PV.",
                    "$26.4M − $29.76M = −$3,360,000.",
                    "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team."
                ],
                "correctAnswer": 3,
                "explanation": "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team.",
                "rawAnswer": "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team."
            },
            {
                "id": "module-6-q9",
                "qNum": 9,
                "question": "What was Northstar's progress at the Month 12 data date?",
                "options": [
                    "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned.",
                    "Costs double and time is lost; quality problems compound schedule and cost simultaneously.",
                    "Planned 62% by Month 12; actual 55%; forecast finish Feb 2026, two months late.",
                    "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team."
                ],
                "correctAnswer": 0,
                "explanation": "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned.",
                "rawAnswer": "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned."
            },
            {
                "id": "module-6-q10",
                "qNum": 10,
                "question": "What is Northstar's forecast at Month 12?",
                "options": [
                    "A Feb 2026 finish, two months behind plan.",
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                    "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                    "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline."
                ],
                "correctAnswer": 0,
                "explanation": "A Feb 2026 finish, two months behind plan.",
                "rawAnswer": "A Feb 2026 finish, two months behind plan."
            },
            {
                "id": "module-6-q11",
                "qNum": 11,
                "question": "Explain planned vs. actual vs. forecast with the road trip.",
                "options": [
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "Facts → Variance → Cause → Impact → Action."
                ],
                "correctAnswer": 0,
                "explanation": "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                "rawAnswer": "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace."
            },
            {
                "id": "module-6-q12",
                "qNum": 12,
                "question": "Translate that to Northstar.",
                "options": [
                    "Budget at Completion: the total approved performance budget for the entire project.",
                    "Planned 62% by Month 12; actual 55%; forecast finish Feb 2026, two months late.",
                    "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation).",
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs."
                ],
                "correctAnswer": 1,
                "explanation": "Planned 62% by Month 12; actual 55%; forecast finish Feb 2026, two months late.",
                "rawAnswer": "Planned 62% by Month 12; actual 55%; forecast finish Feb 2026, two months late."
            },
            {
                "id": "module-6-q13",
                "qNum": 13,
                "question": "Why is honesty essential in project controls?",
                "options": [
                    "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend).",
                    "It's the anchor for EAC, VAC and percent calculations; without an accurate approved BAC, EVM metrics aren't reliable.",
                    "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?",
                    "Credibility is everything; never adjust metrics to please management."
                ],
                "correctAnswer": 3,
                "explanation": "Credibility is everything; never adjust metrics to please management.",
                "rawAnswer": "Credibility is everything; never adjust metrics to please management."
            },
            {
                "id": "module-6-q14",
                "qNum": 14,
                "question": "What is the core cycle of controls?",
                "options": [
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable.",
                    "Plan, execute, measure, analyze, and repeat: never stop at the baseline.",
                    "Baseline 24-month duration; start Jan 2024; planned completion Dec 2025.",
                    "CV = EV − AC."
                ],
                "correctAnswer": 1,
                "explanation": "Plan, execute, measure, analyze, and repeat: never stop at the baseline.",
                "rawAnswer": "Plan, execute, measure, analyze, and repeat: never stop at the baseline."
            },
            {
                "id": "module-6-q15",
                "qNum": 15,
                "question": "Renovation story: $10,000 budget, planned $5,000 done, $4,000 done, $4,500 charged. What are PV, EV, AC?",
                "options": [
                    "PV = $5,000; EV = $4,000; AC = $4,500.",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?",
                    "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges."
                ],
                "correctAnswer": 0,
                "explanation": "PV = $5,000; EV = $4,000; AC = $4,500.",
                "rawAnswer": "PV = $5,000; EV = $4,000; AC = $4,500."
            },
            {
                "id": "module-6-q16",
                "qNum": 16,
                "question": "What is Planned Value (PV)?",
                "options": [
                    "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars.",
                    "Estimate at Completion: the forecast final project cost.",
                    "Crew: site congestion, supervision challenges, higher cost. Overtime: premium pay, fatigue, reduced productivity.",
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening."
                ],
                "correctAnswer": 0,
                "explanation": "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars.",
                "rawAnswer": "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars."
            },
            {
                "id": "module-6-q17",
                "qNum": 17,
                "question": "What are PV examples?",
                "options": [
                    "PV = $5,000; EV = $4,000; AC = $4,500.",
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun.",
                    "Road: $1,000,000 × 40% = $400,000. Northstar Month 12: $48M × 62% = $29,760,000. Renovation: $5,000.",
                    "What did we plan? What have we accomplished? What have we spent? Are we ahead or behind? Are we over or under budget? Where will we finish? What should management do?"
                ],
                "correctAnswer": 2,
                "explanation": "Road: $1,000,000 × 40% = $400,000. Northstar Month 12: $48M × 62% = $29,760,000. Renovation: $5,000.",
                "rawAnswer": "Road: $1,000,000 × 40% = $400,000. Northstar Month 12: $48M × 62% = $29,760,000. Renovation: $5,000."
            },
            {
                "id": "module-6-q18",
                "qNum": 18,
                "question": "Does PV change during the project?",
                "options": [
                    "SV = −$10,000; CV = −$5,000.",
                    "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred).",
                    "No, unless there is an approved scope change; it's the measuring stick.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?"
                ],
                "correctAnswer": 2,
                "explanation": "No, unless there is an approved scope change; it's the measuring stick.",
                "rawAnswer": "No, unless there is an approved scope change; it's the measuring stick."
            },
            {
                "id": "module-6-q19",
                "qNum": 19,
                "question": "What is Earned Value (EV)?",
                "options": [
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "The budgeted value of the work actually completed.",
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable."
                ],
                "correctAnswer": 2,
                "explanation": "The budgeted value of the work actually completed.",
                "rawAnswer": "The budgeted value of the work actually completed."
            },
            {
                "id": "module-6-q20",
                "qNum": 20,
                "question": "What is the key distinction about EV?",
                "options": [
                    "1.00 on budget; 0.93 means $0.93 earned per $1 (over budget); 1.10 means $1.10 earned (under budget, verify it's real).",
                    "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred).",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?",
                    "EV is NOT the money spent; it is the budget credit earned for physical progress."
                ],
                "correctAnswer": 3,
                "explanation": "EV is NOT the money spent; it is the budget credit earned for physical progress.",
                "rawAnswer": "EV is NOT the money spent; it is the budget credit earned for physical progress."
            },
            {
                "id": "module-6-q21",
                "qNum": 21,
                "question": "Calculate Northstar EV.",
                "options": [
                    "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                    "1.00 on schedule; < 1.00 behind; > 1.00 ahead. 0.90 = progressing at 90% of the planned rate; 1.05 = $1.05 earned per $1 planned (verify quality and resource loading).",
                    "$48M × 55% = $26,400,000.",
                    "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;"
                ],
                "correctAnswer": 2,
                "explanation": "$48M × 55% = $26,400,000.",
                "rawAnswer": "$48M × 55% = $26,400,000."
            },
            {
                "id": "module-6-q22",
                "qNum": 22,
                "question": "An activity budgeted at $100,000 is 40% complete. What is EV?",
                "options": [
                    "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                    "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026.",
                    "$40,000, regardless of what was spent.",
                    "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC."
                ],
                "correctAnswer": 2,
                "explanation": "$40,000, regardless of what was spent.",
                "rawAnswer": "$40,000, regardless of what was spent."
            },
            {
                "id": "module-6-q23",
                "qNum": 23,
                "question": "What is Actual Cost (AC)?",
                "options": [
                    "The money actually spent to perform work in a period, from accounting: invoices, payroll, purchase orders, allocated overhead.",
                    "$26.4M − $29.76M = −$3,360,000.",
                    "Baseline 24-month duration; start Jan 2024; planned completion Dec 2025.",
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun."
                ],
                "correctAnswer": 0,
                "explanation": "The money actually spent to perform work in a period, from accounting: invoices, payroll, purchase orders, allocated overhead.",
                "rawAnswer": "The money actually spent to perform work in a period, from accounting: invoices, payroll, purchase orders, allocated overhead."
            },
            {
                "id": "module-6-q24",
                "qNum": 24,
                "question": "What are Northstar's Month 12 PV, EV and AC?",
                "options": [
                    "PV $29,760,000; EV $26,400,000; AC $27,500,000.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges.",
                    "EAC = AC + ETC, or the simple form EAC = BAC ÷ CPI.",
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening."
                ],
                "correctAnswer": 0,
                "explanation": "PV $29,760,000; EV $26,400,000; AC $27,500,000.",
                "rawAnswer": "PV $29,760,000; EV $26,400,000; AC $27,500,000."
            },
            {
                "id": "module-6-q25",
                "qNum": 25,
                "question": "How is progress for EV measured?",
                "options": [
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned.",
                    "SV = EV − PV.",
                    "Field inspections, percent complete reports, milestones hit, or quantities installed, verified by controls."
                ],
                "correctAnswer": 3,
                "explanation": "Field inspections, percent complete reports, milestones hit, or quantities installed, verified by controls.",
                "rawAnswer": "Field inspections, percent complete reports, milestones hit, or quantities installed, verified by controls."
            },
            {
                "id": "module-6-q26",
                "qNum": 26,
                "question": "Why can EV differ from AC?",
                "options": [
                    "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work.",
                    "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                    "Variance at Completion = BAC − EAC."
                ],
                "correctAnswer": 1,
                "explanation": "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work.",
                "rawAnswer": "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work."
            },
            {
                "id": "module-6-q27",
                "qNum": 27,
                "question": "How do the reference formulas define PV and EV?",
                "options": [
                    "It shows the path to completion lengthening before SPI shows a problem.",
                    "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend).",
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "PV = BAC × planned % complete; EV = BAC × actual % complete."
                ],
                "correctAnswer": 3,
                "explanation": "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                "rawAnswer": "PV = BAC × planned % complete; EV = BAC × actual % complete."
            },
            {
                "id": "module-6-q28",
                "qNum": 28,
                "question": "What is BAC?",
                "options": [
                    "EV is NOT the money spent; it is the budget credit earned for physical progress.",
                    "Budget at Completion: the total approved performance budget for the entire project.",
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
                ],
                "correctAnswer": 1,
                "explanation": "Budget at Completion: the total approved performance budget for the entire project.",
                "rawAnswer": "Budget at Completion: the total approved performance budget for the entire project."
            },
            {
                "id": "module-6-q29",
                "qNum": 29,
                "question": "Does BAC change if costs run over?",
                "options": [
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick.",
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                    "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team."
                ],
                "correctAnswer": 1,
                "explanation": "No. It changes only with an approved scope change or contract modification; it's the yardstick.",
                "rawAnswer": "No. It changes only with an approved scope change or contract modification; it's the yardstick."
            },
            {
                "id": "module-6-q30",
                "qNum": 30,
                "question": "Why does BAC matter?",
                "options": [
                    "It's the anchor for EAC, VAC and percent calculations; without an accurate approved BAC, EVM metrics aren't reliable.",
                    "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;",
                    "PV $29,760,000; EV $26,400,000; AC $27,500,000.",
                    "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom."
                ],
                "correctAnswer": 0,
                "explanation": "It's the anchor for EAC, VAC and percent calculations; without an accurate approved BAC, EVM metrics aren't reliable.",
                "rawAnswer": "It's the anchor for EAC, VAC and percent calculations; without an accurate approved BAC, EVM metrics aren't reliable."
            },
            {
                "id": "module-6-q31",
                "qNum": 31,
                "question": "What is the Schedule Variance formula?",
                "options": [
                    "SV = EV − PV.",
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "It shows the path to completion lengthening before SPI shows a problem."
                ],
                "correctAnswer": 0,
                "explanation": "SV = EV − PV.",
                "rawAnswer": "SV = EV − PV."
            },
            {
                "id": "module-6-q32",
                "qNum": 32,
                "question": "How do you read SV negative, zero, positive?",
                "options": [
                    "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "It's the anchor for EAC, VAC and percent calculations; without an accurate approved BAC, EVM metrics aren't reliable.",
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team)."
                ],
                "correctAnswer": 3,
                "explanation": "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                "rawAnswer": "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team)."
            },
            {
                "id": "module-6-q33",
                "qNum": 33,
                "question": "What is Northstar's SV?",
                "options": [
                    "$26.4M − $27.5M = −$1,100,000.",
                    "Begin electrical rough-in in completed floors while structural work finishes above. Crash when the critical path can genuinely be shortened and delay cost exceeds crash cost. Fast track when information is stable, the dependency is logistical and teams can be separated.",
                    "$26.4M − $29.76M = −$3,360,000.",
                    "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned."
                ],
                "correctAnswer": 2,
                "explanation": "$26.4M − $29.76M = −$3,360,000.",
                "rawAnswer": "$26.4M − $29.76M = −$3,360,000."
            },
            {
                "id": "module-6-q34",
                "qNum": 34,
                "question": "What is the limitation of SV?",
                "options": [
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "Progress (how much planned work is done) and Forecasting (where the project is heading: final cost and finish date).",
                    "It's in dollars, not calendar days; it doesn't say directly how many days late.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work."
                ],
                "correctAnswer": 2,
                "explanation": "It's in dollars, not calendar days; it doesn't say directly how many days late.",
                "rawAnswer": "It's in dollars, not calendar days; it doesn't say directly how many days late."
            },
            {
                "id": "module-6-q35",
                "qNum": 35,
                "question": "What is the Cost Variance formula?",
                "options": [
                    "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars.",
                    "$26.4M ÷ $29.76M = 0.887 (88.7% of planned rate).",
                    "CV = EV − AC.",
                    "SV = −$10,000; CV = −$5,000."
                ],
                "correctAnswer": 2,
                "explanation": "CV = EV − AC.",
                "rawAnswer": "CV = EV − AC."
            },
            {
                "id": "module-6-q36",
                "qNum": 36,
                "question": "How do you read CV?",
                "options": [
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom.",
                    "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred).",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?"
                ],
                "correctAnswer": 2,
                "explanation": "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred).",
                "rawAnswer": "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred)."
            },
            {
                "id": "module-6-q37",
                "qNum": 37,
                "question": "What is Northstar's CV?",
                "options": [
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision.",
                    "$26.4M − $27.5M = −$1,100,000.",
                    "Estimate at Completion: the forecast final project cost."
                ],
                "correctAnswer": 2,
                "explanation": "$26.4M − $27.5M = −$1,100,000.",
                "rawAnswer": "$26.4M − $27.5M = −$1,100,000."
            },
            {
                "id": "module-6-q38",
                "qNum": 38,
                "question": "Renovation CV?",
                "options": [
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                    "Executive summary, progress this period, critical path, major variances, key risks, recovery actions, forecast, decisions required. It should be readable in 90 seconds.",
                    "$4,000 − $4,500 = −$500 (over budget by $500).",
                    "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns."
                ],
                "correctAnswer": 2,
                "explanation": "$4,000 − $4,500 = −$500 (over budget by $500).",
                "rawAnswer": "$4,000 − $4,500 = −$500 (over budget by $500)."
            },
            {
                "id": "module-6-q39",
                "qNum": 39,
                "question": "Why is CV a more honest measure than total spend vs. total budget?",
                "options": [
                    "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC.",
                    "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                    "Starting the next activity before the previous one completes, accepting overlap. It may not add direct cost but increases coordination and rework risk.",
                    "It measures cost efficiency on the work actually performed."
                ],
                "correctAnswer": 3,
                "explanation": "It measures cost efficiency on the work actually performed.",
                "rawAnswer": "It measures cost efficiency on the work actually performed."
            },
            {
                "id": "module-6-q40",
                "qNum": 40,
                "question": "What is SPI and its formula?",
                "options": [
                    "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?",
                    "It shows the path to completion lengthening before SPI shows a problem.",
                    "Negative: cost overrun for work completed. Positive: underrun (productivity may be better or costs deferred).",
                    "$48M × 55% = $26,400,000."
                ],
                "correctAnswer": 0,
                "explanation": "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?",
                "rawAnswer": "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?"
            },
            {
                "id": "module-6-q41",
                "qNum": 41,
                "question": "How do you interpret SPI values?",
                "options": [
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms).",
                    "1.00 on schedule; < 1.00 behind; > 1.00 ahead. 0.90 = progressing at 90% of the planned rate; 1.05 = $1.05 earned per $1 planned (verify quality and resource loading).",
                    "Estimate to Complete: how much more money we expect to spend to finish the remaining work.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
                ],
                "correctAnswer": 1,
                "explanation": "1.00 on schedule; < 1.00 behind; > 1.00 ahead. 0.90 = progressing at 90% of the planned rate; 1.05 = $1.05 earned per $1 planned (verify quality and resource loading).",
                "rawAnswer": "1.00 on schedule; < 1.00 behind; > 1.00 ahead. 0.90 = progressing at 90% of the planned rate; 1.05 = $1.05 earned per $1 planned (verify quality and resource loading)."
            },
            {
                "id": "module-6-q42",
                "qNum": 42,
                "question": "What is Northstar's SPI?",
                "options": [
                    "Schedule (SPI), Cost (CPI), Critical Path/Float, Risks, Milestones, and Recovery Status.",
                    "$26.4M ÷ $29.76M = 0.887 (88.7% of planned rate).",
                    "It measures cost efficiency on the work actually performed.",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick."
                ],
                "correctAnswer": 1,
                "explanation": "$26.4M ÷ $29.76M = 0.887 (88.7% of planned rate).",
                "rawAnswer": "$26.4M ÷ $29.76M = 0.887 (88.7% of planned rate)."
            },
            {
                "id": "module-6-q43",
                "qNum": 43,
                "question": "What is CPI and its formula?",
                "options": [
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges."
                ],
                "correctAnswer": 1,
                "explanation": "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                "rawAnswer": "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?"
            },
            {
                "id": "module-6-q44",
                "qNum": 44,
                "question": "How do you interpret CPI values?",
                "options": [
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                    "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                    "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC.",
                    "1.00 on budget; 0.93 means $0.93 earned per $1 (over budget); 1.10 means $1.10 earned (under budget, verify it's real)."
                ],
                "correctAnswer": 3,
                "explanation": "1.00 on budget; 0.93 means $0.93 earned per $1 (over budget); 1.10 means $1.10 earned (under budget, verify it's real).",
                "rawAnswer": "1.00 on budget; 0.93 means $0.93 earned per $1 (over budget); 1.10 means $1.10 earned (under budget, verify it's real)."
            },
            {
                "id": "module-6-q45",
                "qNum": 45,
                "question": "What is Northstar's CPI?",
                "options": [
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms).",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible.",
                    "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges."
                ],
                "correctAnswer": 0,
                "explanation": "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms).",
                "rawAnswer": "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms)."
            },
            {
                "id": "module-6-q46",
                "qNum": 46,
                "question": "Worked $100,000 project: BAC $100K, PV $50K, EV $40K, AC $45K. Find SV and CV.",
                "options": [
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "Plan, execute, measure, analyze, and repeat: never stop at the baseline.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work.",
                    "SV = −$10,000; CV = −$5,000."
                ],
                "correctAnswer": 3,
                "explanation": "SV = −$10,000; CV = −$5,000.",
                "rawAnswer": "SV = −$10,000; CV = −$5,000."
            },
            {
                "id": "module-6-q47",
                "qNum": 47,
                "question": "In the same project, find SPI and CPI.",
                "options": [
                    "SPI = 40 ÷ 50 = 0.80; CPI = 40 ÷ 45 = 0.889.",
                    "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026.",
                    "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars.",
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun."
                ],
                "correctAnswer": 0,
                "explanation": "SPI = 40 ÷ 50 = 0.80; CPI = 40 ÷ 45 = 0.889.",
                "rawAnswer": "SPI = 40 ÷ 50 = 0.80; CPI = 40 ÷ 45 = 0.889."
            },
            {
                "id": "module-6-q48",
                "qNum": 48,
                "question": "Interpret the $100K project.",
                "options": [
                    "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms).",
                    "SPI (0.887); schedule underperformance is more significant than the slight cost overrun."
                ],
                "correctAnswer": 0,
                "explanation": "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                "rawAnswer": "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately."
            },
            {
                "id": "module-6-q49",
                "qNum": 49,
                "question": "Which metrics are inputs, variances, ratios and forecasts?",
                "options": [
                    "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?",
                    "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC.",
                    "Crew: site congestion, supervision challenges, higher cost. Overtime: premium pay, fatigue, reduced productivity.",
                    "PV = $5,000; EV = $4,000; AC = $4,500."
                ],
                "correctAnswer": 1,
                "explanation": "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC.",
                "rawAnswer": "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC."
            },
            {
                "id": "module-6-q50",
                "qNum": 50,
                "question": "Renovation SPI and CPI?",
                "options": [
                    "Resequence work (unlocks bottlenecks), add crew (more output), add shift/overtime (more hours), expedite procurement (unblocks installation), parallel work (saves time), resolve constraints (restores productivity), prioritize critical path (focus resources).",
                    "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend).",
                    "SPI = 4,000 ÷ 5,000 = 0.80; CPI = 4,000 ÷ 4,500 ≈ 0.89.",
                    "It measures cost efficiency on the work actually performed."
                ],
                "correctAnswer": 2,
                "explanation": "SPI = 4,000 ÷ 5,000 = 0.80; CPI = 4,000 ÷ 4,500 ≈ 0.89.",
                "rawAnswer": "SPI = 4,000 ÷ 5,000 = 0.80; CPI = 4,000 ÷ 4,500 ≈ 0.89."
            },
            {
                "id": "module-6-q51",
                "qNum": 51,
                "question": "Which is more concerning at Northstar, SPI or CPI?",
                "options": [
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms).",
                    "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;",
                    "Credibility is everything; never adjust metrics to please management.",
                    "SPI (0.887); schedule underperformance is more significant than the slight cost overrun."
                ],
                "correctAnswer": 3,
                "explanation": "SPI (0.887); schedule underperformance is more significant than the slight cost overrun.",
                "rawAnswer": "SPI (0.887); schedule underperformance is more significant than the slight cost overrun."
            },
            {
                "id": "module-6-q52",
                "qNum": 52,
                "question": "What does SPI < 1 mean, simple and professional?",
                "options": [
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "Simple: we're earning less work than planned. Professional: for each dollar of work planned, we earn less than one dollar of physical progress, so the project is progressing below plan; investigate the cause and develop a recovery plan if the trend continues.",
                    "Estimate at Completion: the forecast final project cost."
                ],
                "correctAnswer": 2,
                "explanation": "Simple: we're earning less work than planned. Professional: for each dollar of work planned, we earn less than one dollar of physical progress, so the project is progressing below plan; investigate the cause and develop a recovery plan if the trend continues.",
                "rawAnswer": "Simple: we're earning less work than planned. Professional: for each dollar of work planned, we earn less than one dollar of physical progress, so the project is progressing below plan; investigate the cause and develop a recovery plan if the trend continues."
            },
            {
                "id": "module-6-q53",
                "qNum": 53,
                "question": "What is ETC?",
                "options": [
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "Finishing under budget; management may reallocate contingency or repurpose savings.",
                    "Estimate to Complete: how much more money we expect to spend to finish the remaining work."
                ],
                "correctAnswer": 3,
                "explanation": "Estimate to Complete: how much more money we expect to spend to finish the remaining work.",
                "rawAnswer": "Estimate to Complete: how much more money we expect to spend to finish the remaining work."
            },
            {
                "id": "module-6-q54",
                "qNum": 54,
                "question": "What is the ETC formula?",
                "options": [
                    "Executive summary, progress this period, critical path, major variances, key risks, recovery actions, forecast, decisions required. It should be readable in 90 seconds.",
                    "PV $29,760,000; EV $26,400,000; AC $27,500,000.",
                    "ETC = (BAC − EV) ÷ CPI.",
                    "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action."
                ],
                "correctAnswer": 2,
                "explanation": "ETC = (BAC − EV) ÷ CPI.",
                "rawAnswer": "ETC = (BAC − EV) ÷ CPI."
            },
            {
                "id": "module-6-q55",
                "qNum": 55,
                "question": "Calculate Northstar ETC.",
                "options": [
                    "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                    "No; they're illustrative. Follow the organization's approved KPI framework.",
                    "Ask first: is the delay on the critical path? What's the root cause? Can it be partly or fully recovered? What's the cost of doing nothing? Does management have approval authority? Fix the cause first, then accelerate (crashing a delayed material activity adds cost without progress).",
                    "It's in dollars, not calendar days; it doesn't say directly how many days late."
                ],
                "correctAnswer": 0,
                "explanation": "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                "rawAnswer": "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000."
            },
            {
                "id": "module-6-q56",
                "qNum": 56,
                "question": "What is EAC?",
                "options": [
                    "$4,000 − $4,500 = −$500 (over budget by $500).",
                    "Estimate at Completion: the forecast final project cost.",
                    "It measures cost efficiency on the work actually performed.",
                    "$40,000, regardless of what was spent."
                ],
                "correctAnswer": 1,
                "explanation": "Estimate at Completion: the forecast final project cost.",
                "rawAnswer": "Estimate at Completion: the forecast final project cost."
            },
            {
                "id": "module-6-q57",
                "qNum": 57,
                "question": "What are the EAC formulas?",
                "options": [
                    "EV is NOT the money spent; it is the budget credit earned for physical progress.",
                    "EAC = AC + ETC, or the simple form EAC = BAC ÷ CPI.",
                    "SV = −$10,000; CV = −$5,000.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
                ],
                "correctAnswer": 1,
                "explanation": "EAC = AC + ETC, or the simple form EAC = BAC ÷ CPI.",
                "rawAnswer": "EAC = AC + ETC, or the simple form EAC = BAC ÷ CPI."
            },
            {
                "id": "module-6-q58",
                "qNum": 58,
                "question": "Calculate Northstar EAC both ways.",
                "options": [
                    "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000.",
                    "BAC $48M, PV $29.76M, EV $26.4M, AC $27.5M: SV −$3.36M, CV −$1.1M, SPI 0.887, CPI 0.96, EAC $50M, VAC −$2M; root cause is switchgear 42 working days late from manufacturer delays with MEP on the critical path; forecast completion slips +43 calendar days.",
                    "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision.",
                    "$27.5M + $22.5M = $50M; or $48M ÷ 0.96 = $50,000,000."
                ],
                "correctAnswer": 3,
                "explanation": "$27.5M + $22.5M = $50M; or $48M ÷ 0.96 = $50,000,000.",
                "rawAnswer": "$27.5M + $22.5M = $50M; or $48M ÷ 0.96 = $50,000,000."
            },
            {
                "id": "module-6-q59",
                "qNum": 59,
                "question": "What is VAC and its formula?",
                "options": [
                    "Both behind schedule and over budget; expect a delayed, over-budget finish unless corrective action is taken immediately.",
                    "Variance at Completion = BAC − EAC.",
                    "A Feb 2026 finish, two months behind plan.",
                    "SPI (0.887); schedule underperformance is more significant than the slight cost overrun."
                ],
                "correctAnswer": 1,
                "explanation": "Variance at Completion = BAC − EAC.",
                "rawAnswer": "Variance at Completion = BAC − EAC."
            },
            {
                "id": "module-6-q60",
                "qNum": 60,
                "question": "What is Northstar's VAC and meaning?",
                "options": [
                    "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision.",
                    "Civil (Mar 2024) and Structural (Aug 2024) done; MEP Rough-In Dec 2024 → Feb 2025 (+42 days, At Risk, switchgear expediting); IT Start Jan → Mar 2025 (+42, At Risk); Commissioning Aug → Oct 2025 (+43, Delayed); Project Complete Dec 2025 → Feb 2026 (+43, Delayed).",
                    "Facts → Variance → Cause → Impact → Action.",
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms)."
                ],
                "correctAnswer": 0,
                "explanation": "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision.",
                "rawAnswer": "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision."
            },
            {
                "id": "module-6-q61",
                "qNum": 61,
                "question": "What does a positive VAC mean?",
                "options": [
                    "Finishing under budget; management may reallocate contingency or repurpose savings.",
                    "Schedule (SPI), Cost (CPI), Critical Path/Float, Risks, Milestones, and Recovery Status.",
                    "$48M − $50M = −$2,000,000: a forecast $2M overrun requiring a formal forecast update, escalation and management decision.",
                    "It's in dollars, not calendar days; it doesn't say directly how many days late."
                ],
                "correctAnswer": 0,
                "explanation": "Finishing under budget; management may reallocate contingency or repurpose savings.",
                "rawAnswer": "Finishing under budget; management may reallocate contingency or repurpose savings."
            },
            {
                "id": "module-6-q62",
                "qNum": 62,
                "question": "How do CPI and VAC interact?",
                "options": [
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun.",
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "Schedule Performance Index = EV ÷ PV: for each $1 of planned work, how much did we earn?",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick."
                ],
                "correctAnswer": 0,
                "explanation": "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun.",
                "rawAnswer": "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun."
            },
            {
                "id": "module-6-q63",
                "qNum": 63,
                "question": "Why do trends matter?",
                "options": [
                    "No; they're illustrative. Follow the organization's approved KPI framework.",
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun.",
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action."
                ],
                "correctAnswer": 3,
                "explanation": "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action.",
                "rawAnswer": "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action."
            },
            {
                "id": "module-6-q64",
                "qNum": 64,
                "question": "What does the Northstar SPI trend show?",
                "options": [
                    "The money actually spent to perform work in a period, from accounting: invoices, payroll, purchase orders, allocated overhead.",
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "$40,000, regardless of what was spent.",
                    "EV is NOT the money spent; it is the budget credit earned for physical progress."
                ],
                "correctAnswer": 1,
                "explanation": "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                "rawAnswer": "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening."
            },
            {
                "id": "module-6-q65",
                "qNum": 65,
                "question": "What is the difference between a metric and root cause analysis?",
                "options": [
                    "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000.",
                    "$4,000 − $4,500 = −$500 (over budget by $500).",
                    "$40,000, regardless of what was spent.",
                    "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom."
                ],
                "correctAnswer": 3,
                "explanation": "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom.",
                "rawAnswer": "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom."
            },
            {
                "id": "module-6-q66",
                "qNum": 66,
                "question": "List the common root causes.",
                "options": [
                    "It's in dollars, not calendar days; it doesn't say directly how many days late.",
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work."
                ],
                "correctAnswer": 2,
                "explanation": "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                "rawAnswer": "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay."
            },
            {
                "id": "module-6-q67",
                "qNum": 67,
                "question": "Give examples of procurement delay and design changes.",
                "options": [
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "PV = $5,000; EV = $4,000; AC = $4,500.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable."
                ],
                "correctAnswer": 2,
                "explanation": "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                "rawAnswer": "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders."
            },
            {
                "id": "module-6-q68",
                "qNum": 68,
                "question": "Why is rework doubly damaging?",
                "options": [
                    "A declaration that the delay will disappear; \"we will catch up\" without a plan is unacceptable.",
                    "Starting the next activity before the previous one completes, accepting overlap. It may not add direct cost but increases coordination and rework risk.",
                    "Costs double and time is lost; quality problems compound schedule and cost simultaneously.",
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months)."
                ],
                "correctAnswer": 2,
                "explanation": "Costs double and time is lost; quality problems compound schedule and cost simultaneously.",
                "rawAnswer": "Costs double and time is lost; quality problems compound schedule and cost simultaneously."
            },
            {
                "id": "module-6-q69",
                "qNum": 69,
                "question": "What does forecasting combine?",
                "options": [
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "$48,000,000 (BAC), including labor, materials, equipment and indirect costs.",
                    "Actual performance with the plan for remaining work.",
                    "$40,000, regardless of what was spent."
                ],
                "correctAnswer": 2,
                "explanation": "Actual performance with the plan for remaining work.",
                "rawAnswer": "Actual performance with the plan for remaining work."
            },
            {
                "id": "module-6-q70",
                "qNum": 70,
                "question": "What are the four forecast types?",
                "options": [
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick.",
                    "$26.4M − $29.76M = −$3,360,000.",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "Schedule forecast (CPM revised completion), cost forecast (EAC), resource forecast (peak crews, labor, equipment re-profiled), and integrated forecast (all together)."
                ],
                "correctAnswer": 3,
                "explanation": "Schedule forecast (CPM revised completion), cost forecast (EAC), resource forecast (peak crews, labor, equipment re-profiled), and integrated forecast (all together).",
                "rawAnswer": "Schedule forecast (CPM revised completion), cost forecast (EAC), resource forecast (peak crews, labor, equipment re-profiled), and integrated forecast (all together)."
            },
            {
                "id": "module-6-q71",
                "qNum": 71,
                "question": "What is the Northstar schedule forecast?",
                "options": [
                    "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window.",
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick.",
                    "$4,000 − $4,500 = −$500 (over budget by $500)."
                ],
                "correctAnswer": 1,
                "explanation": "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                "rawAnswer": "Baseline Dec 2025 → forecast Feb 2026 (+2 months)."
            },
            {
                "id": "module-6-q72",
                "qNum": 72,
                "question": "What decisions does accurate forecasting support?",
                "options": [
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace.",
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "General contractor crews, MEP subcontractors, IT vendors and the LGL project management team.",
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable."
                ],
                "correctAnswer": 3,
                "explanation": "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable.",
                "rawAnswer": "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable."
            },
            {
                "id": "module-6-q73",
                "qNum": 73,
                "question": "What is a recovery plan?",
                "options": [
                    "Estimate to Complete: how much more money we expect to spend to finish the remaining work.",
                    "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible.",
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "Begin electrical rough-in in completed floors while structural work finishes above. Crash when the critical path can genuinely be shortened and delay cost exceeds crash cost. Fast track when information is stable, the dependency is logistical and teams can be separated."
                ],
                "correctAnswer": 1,
                "explanation": "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible.",
                "rawAnswer": "A realistic, approved plan for improving performance after delay or underperformance; documented, resourced and technically feasible."
            },
            {
                "id": "module-6-q74",
                "qNum": 74,
                "question": "What is a recovery plan NOT?",
                "options": [
                    "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned.",
                    "A declaration that the delay will disappear; \"we will catch up\" without a plan is unacceptable.",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost."
                ],
                "correctAnswer": 1,
                "explanation": "A declaration that the delay will disappear; \"we will catch up\" without a plan is unacceptable.",
                "rawAnswer": "A declaration that the delay will disappear; \"we will catch up\" without a plan is unacceptable."
            },
            {
                "id": "module-6-q75",
                "qNum": 75,
                "question": "Who develops and who approves it?",
                "options": [
                    "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                    "Remaining work = $48M − $26.4M = $21.6M; ETC = $21.6M ÷ 0.96 = $22,500,000.",
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "The money actually spent to perform work in a period, from accounting: invoices, payroll, purchase orders, allocated overhead."
                ],
                "correctAnswer": 0,
                "explanation": "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization.",
                "rawAnswer": "Controls specialist with the PM, superintendent and subcontractors develop it; the PM presents options to senior management for approval, and options with added cost need formal authorization."
            },
            {
                "id": "module-6-q76",
                "qNum": 76,
                "question": "When is a recovery plan required?",
                "options": [
                    "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window.",
                    "If CPI < 1.00 with significant work remaining, VAC worsens over time unless CPI is recovered; the earlier you act, the smaller the final overrun.",
                    "Facts → Variance → Cause → Impact → Action.",
                    "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026."
                ],
                "correctAnswer": 0,
                "explanation": "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window.",
                "rawAnswer": "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window."
            },
            {
                "id": "module-6-q77",
                "qNum": 77,
                "question": "Name the seven recovery options with a benefit.",
                "options": [
                    "$48M × 55% = $26,400,000.",
                    "Metrics tell WHAT is happening; root cause analysis tells WHY. An SPI of 0.887 is a symptom.",
                    "When SPI falls below the organization's threshold (commonly 0.90), a key milestone is missed, or forecast completion slips beyond an acceptable window.",
                    "Resequence work (unlocks bottlenecks), add crew (more output), add shift/overtime (more hours), expedite procurement (unblocks installation), parallel work (saves time), resolve constraints (restores productivity), prioritize critical path (focus resources)."
                ],
                "correctAnswer": 3,
                "explanation": "Resequence work (unlocks bottlenecks), add crew (more output), add shift/overtime (more hours), expedite procurement (unblocks installation), parallel work (saves time), resolve constraints (restores productivity), prioritize critical path (focus resources).",
                "rawAnswer": "Resequence work (unlocks bottlenecks), add crew (more output), add shift/overtime (more hours), expedite procurement (unblocks installation), parallel work (saves time), resolve constraints (restores productivity), prioritize critical path (focus resources)."
            },
            {
                "id": "module-6-q78",
                "qNum": 78,
                "question": "What are the risks of adding crew and overtime?",
                "options": [
                    "Crew: site congestion, supervision challenges, higher cost. Overtime: premium pay, fatigue, reduced productivity.",
                    "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work.",
                    "What did we plan? What have we accomplished? What have we spent? Are we ahead or behind? Are we over or under budget? Where will we finish? What should management do?"
                ],
                "correctAnswer": 0,
                "explanation": "Crew: site congestion, supervision challenges, higher cost. Overtime: premium pay, fatigue, reduced productivity.",
                "rawAnswer": "Crew: site congestion, supervision challenges, higher cost. Overtime: premium pay, fatigue, reduced productivity."
            },
            {
                "id": "module-6-q79",
                "qNum": 79,
                "question": "What is crashing?",
                "options": [
                    "Estimate at Completion: the forecast final project cost.",
                    "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns.",
                    "EV is NOT the money spent; it is the budget credit earned for physical progress.",
                    "Planned: arrive 5:00 PM. Actual: at 3 PM you're only halfway (should be 70%). Forecast: GPS now predicts 6:15 PM, a 1 hour 15 minute delay at current pace."
                ],
                "correctAnswer": 1,
                "explanation": "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns.",
                "rawAnswer": "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns."
            },
            {
                "id": "module-6-q80",
                "qNum": 80,
                "question": "Give the crashing example.",
                "options": [
                    "Estimate at Completion: the forecast final project cost.",
                    "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                    "Spending $4,500 for $4,000 of work reveals inefficiency, a cost overrun per unit of work.",
                    "$48M × 55% = $26,400,000."
                ],
                "correctAnswer": 1,
                "explanation": "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                "rawAnswer": "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days."
            },
            {
                "id": "module-6-q81",
                "qNum": 81,
                "question": "What is fast tracking?",
                "options": [
                    "SPI declined every month for five consecutive periods, so the project is consistently underperforming and the gap is widening.",
                    "Starting the next activity before the previous one completes, accepting overlap. It may not add direct cost but increases coordination and rework risk.",
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay."
                ],
                "correctAnswer": 1,
                "explanation": "Starting the next activity before the previous one completes, accepting overlap. It may not add direct cost but increases coordination and rework risk.",
                "rawAnswer": "Starting the next activity before the previous one completes, accepting overlap. It may not add direct cost but increases coordination and rework risk."
            },
            {
                "id": "module-6-q82",
                "qNum": 82,
                "question": "Give the fast tracking example and when to use each.",
                "options": [
                    "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline.",
                    "When we'll be done, what it will cost, whether more funding is needed, whether to renegotiate contracts, and whether milestones are achievable.",
                    "Facts → Variance → Cause → Impact → Action.",
                    "Begin electrical rough-in in completed floors while structural work finishes above. Crash when the critical path can genuinely be shortened and delay cost exceeds crash cost. Fast track when information is stable, the dependency is logistical and teams can be separated."
                ],
                "correctAnswer": 3,
                "explanation": "Begin electrical rough-in in completed floors while structural work finishes above. Crash when the critical path can genuinely be shortened and delay cost exceeds crash cost. Fast track when information is stable, the dependency is logistical and teams can be separated.",
                "rawAnswer": "Begin electrical rough-in in completed floors while structural work finishes above. Crash when the critical path can genuinely be shortened and delay cost exceeds crash cost. Fast track when information is stable, the dependency is logistical and teams can be separated."
            },
            {
                "id": "module-6-q83",
                "qNum": 83,
                "question": "What are the eight steps of building a recovery schedule?",
                "options": [
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "Finishing under budget; management may reallocate contingency or repurpose savings.",
                    "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline.",
                    "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation)."
                ],
                "correctAnswer": 2,
                "explanation": "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline.",
                "rawAnswer": "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline."
            },
            {
                "id": "module-6-q84",
                "qNum": 84,
                "question": "What key principles guide recovery decisions?",
                "options": [
                    "1.00 on budget; 0.93 means $0.93 earned per $1 (over budget); 1.10 means $1.10 earned (under budget, verify it's real).",
                    "Facts → Variance → Cause → Impact → Action.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges.",
                    "Ask first: is the delay on the critical path? What's the root cause? Can it be partly or fully recovered? What's the cost of doing nothing? Does management have approval authority? Fix the cause first, then accelerate (crashing a delayed material activity adds cost without progress)."
                ],
                "correctAnswer": 3,
                "explanation": "Ask first: is the delay on the critical path? What's the root cause? Can it be partly or fully recovered? What's the cost of doing nothing? Does management have approval authority? Fix the cause first, then accelerate (crashing a delayed material activity adds cost without progress).",
                "rawAnswer": "Ask first: is the delay on the critical path? What's the root cause? Can it be partly or fully recovered? What's the cost of doing nothing? Does management have approval authority? Fix the cause first, then accelerate (crashing a delayed material activity adds cost without progress)."
            },
            {
                "id": "module-6-q85",
                "qNum": 85,
                "question": "What five things do executives need?",
                "options": [
                    "SV = −$10,000; CV = −$5,000.",
                    "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?",
                    "$26.4M ÷ $29.76M = 0.887 (88.7% of planned rate)."
                ],
                "correctAnswer": 2,
                "explanation": "Where are we? What changed? What is at risk? What is the forecast? What do you need?",
                "rawAnswer": "Where are we? What changed? What is at risk? What is the forecast? What do you need?"
            },
            {
                "id": "module-6-q86",
                "qNum": 86,
                "question": "What does a good \"what do you need\" look like?",
                "options": [
                    "Negative: behind plan. Zero: on plan. Positive: ahead of plan (verify with the field team).",
                    "The budgeted value of the work we planned to have completed by now: the baseline progress checkpoint in dollars.",
                    "Specific, e.g., \"Approve the expedited delivery option at a cost of $180,000 by Friday to maintain Feb 2026 forecast.\"",
                    "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000."
                ],
                "correctAnswer": 2,
                "explanation": "Specific, e.g., \"Approve the expedited delivery option at a cost of $180,000 by Friday to maintain Feb 2026 forecast.\"",
                "rawAnswer": "Specific, e.g., \"Approve the expedited delivery option at a cost of $180,000 by Friday to maintain Feb 2026 forecast.\""
            },
            {
                "id": "module-6-q87",
                "qNum": 87,
                "question": "What KPIs belong on a management dashboard?",
                "options": [
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "Schedule (SPI), Cost (CPI), Critical Path/Float, Risks, Milestones, and Recovery Status.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                    "No. It changes only with an approved scope change or contract modification; it's the yardstick."
                ],
                "correctAnswer": 1,
                "explanation": "Schedule (SPI), Cost (CPI), Critical Path/Float, Risks, Milestones, and Recovery Status.",
                "rawAnswer": "Schedule (SPI), Cost (CPI), Critical Path/Float, Risks, Milestones, and Recovery Status."
            },
            {
                "id": "module-6-q88",
                "qNum": 88,
                "question": "Why is float erosion an early warning?",
                "options": [
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "It shows the path to completion lengthening before SPI shows a problem.",
                    "$27.5M + $22.5M = $50M; or $48M ÷ 0.96 = $50,000,000.",
                    "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation)."
                ],
                "correctAnswer": 1,
                "explanation": "It shows the path to completion lengthening before SPI shows a problem.",
                "rawAnswer": "It shows the path to completion lengthening before SPI shows a problem."
            },
            {
                "id": "module-6-q89",
                "qNum": 89,
                "question": "What is the illustrative traffic-light scheme?",
                "options": [
                    "EV is NOT the money spent; it is the budget credit earned for physical progress.",
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "Inputs: PV, EV, AC. Variances: SV, CV. Efficiency ratios: SPI, CPI. Forecasts: BAC, ETC, EAC, VAC.",
                    "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation)."
                ],
                "correctAnswer": 3,
                "explanation": "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation).",
                "rawAnswer": "Green ≥ 0.95 (monitor); Yellow 0.85–0.94 (investigate, root cause analysis); Red < 0.85 (formal recovery plan, executive escalation)."
            },
            {
                "id": "module-6-q90",
                "qNum": 90,
                "question": "How do Northstar's metrics rate?",
                "options": [
                    "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend).",
                    "Baseline Dec 2025 → forecast Feb 2026 (+2 months).",
                    "$4,000 − $4,500 = −$500 (over budget by $500).",
                    "EAC = AC + ETC, or the simple form EAC = BAC ÷ CPI."
                ],
                "correctAnswer": 0,
                "explanation": "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend).",
                "rawAnswer": "SPI 0.887 = Yellow (approaching Red); CPI 0.96 = Green (watch trend)."
            },
            {
                "id": "module-6-q91",
                "qNum": 91,
                "question": "Are these thresholds fixed?",
                "options": [
                    "Costs double and time is lost; quality problems compound schedule and cost simultaneously.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                    "PV = BAC × planned % complete; EV = BAC × actual % complete.",
                    "No; they're illustrative. Follow the organization's approved KPI framework."
                ],
                "correctAnswer": 3,
                "explanation": "No; they're illustrative. Follow the organization's approved KPI framework.",
                "rawAnswer": "No; they're illustrative. Follow the organization's approved KPI framework."
            },
            {
                "id": "module-6-q92",
                "qNum": 92,
                "question": "What does the milestone report show for Northstar?",
                "options": [
                    "Civil (Mar 2024) and Structural (Aug 2024) done; MEP Rough-In Dec 2024 → Feb 2025 (+42 days, At Risk, switchgear expediting); IT Start Jan → Mar 2025 (+42, At Risk); Commissioning Aug → Oct 2025 (+43, Delayed); Project Complete Dec 2025 → Feb 2026 (+43, Delayed).",
                    "A single low SPI might be one-off, but five consecutive declines is a signal requiring investigation, root cause and corrective action.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "Actual performance with the plan for remaining work."
                ],
                "correctAnswer": 0,
                "explanation": "Civil (Mar 2024) and Structural (Aug 2024) done; MEP Rough-In Dec 2024 → Feb 2025 (+42 days, At Risk, switchgear expediting); IT Start Jan → Mar 2025 (+42, At Risk); Commissioning Aug → Oct 2025 (+43, Delayed); Project Complete Dec 2025 → Feb 2026 (+43, Delayed).",
                "rawAnswer": "Civil (Mar 2024) and Structural (Aug 2024) done; MEP Rough-In Dec 2024 → Feb 2025 (+42 days, At Risk, switchgear expediting); IT Start Jan → Mar 2025 (+42, At Risk); Commissioning Aug → Oct 2025 (+43, Delayed); Project Complete Dec 2025 → Feb 2026 (+43, Delayed)."
            },
            {
                "id": "module-6-q93",
                "qNum": 93,
                "question": "What are the eight sections of a management narrative?",
                "options": [
                    "Executive summary, progress this period, critical path, major variances, key risks, recovery actions, forecast, decisions required. It should be readable in 90 seconds.",
                    "Progress (how much planned work is done) and Forecasting (where the project is heading: final cost and finish date).",
                    "A concrete pour planned for 2 crews over 10 days: add 2 more crews and finish in 6 days.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?"
                ],
                "correctAnswer": 0,
                "explanation": "Executive summary, progress this period, critical path, major variances, key risks, recovery actions, forecast, decisions required. It should be readable in 90 seconds.",
                "rawAnswer": "Executive summary, progress this period, critical path, major variances, key risks, recovery actions, forecast, decisions required. It should be readable in 90 seconds."
            },
            {
                "id": "module-6-q94",
                "qNum": 94,
                "question": "What does the Northstar executive summary say?",
                "options": [
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?",
                    "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges.",
                    "$26.4M − $27.5M = −$1,100,000."
                ],
                "correctAnswer": 1,
                "explanation": "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000.",
                "rawAnswer": "The project is 43 calendar days behind the Dec 31, 2025 baseline; forecast is February 2026; CPI = 0.96 is within acceptable range; corrective actions are underway. MEP rough-in drives the critical path; switchgear delivery is 42 working days late; expedited shipping costs about $180,000."
            },
            {
                "id": "module-6-q95",
                "qNum": 95,
                "question": "Compare a bad and good report.",
                "options": [
                    "An integrated, honest and actionable picture management can trust and act upon.",
                    "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                    "Late materials, low productivity, rework, design changes, resource shortage, and procurement delay.",
                    "$26.4M − $27.5M = −$1,100,000."
                ],
                "correctAnswer": 1,
                "explanation": "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                "rawAnswer": "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\""
            },
            {
                "id": "module-6-q96",
                "qNum": 96,
                "question": "What is the five-part framework for controls presentations?",
                "options": [
                    "(1) Current schedule, (2) identify the driving delay, (3) identify the critical path, (4) develop 2–3 options, (5) test options in schedule, (6) review resource & cost impact, (7) obtain approval, (8) implement and monitor against the recovery baseline.",
                    "Adding labor, equipment or shifts to shorten critical path durations. It always increases direct cost, with risks of congestion and diminishing returns.",
                    "Budget at Completion: the total approved performance budget for the entire project.",
                    "Facts → Variance → Cause → Impact → Action."
                ],
                "correctAnswer": 3,
                "explanation": "Facts → Variance → Cause → Impact → Action.",
                "rawAnswer": "Facts → Variance → Cause → Impact → Action."
            },
            {
                "id": "module-6-q97",
                "qNum": 97,
                "question": "Summarize the Northstar case study analysis.",
                "options": [
                    "BAC $48M, PV $29.76M, EV $26.4M, AC $27.5M: SV −$3.36M, CV −$1.1M, SPI 0.887, CPI 0.96, EAC $50M, VAC −$2M; root cause is switchgear 42 working days late from manufacturer delays with MEP on the critical path; forecast completion slips +43 calendar days.",
                    "Finishing under budget; management may reallocate contingency or repurpose savings.",
                    "SPI (0.887); schedule underperformance is more significant than the slight cost overrun.",
                    "Cost Performance Index = EV ÷ AC: for every $1 spent, how much budgeted value did we earn?"
                ],
                "correctAnswer": 0,
                "explanation": "BAC $48M, PV $29.76M, EV $26.4M, AC $27.5M: SV −$3.36M, CV −$1.1M, SPI 0.887, CPI 0.96, EAC $50M, VAC −$2M; root cause is switchgear 42 working days late from manufacturer delays with MEP on the critical path; forecast completion slips +43 calendar days.",
                "rawAnswer": "BAC $48M, PV $29.76M, EV $26.4M, AC $27.5M: SV −$3.36M, CV −$1.1M, SPI 0.887, CPI 0.96, EAC $50M, VAC −$2M; root cause is switchgear 42 working days late from manufacturer delays with MEP on the critical path; forecast completion slips +43 calendar days."
            },
            {
                "id": "module-6-q98",
                "qNum": 98,
                "question": "What should management consider at Northstar?",
                "options": [
                    "Finishing under budget; management may reallocate contingency or repurpose savings.",
                    "Long-lead items (switchgear, generators, custom IT equipment) not delivered on time; engineering revisions during construction not yet in the baseline until formal change orders.",
                    "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026.",
                    "Where are we? What changed? What is at risk? What is the forecast? What do you need?"
                ],
                "correctAnswer": 2,
                "explanation": "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026.",
                "rawAnswer": "Approve expedited switchgear ($180K premium) to recover 15 working days; authorize MEP overtime on other critical activities; fast track IT infrastructure planning with MEP; review contingency for the $2M overrun; commission a formal recovery schedule by the Month 13 update. Delay in decision could worsen VAC by an estimated $350,000 and slip completion to March 2026."
            },
            {
                "id": "module-6-q99",
                "qNum": 99,
                "question": "How do you forecast completion in an interview (professional answer)?",
                "options": [
                    "Estimate at Completion: the forecast final project cost.",
                    "Road: $1,000,000 × 40% = $400,000. Northstar Month 12: $48M × 62% = $29,760,000. Renovation: $5,000.",
                    "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges.",
                    "$26.4M ÷ $27.5M = 0.96 (4% over budget in cost efficiency terms)."
                ],
                "correctAnswer": 2,
                "explanation": "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges.",
                "rawAnswer": "Calculate EAC using BAC ÷ CPI for cost, run a CPM update applying current progress and productivity trends to determine the revised critical path and completion date, and present both time-based and EVM-based forecasts with confidence ranges."
            },
            {
                "id": "module-6-q100",
                "qNum": 100,
                "question": "What are the eight capstone steps and the traits of a great controls professional?",
                "options": [
                    "Civil and structural complete; MEP 60% complete; IT infrastructure 20% started; overall 55% complete vs. 62% planned.",
                    "Bad: \"Electrical is delayed\" (no activity, days, cause, criticality or action). Good: \"Electrical energization is forecast 12 working days later than baseline due to late switchgear delivery; on the critical path and driving forecast completion; procurement is evaluating expedited delivery.\"",
                    "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;",
                    "The discipline that helps management understand whether a project is going according to plan and what needs attention, bringing together scope, schedule, resources and cost."
                ],
                "correctAnswer": 2,
                "explanation": "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;",
                "rawAnswer": "Steps: review schedule; analyze update (SV, CV, SPI, CPI, EAC, VAC); identify critical issues; forecast completion and SPI trend (Months 8–12); recommend ≥2 recovery options; one-page dashboard; one-page narrative; 5-minute briefing. Traits: analytical thinking, honesty and integrity, clear communication, proactive thinking, systems thinking, continuous learning (AACE, PMI). `;"
            }
        ]
    }
];

export const QUIZ_CATEGORIES: QuizCategory[] = RAW_CATEGORIES.map(cat => ({
    ...cat,
    icon: ICON_MAP[cat.iconName] || Calendar
}));

// Fisher-Yates shuffle
export function shuffleAndSlice<T>(items: T[], count: number = 10): T[] {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return count > 0 ? copy.slice(0, count) : copy;
}

export const QUIZ_PASS_MARK = 70;
